export function initTutorial(platform) {
  if (!platform || typeof platform.scheduleFrame !== 'function' || typeof platform.cancelFrame !== 'function') {
    throw new Error('initTutorial requires platform.scheduleFrame and platform.cancelFrame');
  }

  const scheduleFrame = platform.scheduleFrame.bind(platform);
  const cancelFrame = platform.cancelFrame.bind(platform);
  const waitFrame = typeof platform.waitFrame === 'function'
    ? platform.waitFrame.bind(platform)
    : function () {
      return new Promise(function (resolve) {
        scheduleFrame(resolve);
      });
    };

  const STORAGE_KEY = 'stockstorm_tutorial_v1';

  function isVrTutorialPage() {
    if (typeof platform.isVrPage === 'boolean') {
      return platform.isVrPage;
    }
    return /\/vr\.html/i.test(window.location.pathname);
  }

  const MANIFEST_URL = isVrTutorialPage()
    ? './desktop_assets/tutorial/manifest-vr.json'
    : './desktop_assets/tutorial/manifest.json';
  const MOUSE_MOVE_ICON = './desktop_assets/tutorial/svg-icons/mouse-move-bf.svg';
  const MOUSE_LMB_ICON = './desktop_assets/tutorial/svg-icons/mouse-lmb-bf.svg';
  const MOUSE_RMB_ICON = './desktop_assets/tutorial/svg-icons/mouse-rmb-bf.svg';
  const MOUSE_SCROLL_ICON = './desktop_assets/tutorial/svg-icons/mouse-scroll.svg';
  const HAND_MOVE_ICON = './desktop_assets/tutorial/svg-icons/hand-move.svg';
  const HAND_TAP_ICON = './desktop_assets/tutorial/svg-icons/hand-tap.svg';
  const HAND_SCROLL_ICON = './desktop_assets/tutorial/svg-icons/hand-scroll.svg';
  const CONTROLLER_RIGHT_ICON = './desktop_assets/tutorial/svg-icons/controller-right.svg';
  const CONTROLLER_RIGHT_PRESSED_ICON = './desktop_assets/tutorial/svg-icons/controller-right-pressed.svg';
  const CONTROLLER_RIGHT_ZOOMIN_ICON = './desktop_assets/tutorial/svg-icons/controller-right-zoomin.svg';
  const CONTROLLER_RIGHT_ZOOMOUT_ICON = './desktop_assets/tutorial/svg-icons/controller-right-zoomout.svg';
  const CONTROLLER_LEFT_ICON = './desktop_assets/tutorial/svg-icons/controller-left.svg';
  const CONTROLLER_LEFT_PRESSED_ICON = './desktop_assets/tutorial/svg-icons/controller-left-pressed.svg';
  const CLICK_CURSOR_ICON = './desktop_assets/tutorial/svg-icons/cursor.svg';
  const CONTROLLER_DEFAULT_WIDTH = '16%';
  const CONTROLLER_WIDTH_PERCENT = parseFloat(CONTROLLER_DEFAULT_WIDTH) || 16;
  const CONTROLLER_BASE_SCALE = 1.1;
  const CONTROLLER_DRAG_TOP_OFFSET_PX = 20;
  const AUTO_OPEN_DELAY_MS = 500;

  const SITE_LAYOUT_OVERLAY = isVrTutorialPage()
    ? {
      type: 'mouse-lmb-cycle',
      videoStart: 0.89,
      videoEnd: 1,
      fromLeft: '82%',
      fromTop: '48%',
      left: '50%',
      top: '68%',
      cursorFromLeft: '64%',
      cursorFromTop: '62%',
      cursorToLeft: '50%',
      cursorToTop: '68%',
      cursorLeft: '50%',
      cursorTop: '68%',
      cursorLeadSec: 2.5,
      cursorHoldSec: 2,
      clickLeadSec: 0.5,
      clickEarly: 0.1,
      pointerArriveEarly: 0.1,
      pointerTravelSec: 0.9,
      pointerApproachLeadSec: 6.5,
      pointerPresenceFadeSec: 0.5,
      mouseTravelRange: 0,
      cursorTravelEarly: 0.1,
      controllerOffsetLeft: 8,
      controllerOffsetTop: 9,
      cursorWidth: '40px',
      width: '64px',
      pressLineHeight: '80%'
    }
    : {
      type: 'mouse-lmb-cycle',
      videoStart: 0.70,
      videoEnd: 0.80,
      fromLeft: '88%',
      fromTop: '68%',
      left: '64%',
      top: '48%',
      cursorFromLeft: '84%',
      cursorFromTop: '72%',
      cursorToLeft: '42%',
      cursorToTop: '56%',
      cursorLeadSec: 2,
      cursorHoldSec: 2,
      clickLeadSec: 1,
      pointerArriveEarly: 0.38,
      pointerTravelSec: 1.8,
      pointerApproachLeadSec: 5,
      pointerPresenceFadeSec: 0.5,
      mouseTravelRange: 0.14,
      cursorTravelEarly: 0.1,
      cursorWidth: '40px',
      width: '64px'
    };

  const MOBILE_SITE_LAYOUT_HAND_TARGET = {
    left: 50,
    top: 68
  };

  const MOBILE_SITE_LAYOUT_HAND_TIMING = {
    pointerTravelSec: 0.9,
    pointerApproachLeadSec: 6.5,
    pointerPresenceFadeSec: 0.5,
    pointerArriveEarly: 0.1,
    clickLeadSec: 0.5,
    clickEarly: 0.1
  };

  const CHART_INTERACTION_VR_SEGMENTS = [
    { start: 0.02, end: 0.3, button: 'lmb', axis: 'y' },
    { start: 0.35, end: 0.6, button: 'lmb', axis: 'x' },
    { start: 0.60, end: 0.76, button: 'scroll', zoom: 'in' },
    { start: 0.76, end: 0.92, button: 'scroll', zoom: 'out' }
  ];

  const CHART_INTERACTION_DESKTOP_SEGMENTS = [
    { start: 0, end: 0.16, button: 'lmb', axis: 'y' },
    { start: 0.18, end: 0.34, button: 'lmb', axis: 'x' },
    { start: 0.36, end: 0.52, button: 'rmb', axis: 'y' },
    { start: 0.54, end: 0.70, button: 'rmb', axis: 'x' },
    { start: 0.72, end: 0.86, button: 'scroll', zoom: 'in' },
    { start: 0.86, end: 1, button: 'scroll', zoom: 'out' }
  ];

  const DEFAULT_MANIFEST = {
    version: 1,
    steps: [
      {
        id: 'site-layout',
        title: 'Site Layout',
        subtitle: 'Interactive dashboard',
        video: isVrTutorialPage()
          ? './desktop_assets/tutorial/videos/video-helper-vr-1.mp4'
          : './desktop_assets/tutorial/videos/video-helper-1.mp4',
        overlay: SITE_LAYOUT_OVERLAY,
        bullets: [
          'Left panel shows news',
          'Right panel shows details on individual stock',
          'Center panel - shows interactive 3D chart'
        ],
        note: 'Click on a stock bar in the center panel to load that stock into the left and right panels'
      },
      {
        id: 'chart-interaction',
        title: 'Interacting with 3D charts',
        video: isVrTutorialPage()
          ? './desktop_assets/tutorial/videos/video-helper-vr-2.mp4'
          : './desktop_assets/tutorial/videos/video-helper-2.mp4',
        videoMesh: isVrTutorialPage()
          ? './desktop_assets/tutorial/videos/vr-helper2-3d-scene.mp4'
          : undefined,
        overlay: {
          type: 'mouse-drag-segments',
          fromLeft: '88%',
          fromTop: isVrTutorialPage() ? '48%' : '68%',
          left: '88%',
          top: '48%',
          width: '64px',
          dragRange: 8,
          videoSync: {
            startDelaySec: isVrTutorialPage() ? 0 : 1,
            endPaddingSec: 1
          },
          introSec: isVrTutorialPage() ? 0 : undefined,
          segments: isVrTutorialPage()
            ? CHART_INTERACTION_VR_SEGMENTS
            : CHART_INTERACTION_DESKTOP_SEGMENTS
        },
        bulletHighlights: isVrTutorialPage()
          ? [
            { start: 0.04, end: 0.40, index: 0 },
            { start: 0.60, end: 0.92, index: 1 }
          ]
          : undefined,
        bullets: isVrTutorialPage()
          ? [
            'Rotate the chart by holding the trigger / left mouse button.',
            'Zoom the chart using the left joystick / mouse wheel.'
          ]
          : [
            'Left click-and-drag to rotate chart',
            'Right click-and-drag to reposition chart',
            'Mouse wheel to zoom'
          ],
        bulletsMobile: isVrTutorialPage()
          ? undefined
          : [
            'Press Rotate to rotate the chart.',
            'Press Move to reposition the chart.',
            'Pinch to zoom in or out.'
          ],
        bulletsVr: isVrTutorialPage()
          ? [
            'Rotate the chart by holding the trigger',
            'Zoom the chart using the left joystick'
          ]
          : undefined
      },
      {
        id: 'chart-settings',
        title: 'Chart Settings',
        video: isVrTutorialPage()
          ? './desktop_assets/tutorial/videos/video-helper-vr-3.mp4'
          : './desktop_assets/tutorial/videos/video-helper-3.mp4',
        videoMesh: isVrTutorialPage()
          ? './desktop_assets/tutorial/videos/vr-helper3-3d-scene.mp4'
          : undefined,
        overlay: isVrTutorialPage()
          ? {
            type: 'mouse-click-segments',
            fromLeft: '88%',
            fromTop: '68%',
            mouseLeft: '88%',
            mouseTop: '68%',
            cursorFromLeft: '84%',
            cursorFromTop: '72%',
            controllerFromLeft: '84%',
            controllerFromTop: '68%',
            cursorWidth: '40px',
            width: '64px',
            pressLineHeight: '80%',
            controllerTravelRotateDeg: -90,
            controllerTravelTargetOffsetLeft: 10,
            controllerTravelTargetOffsetTop: -10,
            segments: [
              { start: 0, end: 0.1, button: 'lmb', left: '45%', top: '90%', returnHome: true },
              { start: 0.25, end: 0.35, button: 'lmb', left: '43%', top: '92%', returnHome: true },
              { start: 0.8, end: 0.9, button: 'lmb', left: '41%', top: '94%', returnHome: true }
            ]
          }
          : undefined,
        bulletHighlights: [
          { start: 0.05, end: 0.4, index: 0 },
          { start: 0.4, end: 0.6, index: 1 },
          { start: 0.6, end: 0.8, index: 2 },
          { start: 0.8, end: 0.95, index: 3 }
        ],
        bullets: [
          'Sectors button turns on/off sector sorting',
          'Configure > Glow turns on glow showing stocks with high daily volume',
          'Configure > Fireworks turns on fireworks when Watchlist stocks have a big gain',
          'Info button teaches how to interpret charts'
        ]
      },
      {
        id: 'vr-panel-controls',
        title: 'Panel Controls',
        video: './desktop_assets/tutorial/videos/video-helper-vr-4.mp4',
        videoMesh: './desktop_assets/tutorial/videos/vr-helper4-3d-scene.mp4',
        overlay: {
          type: 'mouse-click-segments',
          fromLeft: '76%',
          fromTop: '53%',
          mouseLeft: '88%',
          mouseTop: '68%',
          cursorFromLeft: '76%',
          cursorFromTop: '53%',
          cursorWidth: '40px',
          width: '64px',
          pressLineHeight: '80%',
          keepMouseVisible: true,
          continuousCursor: true,
          controllerOffsetLeft: 9,
          controllerOffsetTop: 8,
          segments: [
            { start: 0.09, end: 0.16, button: 'lmb', left: '76%', top: '53%' },
            { start: 0.18, end: 0.24, button: 'lmb', left: '76%', top: '53%' },
            { start: 0.24, end: 0.3, button: 'lmb', left: '76%', top: '53%' },
            { start: 0.3, end: 0.4, button: 'lmb', left: '60%', top: '15%' },
            { start: 0.4, end: 0.45, button: 'lmb', left: '45%', top: '30%' },
            { start: 0.45, end: 0.55, left: '85%', top: '30%', noClick: true },
            { start: 0.75, end: 0.8, button: 'lmb', left: '72%', top: '15%', clickDelay: 0.01, fastTravel: true },
            { start: 0.8, end: 0.87, button: 'lmb', left: '55%', top: '30%', sharpTravel: true }
          ]
        },
        bullets: [
          'Right / Left arrows rotate panels',
          'Settings > Reposition to reposition panels',
          'Use Reset to return to the original state'
        ]
      }
    ]
  };

  function normalizeManifest(data) {
    if (!data || !Array.isArray(data.steps)) {
      return DEFAULT_MANIFEST;
    }
    return {
      version: data.version,
      steps: data.steps.filter((step) => !step.hidden)
    };
  }

  let manifest = normalizeManifest(DEFAULT_MANIFEST);
  let currentStep = 0;
  let mouseOverlayState = null;
  let mouseOverlayRaf = null;
  let bulletHighlightSegments = null;
  let noteHighlightRange = null;
  let highlightSyncActive = false;
  let bulletHighlightRaf = null;
  let vrTutorialDeviceCache = null;
  let vrTutorialForceOverride = null;
  let vrTutorialForceMesh = false;
  let vrTutorialForceMeshFromUrl = false;
  let vrPresentationSyncGen = 0;
  let vrTutorialMeshEl = null;
  let vrTutorialMeshSteps = null;
  let vrTutorialActiveStepIndex = -1;
  let meshActiveVideoEl = null;
  let meshActiveRefs = null;
  let meshOverlayVideoEl = null;
  let meshOverlaySvgEl = null;
  let vrMeshRefreshTimer = null;
  const VR_TUTORIAL_MESH_BASE_WIDTH = 1152;
  const VR_TUTORIAL_MESH_BASE_HEIGHT = 768;
  const VR_TUTORIAL_MESH_RESOLUTION = 2;
  const VR_TUTORIAL_MESH_TARGET_WIDTH_METERS = 0.95;
  const VR_TUTORIAL_MESH_RIG_DISTANCE = 1.3;
  const VR_MESH_REFRESH_INTERVAL_MS = 80;
  let chartSwitcherEl = null;

  const overlay = document.getElementById('tutorial-overlay');
  if (!overlay) {
    return;
  }

  const titleEl = overlay.querySelector('.tutorial-step-title');
  const subtitleEl = overlay.querySelector('.tutorial-step-subtitle');
  const bulletsEl = overlay.querySelector('.tutorial-bullets');
  const noteEl = overlay.querySelector('.tutorial-step-note');
  const copyEl = overlay.querySelector('.tutorial-copy');
  const dotsEl = overlay.querySelector('.tutorial-dots');
  const mediaEl = overlay.querySelector('.tutorial-media');
  const mediaLoaderEl = overlay.querySelector('.tutorial-media-loader');
  const videoEl = overlay.querySelector('.tutorial-video');
  const overlaySvgEl = overlay.querySelector('.tutorial-overlay-svg');
  const nextBtn = overlay.querySelector('.tutorial-next');
  const prevBtn = overlay.querySelector('.tutorial-prev');
  const remindBtn = overlay.querySelector('.tutorial-remind');
  const closeBtn = overlay.querySelector('.tutorial-close');
  const backdropEl = overlay.querySelector('.tutorial-backdrop');
  const panelSourceEl = overlay.querySelector('#tutorial-panel-source') || overlay.querySelector('.tutorial-panel');

  try {
    const urlParams = new URLSearchParams(window.location.search);
    vrTutorialForceMeshFromUrl = urlParams.get('tutorialMesh') === '1' || urlParams.get('tutorialMesh') === 'true';
  } catch (e) { }

  function isLightTutorialTheme() {
    const body = document.body;
    if (body && (body.classList.contains('light') || body.classList.contains('cb-light'))) {
      return true;
    }
    if (typeof getCacheItem === 'function') {
      try {
        const settings = getCacheItem('mainSettings');
        if (settings && settings.themeMode === 'Light') {
          return true;
        }
      } catch (e) { }
    }
    return false;
  }

  function syncTutorialTheme() {
    overlay.classList.toggle('tutorial-theme-light', isLightTutorialTheme());
  }

  function bindTutorialThemeSync() {
    syncTutorialTheme();
    const body = document.body;
    if (body) {
      const themeObserver = new MutationObserver(() => {
        syncTutorialTheme();
      });
      themeObserver.observe(body, {
        attributes: true,
        attributeFilter: ['class']
      });
    }
    const scene = document.getElementById('main-scene');
    if (scene) {
      scene.addEventListener('theme-changed', syncTutorialTheme);
    }
  }

  function tutorialVideoKey(url) {
    if (!url) {
      return '';
    }
    try {
      return decodeURIComponent(new URL(url, window.location.href).pathname).replace(/^\/+/, '');
    } catch (e) {
      return String(url).replace(/^\.\//, '');
    }
  }

  let tutorialVideoHasPlayed = false;

  function resetTutorialVideoLoadState() {
    tutorialVideoHasPlayed = false;
  }

  let vrScenePausedForTutorial = false;
  let vrImmersiveEntryPendingUntil = 0;
  let vrScenePlayWatchdogRaf = null;

  function isVrScenePlaying(scene) {
    return scene && scene.is && !scene.is('paused');
  }

  function isTutorialOpen() {
    return overlay.classList.contains('show');
  }

  function shouldLockPageInteractionForTutorial() {
    if (!isTutorialOpen()) {
      return false;
    }
    if (isVrTutorialPage() && overlay.classList.contains('tutorial-vr-immersive-guard')) {
      return false;
    }
    if (isVrTutorialPage()
      && shouldUseVrTutorialMesh()
      && window.VrTutorialNative
      && VrTutorialNative.isVisible()) {
      return false;
    }
    return true;
  }

  function syncTutorialPageInteractionLock() {
    document.documentElement.classList.toggle(
      'tutorial-open',
      shouldLockPageInteractionForTutorial()
    );
  }

  function shouldBlockTutorialBackgroundInteraction() {
    return shouldLockPageInteractionForTutorial();
  }

  function onTutorialOverlayTouchStart(event) {
    if (!shouldBlockTutorialBackgroundInteraction()) {
      return;
    }
    event.stopPropagation();
  }

  function onTutorialOverlayTouchMove(event) {
    if (!shouldBlockTutorialBackgroundInteraction()) {
      return;
    }
    if (copyEl && event.target instanceof Node && copyEl.contains(event.target)) {
      if (copyEl.scrollHeight > copyEl.clientHeight + 1) {
        return;
      }
    }
    event.preventDefault();
    event.stopPropagation();
  }

  function isVrTutorialForceMeshEnabled() {
    return vrTutorialForceMesh || vrTutorialForceMeshFromUrl;
  }

  function shouldUseVrTutorialMesh() {
    if (!isVrTutorialPage() || !isTutorialOpen()) {
      return false;
    }
    if (!isVrTutorialDevice() && !vrTutorialForceMeshFromUrl) {
      return false;
    }
    return isImmersiveVrActive() || isVrImmersiveEntryPending() || isVrTutorialForceMeshEnabled();
  }

  function isVrImmersivePresentation() {
    return isImmersiveVrActive() || isVrImmersiveEntryPending();
  }

  function applyNativePresentationMode(nativeActive) {
    if (nativeActive && isVrTutorialPage()) {
      overlay.classList.add('tutorial-vr-mesh-mode', 'tutorial-vr-native-panel');
      overlay.classList.remove('tutorial-vr-overlay-active');
      return;
    }
    overlay.classList.remove('tutorial-vr-mesh-mode', 'tutorial-vr-overlay-active', 'tutorial-vr-native-panel');
  }

  function getVrCookieConsentStatus() {
    const instance = window.cookieConsentInstance;
    if (instance && typeof instance.getStatus === 'function') {
      return instance.getStatus();
    }
    return null;
  }

  function isVrCookieConsentResolved() {
    if (!isVrTutorialPage()) {
      return true;
    }
    if (window.consentGiven === true) {
      return true;
    }
    const status = getVrCookieConsentStatus();
    return status === 'allow' || status === 'deny' || status === 'dismiss';
  }

  function shouldDeferVrCookieConsent() {
    if (!isVrTutorialPage() || isVrCookieConsentResolved()) {
      return false;
    }
    if (isTutorialOpen()) {
      return true;
    }
    return shouldAutoOpen();
  }

  function suppressVrCookieConsentForTutorial() {
    if (!isVrTutorialPage() || isVrCookieConsentResolved()) {
      return;
    }
    const consentElement = document.querySelector('#cookie-consent-container');
    if (!consentElement) {
      return;
    }
    consentElement.setAttribute('cookie-consent', { isShow: false });
    consentElement.setAttribute('visible', false);
    consentElement.setAttribute('position', '0 12.5 -0.25');
  }

  function showVrCookieConsentIfNeeded() {
    if (!isVrTutorialPage() || isVrCookieConsentResolved()) {
      return;
    }
    const consentElement = document.querySelector('#cookie-consent-container');
    if (!consentElement) {
      return;
    }
    consentElement.setAttribute('cookie-consent', { isShow: true });
    consentElement.dispatchEvent(new CustomEvent('cookie-consent-updated', { detail: { consentGiven: false } }));
  }

  window.shouldDeferVrCookieForTutorial = shouldDeferVrCookieConsent;

  function waitForVrCookieConsentResolved() {
    if (isVrCookieConsentResolved()) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (done) {
          return;
        }
        done = true;
        document.removeEventListener('vr-cookie-consent-closed', onClosed);
        clearInterval(pollId);
        resolve();
      };
      const onClosed = () => finish();
      const pollId = setInterval(() => {
        if (isVrCookieConsentResolved()) {
          finish();
        }
      }, 200);
      document.addEventListener('vr-cookie-consent-closed', onClosed);
    });
  }

  function refreshNativeOverlayMeshIfNeeded() {
    if (window.VrTutorialNative && VrTutorialNative.isVisible()) {
      return;
    }
    if (shouldUseVrTutorialMesh() && window.VrTutorialNative) {
      VrTutorialNative.refreshOverlayMesh();
    }
  }

  function syncPrevButtonVisibility(stepIndex) {
    if (!prevBtn) {
      return;
    }
    const hidden = stepIndex <= 0;
    prevBtn.hidden = hidden;
    prevBtn.style.display = hidden ? 'none' : '';
    prevBtn.setAttribute('aria-hidden', hidden ? 'true' : 'false');
  }

  function syncMobileTutorialActionLabels(stepIndex, refs) {
    const next = refs ? refs.next : nextBtn;
    const prev = refs ? refs.prev : prevBtn;
    const remind = refs ? refs.remind : remindBtn;
    if (!next) {
      return;
    }
    const isLast = stepIndex >= manifest.steps.length - 1;
    if (shouldUseFixedTutorialMediaSize()) {
      next.textContent = isLast ? 'Finish' : 'Next';
      if (prev) {
        prev.textContent = 'Prev';
      }
      if (remind) {
        remind.textContent = 'Skip';
      }
      return;
    }
    next.textContent = isLast ? 'Finish' : 'Next tip';
    if (prev) {
      prev.textContent = 'Previous tip';
    }
    if (remind) {
      remind.textContent = 'Skip tutorial';
    }
  }

  function syncMobileTutorialCopyClasses(step, refs) {
    const copy = refs ? refs.copy : copyEl;
    if (!copy) {
      return;
    }
    const dense = shouldUseFixedTutorialMediaSize() && resolveStepBullets(step).length >= 4;
    copy.classList.toggle('tutorial-copy-dense', dense);
  }

  function getOverlayVideoEl() {
    if (shouldUseVrTutorialMesh() && window.VrTutorialNative && VrTutorialNative.isVisible()) {
      const nativeVideo = VrTutorialNative.getVideoEl();
      if (nativeVideo) {
        return nativeVideo;
      }
    }
    return meshActiveVideoEl || meshOverlayVideoEl || videoEl;
  }

  function getVrTutorialNativeHandlers() {
    return {
      onNext: () => goNext(),
      onPrev: () => {
        if (currentStep > 0) {
          renderStep(currentStep - 1);
        }
      },
      onSkip: () => skipTutorial(),
      onClose: () => dismissForSession(),
      onDot: (index) => renderStep(index)
    };
  }

  function getOverlaySvgEl() {
    if (shouldUseVrTutorialMesh() && window.VrTutorialNative && VrTutorialNative.isVisible()) {
      const nativeSvg = VrTutorialNative.getOverlaySvgEl();
      if (nativeSvg) {
        return nativeSvg;
      }
    }
    return meshOverlaySvgEl || overlaySvgEl;
  }

  function getTutorialBulletsEl() {
    return meshActiveRefs ? meshActiveRefs.bullets : bulletsEl;
  }

  function getTutorialNoteEl() {
    return meshActiveRefs ? meshActiveRefs.note : noteEl;
  }

  function setMeshOverlayContext(refs) {
    meshOverlayVideoEl = refs ? refs.video : null;
    meshOverlaySvgEl = refs ? refs.overlaySvg : null;
  }

  function clearMeshOverlayContext() {
    meshOverlayVideoEl = null;
    meshOverlaySvgEl = null;
  }

  function getTutorialPanelRefs(panel) {
    return {
      panel: panel,
      title: panel.querySelector('.tutorial-step-title'),
      subtitle: panel.querySelector('.tutorial-step-subtitle'),
      bullets: panel.querySelector('.tutorial-bullets'),
      note: panel.querySelector('.tutorial-step-note'),
      copy: panel.querySelector('.tutorial-copy'),
      dots: panel.querySelector('.tutorial-dots'),
      media: panel.querySelector('.tutorial-media'),
      mediaLoader: panel.querySelector('.tutorial-media-loader'),
      video: panel.querySelector('.tutorial-video'),
      overlaySvg: panel.querySelector('.tutorial-overlay-svg'),
      next: panel.querySelector('.tutorial-next'),
      prev: panel.querySelector('.tutorial-prev'),
      remind: panel.querySelector('.tutorial-remind'),
      close: panel.querySelector('.tutorial-close')
    };
  }

  function getTutorialSourceRefs() {
    return panelSourceEl ? getTutorialPanelRefs(panelSourceEl) : null;
  }

  function getTutorialHighlightRefsList() {
    const refsList = [];
    if (meshActiveRefs) {
      refsList.push(meshActiveRefs);
    }
    const sourceRefs = getTutorialSourceRefs();
    if (sourceRefs && (!meshActiveRefs || sourceRefs.panel !== meshActiveRefs.panel)) {
      refsList.push(sourceRefs);
    }
    if (!refsList.length) {
      refsList.push({
        panel: panelSourceEl,
        bullets: bulletsEl,
        note: noteEl,
        copy: copyEl
      });
    }
    return refsList;
  }

  function syncPrevButtonVisibilityForRefs(stepIndex, refs) {
    if (!refs.prev) {
      return;
    }
    const hidden = stepIndex <= 0;
    refs.prev.hidden = hidden;
    refs.prev.style.display = hidden ? 'none' : '';
    refs.prev.setAttribute('aria-hidden', hidden ? 'true' : 'false');
  }

  function clearPanelMediaLoading(refs) {
    if (!refs || !refs.media) {
      return;
    }
    refs.media.classList.remove('is-loading');
    if (refs.mediaLoader) {
      refs.mediaLoader.setAttribute('aria-hidden', 'true');
    }
  }

  function setVrTutorialMeshRaycastable(mesh, enabled) {
    if (!mesh) {
      return;
    }
    mesh.setAttribute('visible', enabled ? 'true' : 'false');
    if (enabled) {
      mesh.setAttribute('data-raycastable', '');
      mesh.setAttribute('class', 'clickable');
      return;
    }
    mesh.removeAttribute('data-raycastable');
  }

  function renderDotsIntoPanel(activeIndex, dotsContainer) {
    if (!dotsContainer) {
      return;
    }
    dotsContainer.innerHTML = '';
    manifest.steps.forEach((_, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'tutorial-dot' + (index === activeIndex ? ' active' : '');
      dot.setAttribute('aria-label', 'Go to step ' + (index + 1));
      dot.addEventListener('click', () => {
        renderStep(index);
      });
      dotsContainer.appendChild(dot);
    });
  }

  function measureTutorialPanelMeshHeightFor(panel) {
    if (!panel) {
      return VR_TUTORIAL_MESH_BASE_HEIGHT;
    }
    panel.style.height = 'auto';
    panel.style.minHeight = '0';
    const measured = Math.max(panel.scrollHeight, panel.offsetHeight);
    return Math.min(Math.max(measured, 1), VR_TUTORIAL_MESH_BASE_HEIGHT);
  }

  function prepareTutorialPanelForMesh(panel) {
    if (!panel) {
      return;
    }
    panel.__htmlMeshPixelRatio = VR_TUTORIAL_MESH_RESOLUTION;
    panel.style.width = VR_TUTORIAL_MESH_BASE_WIDTH + 'px';
    panel.style.maxHeight = VR_TUTORIAL_MESH_BASE_HEIGHT + 'px';
    panel.style.height = 'auto';
    panel.style.minHeight = '0';
    panel.style.height = measureTutorialPanelMeshHeightFor(panel) + 'px';
    applyTutorialMeshPanelColors(panel);
    const refs = getTutorialPanelRefs(panel);
    if (refs) {
      applyTutorialMeshDomStyles(refs);
    }
  }

  function getTutorialMeshColors() {
    if (isLightTutorialTheme()) {
      return {
        panelBg: '#ffffff',
        panelBorder: '#3636e3',
        text: '#111111',
        highlightText: '#111111',
        highlightBorder: '#3636e3',
        highlightBg: 'rgba(54, 54, 227, 0.14)',
        highlightShadow: '0 0 18px rgba(54, 54, 227, 0.45), inset 0 0 14px rgba(86, 86, 237, 0.2)',
        highlightTextShadow: '0 0 10px rgba(54, 54, 227, 0.55), 0 0 22px rgba(54, 54, 227, 0.35)'
      };
    }
    return {
      panelBg: '#14141c',
      panelBorder: '#3636e3',
      text: '#ffffff',
      highlightText: '#ffffff',
      highlightBorder: '#3636e3',
      highlightBg: 'rgba(54, 54, 227, 0.16)',
      highlightShadow: '0 0 18px rgba(54, 54, 227, 0.55), inset 0 0 14px rgba(121, 121, 252, 0.28)',
      highlightTextShadow: '0 0 10px rgba(54, 54, 227, 0.95), 0 0 22px rgba(54, 54, 227, 0.65)'
    };
  }

  function applyTutorialMeshPanelColors(panel) {
    const colors = getTutorialMeshColors();
    panel.style.backgroundColor = colors.panelBg;
    panel.style.border = '1px solid ' + colors.panelBorder;
    panel.style.color = colors.text;
  }

  function applyTutorialMeshDomStyles(refs) {
    if (!refs || !refs.panel) {
      return;
    }
    const colors = getTutorialMeshColors();
    const panel = refs.panel;
    applyTutorialMeshPanelColors(panel);
    panel.style.width = VR_TUTORIAL_MESH_BASE_WIDTH + 'px';
    panel.style.maxWidth = VR_TUTORIAL_MESH_BASE_WIDTH + 'px';
    panel.style.maxHeight = VR_TUTORIAL_MESH_BASE_HEIGHT + 'px';
    panel.style.borderRadius = '16px';
    panel.style.padding = '20px 24px 18px';
    panel.style.display = 'flex';
    panel.style.flexDirection = 'column';
    panel.style.gap = '18px';
    panel.style.boxSizing = 'border-box';
    panel.style.fontFamily = 'Inter, sans-serif';
    panel.style.overflow = 'hidden';

    const header = panel.querySelector('.tutorial-header');
    if (header) {
      header.style.display = 'flex';
      header.style.alignItems = 'flex-start';
      header.style.justifyContent = 'space-between';
      header.style.gap = '12px';
      header.style.flex = '0 0 auto';
    }

    const brand = panel.querySelector('.tutorial-brand');
    if (brand) {
      brand.style.display = 'flex';
      brand.style.alignItems = 'center';
      brand.style.gap = '10px';
    }

    const brandTitle = panel.querySelector('.tutorial-brand-title');
    if (brandTitle) {
      brandTitle.style.margin = '0';
      brandTitle.style.fontSize = '18px';
      brandTitle.style.fontWeight = '600';
      brandTitle.style.lineHeight = '1.2';
      brandTitle.style.color = colors.text;
    }

    panel.querySelectorAll('.tutorial-brand-title .accent').forEach((el) => {
      el.style.color = colors.panelBorder;
    });

    if (refs.close) {
      refs.close.style.display = 'flex';
      refs.close.style.alignItems = 'center';
      refs.close.style.justifyContent = 'center';
      refs.close.style.width = '32px';
      refs.close.style.height = '32px';
      refs.close.style.border = 'none';
      refs.close.style.borderRadius = '6px';
      refs.close.style.background = 'transparent';
      refs.close.style.color = colors.text;
      refs.close.style.fontSize = '22px';
      refs.close.style.cursor = 'pointer';
    }

    const body = panel.querySelector('.tutorial-body');
    if (body) {
      body.style.display = 'grid';
      body.style.gridTemplateColumns = 'minmax(0, 1.25fr) minmax(0, 0.75fr)';
      body.style.gap = '24px';
      body.style.minHeight = '0';
      body.style.flex = '1 1 auto';
      body.style.overflow = 'hidden';
    }

    if (refs.media) {
      refs.media.style.position = 'relative';
      refs.media.style.aspectRatio = '16 / 9';
      refs.media.style.width = '100%';
      refs.media.style.borderRadius = '12px';
      refs.media.style.overflow = 'hidden';
      refs.media.style.background = colors.panelBg === '#ffffff' ? '#f0f0f0' : '#191921';
    }

    const placeholder = panel.querySelector('.tutorial-media-placeholder');
    if (placeholder) {
      placeholder.style.display = refs.media && refs.media.classList.contains('has-video') ? 'none' : 'block';
    }

    if (refs.mediaLoader) {
      refs.mediaLoader.style.display = 'none';
    }

    if (refs.video) {
      refs.video.style.width = '100%';
      refs.video.style.height = '100%';
      refs.video.style.objectFit = 'cover';
      refs.video.style.display = 'block';
      refs.video.style.opacity = '1';
      refs.video.style.borderRadius = '12px';
    }

    if (refs.copy) {
      refs.copy.style.display = 'flex';
      refs.copy.style.flexDirection = 'column';
      refs.copy.style.gap = '8px';
      refs.copy.style.minWidth = '0';
      refs.copy.style.overflow = 'hidden';
    }

    if (refs.title) {
      refs.title.style.margin = '0';
      refs.title.style.fontSize = '20px';
      refs.title.style.fontWeight = '600';
      refs.title.style.lineHeight = '1.25';
      refs.title.style.color = colors.text;
    }

    if (refs.subtitle) {
      refs.subtitle.style.margin = '0';
      refs.subtitle.style.fontSize = '14px';
      refs.subtitle.style.lineHeight = '1.35';
      refs.subtitle.style.color = colors.text;
    }

    if (refs.bullets) {
      refs.bullets.style.margin = '0';
      refs.bullets.style.paddingLeft = '16px';
      refs.bullets.style.display = 'flex';
      refs.bullets.style.flexDirection = 'column';
      refs.bullets.style.gap = '6px';
      refs.bullets.style.listStyle = 'disc';
    }

    panel.querySelectorAll('.tutorial-bullets li').forEach((li) => {
      li.style.color = colors.text;
      li.style.fontSize = '13px';
      li.style.lineHeight = '1.35';
      li.style.padding = '3px 6px';
    });

    panel.querySelectorAll('.tutorial-vr-mesh-text-line').forEach((line) => {
      line.style.display = 'block';
      line.style.color = colors.text;
      line.style.fontSize = '13px';
      line.style.lineHeight = '1.35';
    });

    if (refs.note) {
      refs.note.style.margin = '0';
      refs.note.style.fontSize = '13px';
      refs.note.style.lineHeight = '1.35';
      refs.note.style.color = colors.text;
    }

    const divider = panel.querySelector('.tutorial-copy-divider');
    if (divider) {
      divider.style.height = '1px';
      divider.style.background = colors.panelBg === '#ffffff' ? 'rgba(30,30,30,0.12)' : 'rgba(255,255,255,0.15)';
      divider.style.margin = '12px 0';
    }

    const footer = panel.querySelector('.tutorial-footer');
    if (footer) {
      footer.style.display = 'flex';
      footer.style.flexDirection = 'column';
      footer.style.gap = '12px';
      footer.style.flex = '0 0 auto';
    }

    const dots = refs.dots;
    if (dots) {
      dots.style.display = 'flex';
      dots.style.gap = '8px';
      dots.style.justifyContent = 'center';
    }

    panel.querySelectorAll('.tutorial-dot').forEach((dot) => {
      dot.style.width = '10px';
      dot.style.height = '10px';
      dot.style.borderRadius = '50%';
      dot.style.border = '1px solid ' + colors.panelBorder;
      dot.style.background = dot.classList.contains('active') ? colors.panelBorder : 'transparent';
      dot.style.cursor = 'pointer';
      dot.style.padding = '0';
    });

    const actions = panel.querySelector('.tutorial-actions');
    if (actions) {
      actions.style.display = 'flex';
      actions.style.gap = '8px';
      actions.style.flexWrap = 'wrap';
      actions.style.justifyContent = 'flex-end';
    }

    function styleActionButton(btn, primary) {
      if (!btn || btn.hidden) {
        return;
      }
      btn.style.display = 'inline-flex';
      btn.style.alignItems = 'center';
      btn.style.justifyContent = 'center';
      btn.style.padding = '8px 16px';
      btn.style.borderRadius = '8px';
      btn.style.border = 'none';
      btn.style.cursor = 'pointer';
      btn.style.fontSize = '14px';
      btn.style.fontWeight = '600';
      btn.style.fontFamily = 'Inter, sans-serif';
      btn.style.background = primary ? colors.panelBorder : 'rgba(255,255,255,0.1)';
      btn.style.color = primary ? '#ffffff' : colors.text;
    }

    styleActionButton(refs.next, true);
    styleActionButton(refs.prev, false);
    styleActionButton(refs.remind, false);
  }

  function waitForPanelVideoReady(panelVideo) {
    return new Promise((resolve) => {
      if (!panelVideo || !panelVideo.getAttribute('src') || panelVideo.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        resolve();
        return;
      }
      const timeout = setTimeout(resolve, 3000);
      const finish = () => {
        clearTimeout(timeout);
        resolve();
      };
      panelVideo.addEventListener('loadeddata', finish, { once: true });
      panelVideo.addEventListener('canplay', finish, { once: true });
      panelVideo.addEventListener('error', finish, { once: true });
    });
  }

  function waitForPanelImagesReady(panel) {
    return new Promise((resolve) => {
      if (!panel) {
        resolve();
        return;
      }
      const images = Array.from(panel.querySelectorAll('img')).filter((image) => image.src);
      if (!images.length) {
        resolve();
        return;
      }
      const imagePromises = images.map((image) => {
        if (typeof image.decode === 'function') {
          return image.decode().catch(() => { });
        }
        if (image.complete) {
          return Promise.resolve();
        }
        return new Promise((imageResolve) => {
          const done = () => {
            image.removeEventListener('load', done);
            image.removeEventListener('error', done);
            imageResolve();
          };
          image.addEventListener('load', done);
          image.addEventListener('error', done);
        });
      });
      const timeout = new Promise((timeoutResolve) => {
        setTimeout(timeoutResolve, 2000);
      });
      Promise.race([Promise.all(imagePromises), timeout]).then(resolve);
    });
  }

  function playPanelVideo(refs) {
    if (!refs || !refs.video || !refs.video.getAttribute('src')) {
      return;
    }
    refs.video.muted = true;
    refs.video.playsInline = true;
    const playPromise = refs.video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => { });
    }
  }

  function applyVrTutorialMeshScale(meshEl, panelEl) {
    if (!meshEl || !panelEl) {
      return;
    }
    const meshObject = meshEl.getObject3D('html');
    const planeWidth = meshObject && meshObject.geometry && meshObject.geometry.parameters
      ? meshObject.geometry.parameters.width
      : 0;
    const resolvedPlaneWidth = planeWidth > 0
      ? planeWidth
      : Math.max(panelEl.getBoundingClientRect().width, 1) * VR_TUTORIAL_MESH_RESOLUTION * 0.001;
    const scale = VR_TUTORIAL_MESH_TARGET_WIDTH_METERS / resolvedPlaneWidth;
    meshEl.setAttribute('scale', scale + ' ' + scale + ' ' + scale);
  }

  function getVrTutorialMeshParent() {
    return document.getElementById('cameraRig');
  }

  function positionVrTutorialMesh(meshEl) {
    const parent = getVrTutorialMeshParent();
    if (!meshEl || !parent) {
      return;
    }
    if (meshEl.parentElement !== parent) {
      parent.appendChild(meshEl);
    }
    meshEl.setAttribute('position', '0 0 -' + VR_TUTORIAL_MESH_RIG_DISTANCE);
    meshEl.setAttribute('rotation', '0 0 0');
  }

  function repositionActiveVrTutorialMeshes() {
    if (window.VrTutorialNative && VrTutorialNative.isVisible()) {
      VrTutorialNative.reposition();
      return;
    }
    if (vrTutorialMeshSteps) {
      vrTutorialMeshSteps.forEach((bundle) => {
        positionVrTutorialMesh(bundle.mesh);
      });
      return;
    }
    if (vrTutorialMeshEl) {
      positionVrTutorialMesh(vrTutorialMeshEl);
    }
  }

  function splitTutorialMeshText(text, maxChars) {
    const words = String(text || '').trim().split(/\s+/).filter(Boolean);
    const lines = [];
    let line = '';
    words.forEach((word) => {
      const nextLine = line ? line + ' ' + word : word;
      if (line && nextLine.length > maxChars) {
        lines.push(line);
        line = word;
      } else {
        line = nextLine;
      }
    });
    if (line) {
      lines.push(line);
    }
    return lines.length ? lines : [''];
  }

  function setTutorialMeshWrappedText(element, text, maxChars) {
    if (!element) {
      return;
    }
    element.textContent = '';
    splitTutorialMeshText(text, maxChars).forEach((line) => {
      const span = document.createElement('span');
      span.className = 'tutorial-vr-mesh-text-line';
      span.textContent = line;
      element.appendChild(span);
    });
  }

  function renderStepIntoPanel(stepIndex, refs) {
    const step = manifest.steps[stepIndex];
    if (!step || !refs) {
      return;
    }
    clearPanelMediaLoading(refs);
    refs.title.textContent = step.title;
    refs.subtitle.textContent = step.subtitle || '';
    refs.bullets.innerHTML = '';
    resolveStepBullets(step).forEach((text) => {
      const li = document.createElement('li');
      setTutorialMeshWrappedText(li, text, 42);
      refs.bullets.appendChild(li);
    });
    setTutorialMeshWrappedText(refs.note, step.note || '', 44);
    refs.copy.classList.toggle('has-note', Boolean(step.note));
    syncMobileTutorialCopyClasses(step, refs);
    if (step.video) {
      refs.media.classList.add('has-video');
      refs.video.loop = true;
      refs.video.muted = true;
      refs.video.playsInline = true;
      refs.video.setAttribute('playsinline', '');
      refs.video.src = step.video;
      refs.video.load();
    } else {
      refs.media.classList.remove('has-video');
      refs.video.removeAttribute('src');
      refs.video.load();
    }
    setMeshOverlayContext(refs);
    renderOverlay(step);
    syncMediaAspectRatioForRefs(refs);
    clearMeshOverlayContext();
    syncMobileTutorialActionLabels(stepIndex, refs);
    syncPrevButtonVisibilityForRefs(stepIndex, refs);
    renderDotsIntoPanel(stepIndex, refs.dots);
  }

  function resolveTutorialMeshActionTarget(normalizedX, normalizedY, refs) {
    if (!refs || !refs.panel) {
      return null;
    }
    const panel = refs.panel;
    const panelRect = panel.getBoundingClientRect();
    const panelWidth = panel.offsetWidth || panelRect.width || VR_TUTORIAL_MESH_BASE_WIDTH;
    const panelHeight = panel.offsetHeight || panelRect.height || VR_TUTORIAL_MESH_BASE_HEIGHT;
    if (!panelWidth || !panelHeight) {
      return null;
    }
    const px = normalizedX * panelWidth;
    const py = normalizedY * panelHeight;

    function pointInElement(el) {
      if (!el || el.hidden || el.getAttribute('aria-hidden') === 'true') {
        return false;
      }
      if (el.offsetParent === null && el !== document.body && el !== document.documentElement) {
        const pos = el.style && el.style.position;
        if (pos !== 'fixed' && pos !== 'sticky') {
          return false;
        }
      }
      const bounds = el.getBoundingClientRect();
      if (!bounds.width || !bounds.height) {
        return false;
      }
      const origin = panelRect.width ? panelRect : { left: 0, top: 0, width: panelWidth, height: panelHeight };
      const left = bounds.left - origin.left;
      const top = bounds.top - origin.top;
      const width = bounds.width;
      const height = bounds.height;
      return px >= left && px <= left + width && py >= top && py <= top + height;
    }

    if (pointInElement(refs.next)) {
      return 'next';
    }
    if (pointInElement(refs.prev)) {
      return 'prev';
    }
    if (pointInElement(refs.remind)) {
      return 'skip';
    }
    if (pointInElement(refs.close)) {
      return 'close';
    }
    if (refs.dots) {
      const dots = refs.dots.querySelectorAll('.tutorial-dot');
      for (let i = 0; i < dots.length; i += 1) {
        if (pointInElement(dots[i])) {
          return { dotIndex: i };
        }
      }
    }
    return null;
  }

  function handleVrTutorialMeshClick(evt) {
    if (!shouldUseVrTutorialMesh() || !isTutorialOpen() || !vrTutorialMeshSteps) {
      return;
    }
    const mesh = evt.currentTarget;
    const activeBundle = vrTutorialMeshSteps[vrTutorialActiveStepIndex];
    if (!activeBundle || mesh !== activeBundle.mesh) {
      return;
    }
    const intersection = evt.detail && evt.detail.intersection;
    if (!intersection || !intersection.uv) {
      return;
    }
    const action = resolveTutorialMeshActionTarget(intersection.uv.x, 1 - intersection.uv.y, activeBundle.refs);
    if (!action) {
      return;
    }
    evt.stopPropagation();
    evt.stopImmediatePropagation();
    if (action === 'next') {
      goNext();
      return;
    }
    if (action === 'prev') {
      if (currentStep > 0) {
        activateVrTutorialMeshStep(currentStep - 1);
      }
      return;
    }
    if (action === 'skip') {
      skipTutorial();
      return;
    }
    if (action === 'close') {
      dismissForSession();
      return;
    }
    if (action.dotIndex != null) {
      activateVrTutorialMeshStep(action.dotIndex);
    }
  }

  function bindVrTutorialMeshNavigation(mesh) {
    if (!mesh || mesh.__tutorialNavBound) {
      return;
    }
    mesh.__tutorialNavBound = true;
    mesh.addEventListener('click', handleVrTutorialMeshClick, true);
  }

  function ensureVrTutorialMeshHost() {
    let host = document.getElementById('tutorial-vr-mesh-host');
    if (!host) {
      host = document.createElement('div');
      host.id = 'tutorial-vr-mesh-host';
      document.body.appendChild(host);
    } else if (host.parentElement !== document.body) {
      document.body.appendChild(host);
    }
    return host;
  }

  function waitForVrSceneReady() {
    return new Promise((resolve) => {
      const scene = document.getElementById('main-scene');
      if (!scene) {
        resolve();
        return;
      }
      if (scene.hasLoaded) {
        resolve();
        return;
      }
      scene.addEventListener('loaded', () => resolve(), { once: true });
      setTimeout(resolve, 5000);
    });
  }

  async function waitForMeshHtmlObject(meshEl, attempts) {
    const maxAttempts = typeof attempts === 'number' ? attempts : 40;
    for (let i = 0; i < maxAttempts; i += 1) {
      if (meshEl && meshEl.getObject3D && meshEl.getObject3D('html')) {
        return meshEl.getObject3D('html');
      }
      await waitForNextFrame();
    }
    return null;
  }

  async function buildVrTutorialMeshSteps() {
    return null;
  }

  function destroyVrTutorialMeshSteps() {
    stopVrMeshRefreshLoop();
    if (vrTutorialMeshSteps) {
      vrTutorialMeshSteps.forEach((bundle) => {
        if (bundle.mesh && bundle.mesh.parentNode) {
          bundle.mesh.parentNode.removeChild(bundle.mesh);
        }
      });
    }
    const host = document.getElementById('tutorial-vr-mesh-host');
    if (host) {
      host.innerHTML = '';
    }
    vrTutorialMeshSteps = null;
    vrTutorialActiveStepIndex = -1;
    vrTutorialMeshEl = null;
    meshActiveVideoEl = null;
    meshActiveRefs = null;
    clearMeshOverlayContext();
  }

  function activateVrTutorialMeshStep(stepIndex) {
    const step = manifest.steps[stepIndex];
    if (!step || !window.VrTutorialNative) {
      return false;
    }
    stopMouseOverlaySync();
    stopBulletHighlightSync();
    currentStep = stepIndex;
    vrTutorialActiveStepIndex = stepIndex;
    meshActiveRefs = null;
    const handlers = getVrTutorialNativeHandlers();
    const meshStep = step.videoMesh ? { ...step, video: step.videoMesh } : step;
    const shown = VrTutorialNative.setStep({
      stepIndex: stepIndex,
      step: meshStep,
      bullets: resolveStepBullets(step),
      totalSteps: manifest.steps.length,
      isLightTheme: isLightTutorialTheme(),
      onNext: handlers.onNext,
      onPrev: handlers.onPrev,
      onSkip: handlers.onSkip,
      onClose: handlers.onClose,
      onDot: handlers.onDot
    });
    if (!shown) {
      applyNativePresentationMode(false);
      return false;
    }
    meshActiveVideoEl = VrTutorialNative.getVideoEl();
    applyNativePresentationMode(true);
    syncTutorialPageInteractionLock();
    playTutorialVideo();
    saveState({
      step: stepIndex,
      status: (() => {
        const status = loadState()?.status;
        if (status === 'completed' || status === 'skipped') {
          return status;
        }
        return status || 'pending';
      })()
    });
    return true;
  }

  function restorePanelToOverlay() {
    if (!panelSourceEl || panelSourceEl.parentElement === overlay) {
      return;
    }
    if (backdropEl && backdropEl.parentElement === overlay) {
      overlay.insertBefore(panelSourceEl, backdropEl.nextSibling);
    } else {
      overlay.appendChild(panelSourceEl);
    }
  }

  function measureTutorialPanelMeshHeight() {
    if (!panelSourceEl) {
      return VR_TUTORIAL_MESH_BASE_HEIGHT;
    }
    panelSourceEl.style.height = 'auto';
    panelSourceEl.style.minHeight = '0';
    const measured = Math.max(panelSourceEl.scrollHeight, panelSourceEl.offsetHeight);
    return Math.min(Math.max(measured, 1), VR_TUTORIAL_MESH_BASE_HEIGHT);
  }

  function prepareTutorialPanelSourceForMesh() {
    if (!panelSourceEl) {
      return;
    }
    restorePanelToOverlay();
    clearMobileTutorialLayout();
    if (!panelSourceEl.id) {
      panelSourceEl.id = 'tutorial-panel-source';
    }
    panelSourceEl.__htmlMeshPixelRatio = VR_TUTORIAL_MESH_RESOLUTION;
    panelSourceEl.style.width = VR_TUTORIAL_MESH_BASE_WIDTH + 'px';
    panelSourceEl.style.maxHeight = VR_TUTORIAL_MESH_BASE_HEIGHT + 'px';
    panelSourceEl.style.height = 'auto';
    panelSourceEl.style.minHeight = '0';
    const measuredHeight = measureTutorialPanelMeshHeight();
    panelSourceEl.style.height = measuredHeight + 'px';
  }

  function resetTutorialPanelSourceForOverlay() {
    if (!panelSourceEl) {
      return;
    }
    restorePanelToOverlay();
    panelSourceEl.style.width = '';
    panelSourceEl.style.height = '';
    panelSourceEl.style.minHeight = '';
    panelSourceEl.style.maxHeight = '';
    delete panelSourceEl.__htmlMeshPixelRatio;
  }

  function waitForNextFrame() {
    return waitFrame();
  }

  function refreshVrTutorialMeshEntity(meshEl) {
    return;
  }

  function refreshVrTutorialMesh() {
    return;
  }

  function stopVrMeshRefreshLoop() {
    if (vrMeshRefreshTimer) {
      clearInterval(vrMeshRefreshTimer);
      vrMeshRefreshTimer = null;
    }
  }

  function startVrMeshRefreshLoop() {
    stopVrMeshRefreshLoop();
  }

  function hideVrTutorialMesh() {
    stopVrMeshRefreshLoop();
    stopMouseOverlaySync();
    stopBulletHighlightSync();
    if (window.VrTutorialNative) {
      VrTutorialNative.destroy();
    }
    destroyVrTutorialMeshSteps();
    removeVrTutorialBackdrop();
    resetTutorialPanelSourceForOverlay();
    overlay.classList.remove('tutorial-vr-mesh-mode');
    meshActiveVideoEl = null;
  }

  async function showVrTutorialMesh() {
    ensureVrScenePlaying();
    removeVrTutorialBackdrop();
    syncTutorialTheme();
    if (!window.VrTutorialNative) {
      applyNativePresentationMode(false);
      return false;
    }
    if (VrTutorialNative.isVisible()
      && typeof VrTutorialNative.getActiveStepIndex === 'function'
      && VrTutorialNative.getActiveStepIndex() === currentStep) {
      applyNativePresentationMode(true);
      syncTutorialPageInteractionLock();
      repositionActiveVrTutorialMeshes();
      return true;
    }
    const ready = await VrTutorialNative.ensureReady();
    if (!ready) {
      applyNativePresentationMode(false);
      return false;
    }
    await waitForVrSceneReady();
    destroyVrTutorialMeshSteps();
    const shown = activateVrTutorialMeshStep(currentStep);
    if (!shown) {
      return false;
    }
    repositionActiveVrTutorialMeshes();
    return true;
  }

  function ensureVrScenePlaying() {
    const scene = document.getElementById('main-scene');
    if (!scene || typeof scene.play !== 'function') {
      return;
    }
    scene.play();
    vrScenePausedForTutorial = false;
  }

  function shouldKeepVrScenePlaying() {
    return isVrTutorialPage() && isTutorialOpen() && isImmersiveVrActive();
  }

  function syncVrOverlayPointerGuard() {
    if (!isVrTutorialPage() || !isTutorialOpen()) {
      overlay.classList.remove('tutorial-vr-immersive-guard');
      syncTutorialPageInteractionLock();
      return;
    }
    overlay.classList.toggle(
      'tutorial-vr-immersive-guard',
      isImmersiveVrActive() || isVrImmersiveEntryPending()
    );
    syncTutorialPageInteractionLock();
  }

  function stopVrScenePlayWatchdog() {
    if (vrScenePlayWatchdogRaf) {
      cancelFrame(vrScenePlayWatchdogRaf);
      vrScenePlayWatchdogRaf = null;
    }
  }

  function runVrScenePlayWatchdog() {
    stopVrScenePlayWatchdog();
    const loop = () => {
      if (!shouldKeepVrScenePlaying()) {
        vrScenePlayWatchdogRaf = null;
        syncVrOverlayPointerGuard();
        return;
      }
      const scene = document.getElementById('main-scene');
      if (scene && scene.is && scene.is('paused')) {
        ensureVrScenePlaying();
      }
      syncVrOverlayPointerGuard();
      vrScenePlayWatchdogRaf = scheduleFrame(loop);
    };
    vrScenePlayWatchdogRaf = scheduleFrame(loop);
  }

  function startVrScenePlayWatchdogIfNeeded() {
    syncVrOverlayPointerGuard();
    if (shouldKeepVrScenePlaying()) {
      runVrScenePlayWatchdog();
    }
  }

  function ensureVrScenePlayingForMeshTutorial() {
    if (!isVrTutorialPage() || !isTutorialOpen() || !shouldUseVrTutorialMesh()) {
      return;
    }
    ensureVrScenePlaying();
  }

  function removeVrTutorialBackdrop() {
    const existing = document.getElementById('tutorial-vr-backdrop');
    if (existing && existing.parentNode) {
      existing.parentNode.removeChild(existing);
    }
  }

  async function syncTutorialPresentation(syncGen) {
    const gen = typeof syncGen === 'number' ? syncGen : vrPresentationSyncGen;
    if (!isVrTutorialPage() || !isTutorialOpen()) {
      return;
    }
    if (gen !== vrPresentationSyncGen) {
      return;
    }
    if (shouldUseVrTutorialMesh()) {
      ensureVrScenePlaying();
      if (window.VrTutorialNative
        && VrTutorialNative.isVisible()
        && typeof VrTutorialNative.getActiveStepIndex === 'function'
        && VrTutorialNative.getActiveStepIndex() === currentStep) {
        applyNativePresentationMode(true);
        syncTutorialPageInteractionLock();
        repositionActiveVrTutorialMeshes();
        ensureVrScenePlaying();
        startVrScenePlayWatchdogIfNeeded();
        return;
      }
      const nativeShown = await showVrTutorialMesh();
      if (gen !== vrPresentationSyncGen) {
        return;
      }
      if (nativeShown) {
        if (!(window.VrTutorialNative
          && VrTutorialNative.isVisible()
          && typeof VrTutorialNative.getActiveStepIndex === 'function'
          && VrTutorialNative.getActiveStepIndex() === currentStep)) {
          activateVrTutorialMeshStep(currentStep);
        } else {
          repositionActiveVrTutorialMeshes();
        }
      } else {
        applyNativePresentationMode(false);
        renderStep(currentStep, { force2d: true });
      }
      ensureVrScenePlaying();
      startVrScenePlayWatchdogIfNeeded();
      return;
    }
    if (gen !== vrPresentationSyncGen) {
      return;
    }
    applyNativePresentationMode(false);
    syncTutorialPageInteractionLock();
    hideVrTutorialMesh();
    ensureVrScenePlaying();
    if (shouldUseFixedTutorialMediaSize()) {
      scheduleFrame(() => {
        syncMobileTutorialLayout();
      });
    }
  }

  function openTutorialFromVrEntry() {
    if (!isVrTutorialPage() || isTutorialOpen() || !shouldAutoOpen()) {
      return;
    }
    openVrMeshTutorial(loadState()?.step || 0);
  }

  function markVrImmersiveEntryPending() {
    vrImmersiveEntryPendingUntil = Date.now() + 4000;
  }

  function scheduleVrTutorialPresentationSync() {
    vrPresentationSyncGen += 1;
    const gen = vrPresentationSyncGen;
    syncTutorialPresentation(gen);
    scheduleFrame(() => {
      if (gen !== vrPresentationSyncGen) {
        return;
      }
      syncTutorialPresentation(gen);
      repositionActiveVrTutorialMeshes();
    });
    [100, 250, 500, 1000, 1500, 2500, 4000].forEach((delay) => {
      setTimeout(() => {
        if (!isTutorialOpen() || !isVrTutorialPage() || gen !== vrPresentationSyncGen) {
          return;
        }
        syncTutorialPresentation(gen);
        repositionActiveVrTutorialMeshes();
        if (delay >= 4000) {
          clearVrImmersiveEntryPending();
        }
      }, delay);
    });
  }

  function clearVrImmersiveEntryPending() {
    vrImmersiveEntryPendingUntil = 0;
  }

  function isVrImmersiveEntryPending() {
    return Date.now() < vrImmersiveEntryPendingUntil;
  }

  function handleVrTutorialAppStateChange() {
    if (!isTutorialOpen() || !isVrTutorialPage()) {
      return;
    }
    if (window.AppState && window.AppState.isTrueVRMode) {
      ensureVrScenePlaying();
      startVrScenePlayWatchdogIfNeeded();
    }
    if (isImmersiveVrActive() || isVrImmersiveEntryPending()) {
      if (isVrTutorialDevice() || vrTutorialForceMeshFromUrl) {
        vrTutorialForceMesh = true;
      }
    }
    scheduleVrTutorialPresentationSync();
  }

  function syncTutorialPresentationOnVrChange(event) {
    const isEnteringVr = event && event.type === 'enter-vr';
    if (isEnteringVr) {
      markVrImmersiveEntryPending();
      if (isVrTutorialDevice() || vrTutorialForceMeshFromUrl) {
        vrTutorialForceMesh = true;
      }
      ensureVrScenePlaying();
      startVrScenePlayWatchdogIfNeeded();
    } else {
      clearVrImmersiveEntryPending();
      stopVrScenePlayWatchdog();
      syncVrOverlayPointerGuard();
      if (!vrTutorialForceMeshFromUrl) {
        vrTutorialForceMesh = false;
      }
    }
    if (!isTutorialOpen()) {
      if (isEnteringVr) {
        openTutorialFromVrEntry();
      }
      return;
    }
    scheduleVrTutorialPresentationSync();
  }

  function pauseVrSceneForTutorial() {
    return;
  }

  function resumeVrSceneAfterTutorial() {
    if (!vrScenePausedForTutorial) {
      return;
    }
    const scene = document.getElementById('main-scene');
    if (scene && typeof scene.play === 'function') {
      scene.play();
    }
    vrScenePausedForTutorial = false;
  }

  function isImmersiveVrActive() {
    const scene = document.getElementById('main-scene');
    if (!scene) {
      return false;
    }
    if (scene.is && scene.is('vr-mode')) {
      return true;
    }
    const renderer = scene.renderer;
    const xr = renderer && renderer.xr;
    if (xr && typeof xr.getSession === 'function') {
      const session = xr.getSession();
      if (session) {
        return true;
      }
    }
    if (xr && typeof xr.isPresenting === 'boolean') {
      return xr.isPresenting;
    }
    if (scene.xrSession) {
      return true;
    }
    return false;
  }

  function bindVrTutorialImmersiveHandlers() {
    if (!isVrTutorialPage()) {
      return;
    }

    const attach = (scene) => {
      scene.addEventListener('enter-vr', syncTutorialPresentationOnVrChange);
      scene.addEventListener('exit-vr', syncTutorialPresentationOnVrChange);
      scene.addEventListener('app-state-changed', handleVrTutorialAppStateChange);
    };

    const scene = document.getElementById('main-scene');
    if (!scene) {
      return;
    }
    if (scene.hasLoaded) {
      attach(scene);
    } else {
      scene.addEventListener('loaded', () => attach(scene), { once: true });
    }
  }

  function shouldSuppressMediaLoadingFlash() {
    if (isVrTutorialPage() && tutorialVideoHasPlayed) {
      return true;
    }
    return videoEl.loop && Boolean(videoEl.getAttribute('src'));
  }

  function isTutorialMediaLoading() {
    const activeMedia = meshActiveRefs && meshActiveRefs.media ? meshActiveRefs.media : mediaEl;
    return Boolean(activeMedia && activeMedia.classList.contains('is-loading'));
  }

  function setMediaLoading(loading) {
    if (!mediaEl) {
      return;
    }
    if (isVrTutorialPage() && loading && tutorialVideoHasPlayed) {
      return;
    }
    mediaEl.classList.toggle('is-loading', loading);
    if (mediaLoaderEl) {
      mediaLoaderEl.setAttribute('aria-hidden', loading ? 'false' : 'true');
    }
    if (mouseOverlayState) {
      updateMouseOverlay();
    }
  }

  function isTutorialVideoReady() {
    return videoEl.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA;
  }

  function getTutorialCopyEl() {
    return meshActiveRefs ? meshActiveRefs.copy : copyEl;
  }

  function syncMediaAspectRatioForRefs(refs) {
    if (!refs || !refs.media || !refs.video) {
      return;
    }
    if (!refs.video.videoWidth || !refs.video.videoHeight) {
      return;
    }
    refs.media.style.aspectRatio = refs.video.videoWidth + ' / ' + refs.video.videoHeight;
  }

  function playTutorialVideo() {
    const activeVideo = meshActiveVideoEl || videoEl;
    if (!activeVideo || !activeVideo.getAttribute('src')) {
      return;
    }
    activeVideo.muted = true;
    const playPromise = activeVideo.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => { });
    }
  }

  function syncMediaLoadingState() {
    if (!mediaEl.classList.contains('has-video') || !videoEl.getAttribute('src')) {
      setMediaLoading(false);
      return;
    }
    setMediaLoading(!isTutorialVideoReady());
  }

  function syncHtmlTutorialOverlayAfterMediaReady() {
    if (!isTutorialOpen()) {
      return;
    }
    const nativeActive = shouldUseVrTutorialMesh()
      && window.VrTutorialNative
      && VrTutorialNative.isVisible();
    if (nativeActive) {
      playTutorialVideo();
      return;
    }
    if (shouldUseVrTutorialMesh()) {
      return;
    }
    const step = manifest.steps[currentStep];
    if (!step || !step.overlay) {
      return;
    }
    renderOverlay(step);
    playTutorialVideo();
  }

  function shouldUseFixedTutorialMediaSize() {
    return window.matchMedia('(max-width: 1032px)').matches;
  }

  function isMobileTutorialPortrait() {
    return window.matchMedia('(max-width: 1032px) and (orientation: portrait)').matches;
  }

  function isMobileTutorialLandscape() {
    return window.matchMedia('(max-width: 1032px) and (orientation: landscape)').matches;
  }

  function clearMobileTutorialLayout() {
    const panel = overlay.querySelector('.tutorial-panel');
    if (panel) {
      panel.classList.remove('tutorial-mobile-fixed');
      panel.style.removeProperty('height');
      panel.style.removeProperty('min-height');
      panel.style.removeProperty('max-height');
    }
    const body = overlay.querySelector('.tutorial-body');
    if (body) {
      body.style.removeProperty('flex');
      body.style.removeProperty('min-height');
      body.style.removeProperty('max-height');
      body.style.removeProperty('overflow');
    }
    if (copyEl) {
      copyEl.style.removeProperty('flex');
      copyEl.style.removeProperty('height');
      copyEl.style.removeProperty('min-height');
      copyEl.style.removeProperty('max-height');
      copyEl.style.removeProperty('overflow');
    }
    if (mediaEl) {
      mediaEl.style.removeProperty('height');
      mediaEl.style.removeProperty('min-height');
      mediaEl.style.removeProperty('max-height');
      mediaEl.style.removeProperty('flex');
      mediaEl.classList.remove('tutorial-mobile-media-grid');
    }
    overlay.style.removeProperty('--tutorial-mobile-media-h');
    overlay.style.removeProperty('--tutorial-mobile-copy-h');
  }

  function getMobileTutorialContentWidth() {
    if (isMobileTutorialPortrait()) {
      return Math.min(window.innerWidth - 32, 420) - 32;
    }
    if (isMobileTutorialLandscape()) {
      const panelWidth = Math.min(760, Math.round(window.innerWidth * 0.84));
      const bodyWidth = panelWidth - 28;
      return Math.round(bodyWidth * 1.25 / (1.25 + 0.75));
    }
    return 0;
  }

  function getMobileTutorialPanelHeight() {
    if (isMobileTutorialPortrait()) {
      return Math.min(720, window.innerHeight - 32);
    }
    if (isMobileTutorialLandscape()) {
      return Math.min(480, Math.round(window.innerHeight * 0.92));
    }
    return 0;
  }

  function getMobileTutorialMediaHeight(contentWidth) {
    if (contentWidth <= 0) {
      return 0;
    }
    return Math.round(contentWidth * 9 / 16);
  }

  function applyMobileTutorialMediaHeight(totalHeight) {
    if (!mediaEl || totalHeight <= 0) {
      return;
    }
    mediaEl.style.aspectRatio = 'unset';
    mediaEl.style.height = totalHeight + 'px';
    mediaEl.style.minHeight = totalHeight + 'px';
    mediaEl.style.maxHeight = totalHeight + 'px';
    mediaEl.style.flex = '0 0 ' + totalHeight + 'px';
  }

  function placeChartSwitcherOnMedia() {
    if (!chartSwitcherEl || !mediaEl || shouldUseFixedTutorialMediaSize()) {
      return;
    }
    if (chartSwitcherEl.parentElement !== mediaEl) {
      mediaEl.appendChild(chartSwitcherEl);
    }
  }

  function getMobileTutorialBodyHeight(panelHeight) {
    if (isVrTutorialPage()) {
      return panelHeight;
    }
    const panel = overlay.querySelector('.tutorial-panel');
    const header = overlay.querySelector('.tutorial-header');
    const footer = overlay.querySelector('.tutorial-footer');
    if (!panel || !header || !footer) {
      return panelHeight;
    }
    const panelStyles = window.getComputedStyle(panel);
    const paddingY = parseFloat(panelStyles.paddingTop) + parseFloat(panelStyles.paddingBottom);
    const gap = parseFloat(panelStyles.gap) || 18;
    const bodyHeight = panelHeight
      - paddingY
      - header.offsetHeight
      - footer.offsetHeight
      - gap * 2;
    return Math.max(100, Math.round(bodyHeight));
  }

  function applyMobileTutorialLandscapeLayout(panelHeight, contentWidth) {
    const body = overlay.querySelector('.tutorial-body');
    const bodyHeight = getMobileTutorialBodyHeight(panelHeight);
    const idealMediaHeight = getMobileTutorialMediaHeight(contentWidth);
    const mediaHeight = Math.min(
      idealMediaHeight,
      Math.max(72, bodyHeight)
    );

    applyMobileTutorialMediaHeight(mediaHeight);
    overlay.style.setProperty('--tutorial-mobile-media-h', mediaHeight + 'px');

    if (body) {
      body.style.flex = '1 1 auto';
      body.style.minHeight = '0';
      body.style.maxHeight = bodyHeight + 'px';
      body.style.overflow = 'hidden';
    }
    if (copyEl) {
      copyEl.style.flex = '1 1 0';
      copyEl.style.removeProperty('height');
      copyEl.style.minHeight = '0';
      copyEl.style.maxHeight = bodyHeight + 'px';
      copyEl.style.overflow = 'auto';
    }
    overlay.style.removeProperty('--tutorial-mobile-copy-h');
  }

  function applyMobileTutorialCopyHeight(panelHeight, mediaHeight) {
    if (!copyEl || !isMobileTutorialPortrait() || isVrTutorialPage()) {
      return;
    }
    const panel = overlay.querySelector('.tutorial-panel');
    const header = overlay.querySelector('.tutorial-header');
    const footer = overlay.querySelector('.tutorial-footer');
    if (!panel || !header || !footer) {
      return;
    }
    const panelStyles = window.getComputedStyle(panel);
    const paddingY = parseFloat(panelStyles.paddingTop) + parseFloat(panelStyles.paddingBottom);
    const gap = parseFloat(panelStyles.gap) || 18;
    const copyHeight = panelHeight
      - paddingY
      - header.offsetHeight
      - footer.offsetHeight
      - gap * 2
      - mediaHeight
      - (copyEl.classList.contains('has-note') ? 24 : 8);
    const denseBoost = copyEl.classList.contains('tutorial-copy-dense') ? 28 : 0;
    const copyH = Math.max(140, Math.round(copyHeight + denseBoost));
    copyEl.style.flex = '0 0 ' + copyH + 'px';
    copyEl.style.height = copyH + 'px';
    copyEl.style.minHeight = copyH + 'px';
    copyEl.style.maxHeight = copyH + 'px';
    overlay.style.setProperty('--tutorial-mobile-copy-h', copyH + 'px');
  }

  function syncMobileTutorialLayout() {
    const panel = overlay.querySelector('.tutorial-panel');
    if (!panel || !mediaEl) {
      return;
    }

    if (overlay.classList.contains('tutorial-vr-mesh-mode')) {
      return;
    }

    if (!shouldUseFixedTutorialMediaSize()) {
      clearMobileTutorialLayout();
      return;
    }

    panel.classList.add('tutorial-mobile-fixed');

    const panelHeight = getMobileTutorialPanelHeight();
    const contentWidth = getMobileTutorialContentWidth();

    panel.style.height = panelHeight + 'px';
    panel.style.minHeight = panelHeight + 'px';
    panel.style.maxHeight = panelHeight + 'px';

    if (isMobileTutorialLandscape()) {
      applyMobileTutorialLandscapeLayout(panelHeight, contentWidth);
    } else {
      const mediaHeight = getMobileTutorialMediaHeight(contentWidth);
      applyMobileTutorialMediaHeight(mediaHeight);
      overlay.style.setProperty('--tutorial-mobile-media-h', mediaHeight + 'px');
      applyMobileTutorialCopyHeight(panelHeight, mediaHeight);
    }
  }

  function syncMediaAspectRatio() {
    if (!mediaEl) {
      return;
    }
    if (shouldUseFixedTutorialMediaSize()) {
      syncMobileTutorialLayout();
      return;
    }
    clearMobileTutorialLayout();
    if (!videoEl.videoWidth || !videoEl.videoHeight) {
      return;
    }
    mediaEl.style.aspectRatio = videoEl.videoWidth + ' / ' + videoEl.videoHeight;
  }

  if (isVrTutorialPage()) {
    restorePanelToOverlay();
    videoEl.muted = true;
    videoEl.playsInline = true;
    videoEl.setAttribute('preload', 'auto');
    overlay.classList.add('tutorial-vr-page');
    bindVrTutorialImmersiveHandlers();
  }

  bindTutorialThemeSync();

  videoEl.addEventListener('loadstart', () => {
    if (shouldSuppressMediaLoadingFlash() && videoEl.readyState > HTMLMediaElement.HAVE_NOTHING) {
      return;
    }
    setMediaLoading(true);
  });
  videoEl.addEventListener('waiting', () => {
    if (shouldSuppressMediaLoadingFlash()) {
      return;
    }
    setMediaLoading(true);
  });
  videoEl.addEventListener('canplay', () => {
    setMediaLoading(false);
    syncHtmlTutorialOverlayAfterMediaReady();
    if (isVrTutorialPage()) {
      playTutorialVideo();
    }
  });
  videoEl.addEventListener('canplaythrough', () => {
    setMediaLoading(false);
    syncHtmlTutorialOverlayAfterMediaReady();
    if (isVrTutorialPage()) {
      playTutorialVideo();
    }
  });
  videoEl.addEventListener('loadeddata', () => {
    syncMediaAspectRatio();
    syncMediaLoadingState();
    syncHtmlTutorialOverlayAfterMediaReady();
    if (isVrTutorialPage()) {
      playTutorialVideo();
    }
  });
  videoEl.addEventListener('loadedmetadata', syncMediaAspectRatio);
  videoEl.addEventListener('playing', () => {
    tutorialVideoHasPlayed = true;
    setMediaLoading(false);
    syncHtmlTutorialOverlayAfterMediaReady();
  });
  videoEl.addEventListener('error', () => {
    setMediaLoading(false);
  });
  window.addEventListener('resize', syncMediaAspectRatio);
  window.addEventListener('orientationchange', () => {
    scheduleFrame(() => {
      syncMediaAspectRatio();
    });
  });

  function syncTutorialOnLoopRestart() {
    updateCopyHighlights();
    updateMouseOverlay();
  }

  videoEl.addEventListener('ended', syncTutorialOnLoopRestart);
  videoEl.addEventListener('seeked', () => {
    if (videoEl.currentTime < 0.05) {
      syncTutorialOnLoopRestart();
    }
  });

  function loadState() {
    try {
      if (typeof getCacheItem === 'function') {
        const cached = getCacheItem(STORAGE_KEY);
        if (cached) {
          return cached;
        }
      }
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && 'value' in parsed) {
        return parsed.value;
      }
      return parsed;
    } catch (e) {
      return null;
    }
  }

  function saveState(partial) {
    const prev = loadState() || { version: manifest.version, status: 'pending', step: 0 };
    const next = { ...prev, ...partial, version: manifest.version };
    const persistentMinutes = typeof CACHE_EXPIRATION !== 'undefined' && CACHE_EXPIRATION.PERSISTENT
      ? CACHE_EXPIRATION.PERSISTENT
      : 10 * 365 * 24 * 60;
    if (typeof setCacheItem === 'function') {
      setCacheItem(STORAGE_KEY, next, persistentMinutes);
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    }
    return next;
  }

  function resolveAuthSession() {
    let session = window.currentSession;
    if (!session && typeof window.AuthAPI !== 'undefined' && typeof window.AuthAPI.getStoredSession === 'function') {
      session = window.AuthAPI.getStoredSession();
    }
    if (!session && typeof getCacheItem === 'function') {
      session = getCacheItem('auth_session');
    }
    if (!session) {
      const sessionStr = localStorage.getItem('auth_session');
      if (sessionStr) {
        const sessionItem = JSON.parse(sessionStr);
        session = sessionItem.value || sessionItem;
        const expiry = sessionItem.expiry;
        if (expiry && Date.now() > expiry) {
          session = null;
        }
      }
    }
    return session;
  }

  function isAuthenticatedUser() {
    try {
      const session = resolveAuthSession();
      return !!(session && session.user && session.user.is_anonymous !== true);
    } catch (e) {
      return false;
    }
  }

  function shouldAutoOpen() {
    if (isAuthenticatedUser()) {
      return false;
    }
    const state = loadState();
    if (!state) {
      return true;
    }
    if (state.status === 'completed' || state.status === 'skipped') {
      return false;
    }
    return true;
  }

  function renderDots(activeIndex) {
    dotsEl.innerHTML = '';
    manifest.steps.forEach((_, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'tutorial-dot' + (index === activeIndex ? ' active' : '');
      dot.setAttribute('aria-label', 'Go to step ' + (index + 1));
      dot.addEventListener('click', () => {
        currentStep = index;
        renderStep(currentStep);
      });
      dotsEl.appendChild(dot);
    });
  }

  function parsePercentValue(value, fallback) {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
    return fallback;
  }

  function applyLmbOverlayLayout(state) {
    if (!state || state.type !== 'mouse-lmb-cycle') {
      return;
    }
    const svgRoot = getOverlaySvgEl();
    if (svgRoot) {
      svgRoot.style.setProperty('--mouse-from-left', state.fromLeft + '%');
      svgRoot.style.setProperty('--mouse-from-top', state.fromTop + '%');
    }
    if (state.wrapper) {
      const useTravelHome = lmbPointerUsesTravel(state);
      const initLeft = useTravelHome ? state.fromLeft : mouseOverlayHomeLeft(state);
      const initTop = useTravelHome ? state.fromTop : mouseOverlayHomeTop(state);
      setMouseOverlayPosition(state, initLeft, initTop);
    }
    if (state.clickCursorWrapper) {
      updateClickCursorVisuals(state, state.cursorFromLeft, state.cursorFromTop, 1, 0);
    }
    if (shouldHideLmbPointerOutsideCycle(state)) {
      hideMouseOverlay(state);
    }
  }

  function applyClickSegmentsOverlayLayout(state) {
    if (!state || state.type !== 'mouse-click-segments') {
      return;
    }
    if (state.clickCursorWrapper) {
      updateClickCursorVisuals(state, state.cursorFromLeft, state.cursorFromTop, 1, 0);
    }
  }

  function parseLmbCursorStopLeft(overlayConfig) {
    if (overlayConfig.cursorToLeft != null) {
      return parsePercentValue(overlayConfig.cursorToLeft, 50);
    }
    if (overlayConfig.cursorLeft != null) {
      return parsePercentValue(overlayConfig.cursorLeft, 50);
    }
    return 50;
  }

  function parseLmbCursorStopTop(overlayConfig) {
    if (overlayConfig.cursorToTop != null) {
      return parsePercentValue(overlayConfig.cursorToTop, 50);
    }
    if (overlayConfig.cursorTop != null) {
      return parsePercentValue(overlayConfig.cursorTop, 50);
    }
    return 50;
  }

  function easeInOut(t) {
    return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
  }

  function smoothSegmentEase(t) {
    const clamped = Math.max(0, Math.min(1, t));
    return clamped * clamped * clamped * (clamped * (clamped * 6 - 15) + 10);
  }

  function axisSegmentWave(local) {
    if (local <= 0 || local >= 1) {
      return 0;
    }
    if (local < 0.08) {
      return 0;
    }
    if (local < 0.22) {
      return -smoothSegmentEase((local - 0.08) / 0.14);
    }
    if (local < 0.30) {
      return -1;
    }
    if (local < 0.44) {
      return -smoothSegmentEase(1 - (local - 0.30) / 0.14);
    }
    if (local < 0.52) {
      return 0;
    }
    if (local < 0.66) {
      return smoothSegmentEase((local - 0.52) / 0.14);
    }
    if (local < 0.74) {
      return 1;
    }
    if (local < 0.88) {
      return smoothSegmentEase(1 - (local - 0.74) / 0.14);
    }
    return 0;
  }

  function zoomSegmentWave(local) {
    if (local <= 0 || (!isVrTutorialPage() && local >= 1)) {
      return 0;
    }
    if (local < 0.08) {
      return 0;
    }
    if (local < 0.22) {
      return smoothSegmentEase((local - 0.08) / 0.14);
    }
    if (local < 0.30) {
      return 1;
    }
    if (local < 0.44) {
      return smoothSegmentEase(1 - (local - 0.30) / 0.14);
    }
    if (isVrTutorialPage()) {
      return 0;
    }
    if (local < 0.52) {
      return 0;
    }
    if (local < 0.66) {
      return -smoothSegmentEase((local - 0.52) / 0.14);
    }
    if (local < 0.74) {
      return -1;
    }
    if (local < 0.88) {
      return -smoothSegmentEase(1 - (local - 0.74) / 0.14);
    }
    return 0;
  }

  function getSegmentWave(segment, local) {
    if (segment.button === 'scroll') {
      return zoomSegmentWave(local);
    }
    return axisSegmentWave(local);
  }

  function resolveDragSegmentAnchor(state, segment) {
    if (state.controllerSizing && segment && segment.path) {
      return {
        left: typeof state.pathCenterLeft === 'number' ? state.pathCenterLeft : 50,
        top: typeof state.pathCenterTop === 'number' ? state.pathCenterTop : state.centerTop
      };
    }
    return {
      left: state.centerLeft,
      top: state.centerTop
    };
  }

  function applyMouseOverlayAnchor(state, segment) {
    if (!state.wrapper) {
      return;
    }
    const centerAnchor = Boolean(state.controllerSizing && segment && segment.path);
    state.wrapper.classList.toggle('tutorial-mouse-overlay--anchor-end', !centerAnchor);
  }

  function getControllerTravelFromLeft(state) {
    return state.centerLeft - CONTROLLER_WIDTH_PERCENT / 2;
  }

  function resolvePathDragRange(state) {
    if (typeof state.pathDragRange === 'number') {
      return state.pathDragRange;
    }
    return state.dragRange * 2;
  }

  function resolvePathApproachTravel(state, videoProgress) {
    if (!state.controllerSizing || !state.segments) {
      return null;
    }
    const pathIndex = state.segments.findIndex((segment) => segment.path === 'diagonal-up-right');
    if (pathIndex <= 0) {
      return null;
    }
    const prev = state.segments[pathIndex - 1];
    const pathSegment = state.segments[pathIndex];
    const travelStart = prev.end;
    const travelEnd = pathSegment.start;
    if (videoProgress <= travelStart || videoProgress >= travelEnd) {
      return null;
    }
    const t = easeInOut((videoProgress - travelStart) / (travelEnd - travelStart));
    return {
      left: lerp(getControllerTravelFromLeft(state), state.pathCenterLeft, t),
      top: lerp(state.centerTop, state.pathCenterTop, t),
      t: t
    };
  }

  function showPathApproachTravel(state, approach) {
    state.wrapper.style.visibility = 'visible';
    state.wrapper.style.opacity = '1';
    state.wrapper.classList.toggle('tutorial-mouse-overlay--anchor-end', false);
    state.wrapper.style.left = approach.left + '%';
    state.wrapper.style.top = approach.top + '%';
    applyDragIconOpacities(state, { move: 1, lmb: 0, rmb: 0, scroll: 0 }, 1);
  }

  function resolveDragPathOffset(segment, local) {
    const t = smoothSegmentEase(Math.max(0, Math.min(1, local)));
    if (segment.path === 'diagonal-up-right') {
      return { x: t, y: -t };
    }
    if (segment.path === 'diagonal-back') {
      return { x: 1 - t, y: -1 + t };
    }
    return null;
  }

  function mapVideoToProgress(currentTimeSec, durationSec, startDelaySec, endPaddingSec) {
    const delay = typeof startDelaySec === 'number' ? startDelaySec : 1;
    const tailStretch = typeof endPaddingSec === 'number' ? endPaddingSec : delay;
    if (!Number.isFinite(currentTimeSec) || !Number.isFinite(durationSec)) {
      return 0;
    }
    const activeDuration = Math.max(0.1, durationSec - delay + tailStretch);
    if (currentTimeSec <= delay) {
      return 0;
    }
    return Math.max(0, Math.min(1, (currentTimeSec - delay) / activeDuration));
  }

  function resolveOverlayVideoSync(overlayConfig) {
    const sync = overlayConfig && overlayConfig.videoSync ? overlayConfig.videoSync : overlayConfig;
    const startDelaySec = sync && typeof sync.startDelaySec === 'number' ? sync.startDelaySec : 1;
    const endPaddingSec = sync && typeof sync.endPaddingSec === 'number'
      ? sync.endPaddingSec
      : startDelaySec;
    return { startDelaySec: startDelaySec, endPaddingSec: endPaddingSec };
  }

  function lerp(a, b, t) {
    return a + (b - a) * t;
  }

  function stopMouseOverlaySync() {
    if (mouseOverlayRaf) {
      cancelFrame(mouseOverlayRaf);
      mouseOverlayRaf = null;
    }
    if (!mouseOverlayState) {
      return;
    }
    const activeVideo = getOverlayVideoEl();
    activeVideo.removeEventListener('timeupdate', updateMouseOverlay);
    activeVideo.removeEventListener('seeked', updateMouseOverlay);
    activeVideo.removeEventListener('loadedmetadata', updateMouseOverlay);
    activeVideo.removeEventListener('play', startMouseOverlayLoop);
    activeVideo.removeEventListener('pause', stopMouseOverlayLoop);
    mouseOverlayState = null;
    removeChartSwitcher();
  }

  function stopBulletHighlightSync() {
    if (bulletHighlightRaf) {
      cancelFrame(bulletHighlightRaf);
      bulletHighlightRaf = null;
    }
    if (highlightSyncActive) {
      const activeVideo = getOverlayVideoEl();
      activeVideo.removeEventListener('timeupdate', updateCopyHighlights);
      activeVideo.removeEventListener('seeked', updateCopyHighlights);
      activeVideo.removeEventListener('loadedmetadata', updateCopyHighlights);
      activeVideo.removeEventListener('play', startBulletHighlightLoop);
      activeVideo.removeEventListener('pause', stopBulletHighlightLoop);
      highlightSyncActive = false;
    }
    bulletHighlightSegments = null;
    noteHighlightRange = null;
    getTutorialHighlightRefsList().forEach((refs) => {
      if (refs.bullets) {
        refs.bullets.querySelectorAll('li').forEach((li) => {
          li.classList.remove('tutorial-bullet-active');
          li.style.removeProperty('--hi');
          applyTutorialCopyHighlightStyle(li, 0);
        });
        refs.bullets.classList.remove('has-highlight-sync');
      }
      if (refs.note) {
        refs.note.classList.remove('tutorial-note-active');
        refs.note.style.removeProperty('--hi');
        applyTutorialCopyHighlightStyle(refs.note, 0);
      }
      if (refs.copy) {
        refs.copy.classList.remove('has-note-highlight-sync');
      }
    });
  }

  function smoothstep(edge0, edge1, value) {
    const t = Math.max(0, Math.min(1, (value - edge0) / (edge1 - edge0)));
    return t * t * (3 - 2 * t);
  }

  function getHighlightIntensity(segment, progress) {
    const start = segment.start;
    const end = segment.end;
    const span = end - start;
    const fade = Math.min(0.12, span * 0.3);
    const loopTailFade = end >= 1 ? 0.12 : 0;

    if (progress < start - fade) {
      return 0;
    }
    if (progress < start + fade) {
      return smoothstep(start - fade, start + fade, progress);
    }
    if (end >= 1) {
      if (progress > 1 - loopTailFade) {
        return smoothstep(1, 1 - loopTailFade, progress);
      }
      return 1;
    }
    if (progress > end + fade) {
      return 0;
    }
    if (progress > end - fade) {
      return smoothstep(end + fade, end - fade, progress);
    }
    return 1;
  }

  function normalizeBulletHighlights(highlights) {
    if (!highlights || !highlights.length) {
      return highlights;
    }
    const maxIndex = highlights.reduce((max, item) => Math.max(max, item.index), 0);
    return highlights.map((item) => (
      item.index === maxIndex ? { ...item, end: 1 } : { ...item }
    ));
  }

  function applyBulletHighlightIntensity(el, intensity) {
    const value = Math.max(0, Math.min(1, intensity));
    el.style.setProperty('--hi', String(value));
    el.classList.toggle('tutorial-bullet-active', value > 0.12);
    applyTutorialCopyHighlightStyle(el, value);
  }

  function applyNoteHighlightIntensity(el, intensity) {
    const value = Math.max(0, Math.min(1, intensity));
    el.style.setProperty('--hi', String(value));
    el.classList.toggle('tutorial-note-active', value > 0.12);
    applyTutorialCopyHighlightStyle(el, value);
  }

  function applyTutorialCopyHighlightStyle(el, intensity) {
    if (!el) {
      return;
    }
    const isActive = intensity > 0.12;
    if (!isActive) {
      const colors = getTutorialMeshColors();
      el.style.color = colors.text;
      el.style.removeProperty('border');
      el.style.removeProperty('border-color');
      el.style.removeProperty('background');
      el.style.removeProperty('background-color');
      el.style.removeProperty('box-shadow');
      el.style.removeProperty('text-shadow');
      el.style.removeProperty('font-weight');
      el.querySelectorAll('.tutorial-vr-mesh-text-line').forEach((line) => {
        line.style.color = colors.text;
        line.style.removeProperty('text-shadow');
      });
      return;
    }
    const colors = getTutorialMeshColors();
    el.style.color = colors.highlightText;
    el.style.border = '1px solid ' + colors.highlightBorder;
    el.style.borderColor = colors.highlightBorder;
    el.style.background = colors.highlightBg;
    el.style.backgroundColor = colors.highlightBg;
    el.style.boxShadow = colors.highlightShadow;
    el.style.textShadow = colors.highlightTextShadow;
    el.style.fontWeight = '600';
    el.querySelectorAll('.tutorial-vr-mesh-text-line').forEach((line) => {
      line.style.color = colors.highlightText;
      line.style.textShadow = colors.highlightTextShadow;
    });
  }

  function updateCopyHighlights() {
    if (!highlightSyncActive) {
      return;
    }

    const activeVideo = getOverlayVideoEl();
    const duration = activeVideo.duration;
    if (!Number.isFinite(duration) || duration <= 0) {
      return;
    }

    const progress = getCopyHighlightProgress();

    getTutorialHighlightRefsList().forEach((refs) => {
      if (bulletHighlightSegments && refs.bullets) {
        const items = refs.bullets.querySelectorAll('li');
        items.forEach((li, index) => {
          const segment = bulletHighlightSegments.find((item) => item.index === index);
          const intensity = segment ? getHighlightIntensity(segment, progress) : 0;
          applyBulletHighlightIntensity(li, intensity);
        });
      }

      if (noteHighlightRange && refs.note) {
        const intensity = getHighlightIntensity(noteHighlightRange, progress);
        applyNoteHighlightIntensity(refs.note, intensity);
      }
    });

    if (shouldUseVrTutorialMesh() && window.VrTutorialNative && VrTutorialNative.isVisible()) {
      return;
    }
    if (shouldUseVrTutorialMesh()) {
      refreshVrTutorialMesh();
    }
  }

  function startBulletHighlightLoop() {
    if (!highlightSyncActive || bulletHighlightRaf) {
      return;
    }
    const activeVideo = getOverlayVideoEl();
    const loop = () => {
      updateCopyHighlights();
      if (highlightSyncActive && !activeVideo.paused) {
        bulletHighlightRaf = scheduleFrame(loop);
      } else {
        bulletHighlightRaf = null;
      }
    };
    bulletHighlightRaf = scheduleFrame(loop);
  }

  function stopBulletHighlightLoop() {
    if (bulletHighlightRaf) {
      cancelFrame(bulletHighlightRaf);
      bulletHighlightRaf = null;
    }
    updateCopyHighlights();
  }

  function bindBulletHighlightSync(segments, noteHighlight) {
    stopBulletHighlightSync();

    bulletHighlightSegments = segments && segments.length > 0 ? segments : null;
    noteHighlightRange = noteHighlight || null;

    if (!bulletHighlightSegments && !noteHighlightRange) {
      return;
    }

    highlightSyncActive = true;

    getTutorialHighlightRefsList().forEach((refs) => {
      if (bulletHighlightSegments && refs.bullets) {
        refs.bullets.classList.add('has-highlight-sync');
      }
      if (noteHighlightRange && refs.copy) {
        refs.copy.classList.add('has-note-highlight-sync');
      }
    });

    const activeVideo = getOverlayVideoEl();
    activeVideo.addEventListener('timeupdate', updateCopyHighlights);
    activeVideo.addEventListener('seeked', updateCopyHighlights);
    activeVideo.addEventListener('loadedmetadata', updateCopyHighlights);
    activeVideo.addEventListener('play', startBulletHighlightLoop);
    activeVideo.addEventListener('pause', stopBulletHighlightLoop);
    updateCopyHighlights();
    if (!activeVideo.paused) {
      startBulletHighlightLoop();
    }
  }

  function isTouchTutorialDevice() {
    return window.matchMedia('(any-pointer: coarse)').matches ||
      window.matchMedia('(pointer: coarse)').matches ||
      navigator.maxTouchPoints > 0;
  }

  function touchHandUsesLmbTravel(state) {
    return Boolean(state && state.useHandIcons && isTouchTutorialDevice() && lmbPointerUsesTravel(state));
  }

  function vrControllerUsesLmbTravel(state) {
    return Boolean(state && state.travelControllerToCursor && lmbPointerUsesTravel(state));
  }

  function vrPagePointerUsesLmbTravel(state) {
    return Boolean(state && state.travelVrPagePointer && lmbPointerUsesTravel(state));
  }

  function usesVrTutorialControllerOverlay(step, overlayConfig) {
    if (!isVrTutorialDevice() || !overlayConfig) {
      return false;
    }
    if (overlayConfig.type === 'mouse-lmb-cycle') {
      return true;
    }
    return Boolean(step && step.id === 'chart-settings' && overlayConfig.type === 'mouse-click-segments');
  }

  function resolveStepBullets(step) {
    if (!step) {
      return [];
    }
    if (!isVrTutorialPage() && isTouchTutorialDevice() && step.bulletsMobile && step.bulletsMobile.length) {
      return step.bulletsMobile;
    }
    if (isVrTutorialDevice() && step.bulletsVr && step.bulletsVr.length) {
      return step.bulletsVr;
    }
    return step.bullets || [];
  }

  function renderStepBullets(step) {
    bulletsEl.innerHTML = '';
    resolveStepBullets(step).forEach((text) => {
      const li = document.createElement('li');
      li.textContent = text;
      bulletsEl.appendChild(li);
    });
  }

  function refreshStepBulletsIfOpen() {
    if (!overlay.classList.contains('show')) {
      return;
    }
    const step = manifest.steps[currentStep];
    if (!step) {
      return;
    }
    renderStepBullets(step);
    const bulletHighlights = resolveBulletHighlights(step);
    const noteHighlight = resolveNoteHighlight(step);
    if (bulletHighlights || noteHighlight) {
      bindBulletHighlightSync(bulletHighlights, noteHighlight);
    }
    playTutorialVideo();
  }

  function getCopyHighlightProgress() {
    const activeVideo = getOverlayVideoEl();
    const duration = activeVideo.duration;
    if (!Number.isFinite(duration) || duration <= 0) {
      return 0;
    }

    const step = manifest.steps[currentStep];
    const overlay = step && step.overlay;
    if (isVrTutorialPage() && overlay && overlay.type === 'mouse-drag-segments' && Array.isArray(overlay.segments)) {
      const videoSync = resolveOverlayVideoSync(overlay);
      return mapVideoToProgress(
        activeVideo.currentTime,
        duration,
        videoSync.startDelaySec,
        videoSync.endPaddingSec
      );
    }

    return activeVideo.currentTime / duration;
  }

  function removeChartSwitcher() {
    if (chartSwitcherEl) {
      chartSwitcherEl.remove();
      chartSwitcherEl = null;
    }
  }

  function createTutorialRotateMoveSwitcher() {
    const container = document.createElement('div');
    container.className = 'tutorial-rotate-move-switcher rotate-move-switcher-container tri-state show';
    container.setAttribute('aria-hidden', 'true');
    container.innerHTML = '<div class="rotate-move-switcher-wrapper">' +
      '<p class="item item-rotate">' +
      '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<path d="M8.83483 3.67002C8.65465 3.48987 8.45445 3.32572 8.24224 3.1816C8.02832 3.03624 7.80027 2.91284 7.56156 2.81327C7.32132 2.71319 7.06507 2.63312 6.80881 2.58107C6.28028 2.47298 5.72372 2.47298 5.19519 2.58107C4.93894 2.63312 4.68669 2.71319 4.44244 2.81327C3.72657 3.11452 3.11426 3.61822 2.68068 4.26254C2.5353 4.47644 2.41189 4.70446 2.31231 4.94314C2.21221 5.18335 2.13213 5.43957 2.08008 5.6958C2.02803 5.96003 2 6.23227 2 6.5005C2 6.76874 2.02803 7.04097 2.08008 7.3052C2.13213 7.56143 2.21221 7.81365 2.31231 8.05786C2.6136 8.77364 3.11736 9.38588 3.76176 9.81941C3.97397 9.96353 4.2022 10.0876 4.44244 10.1877C4.68268 10.2878 4.93894 10.3679 5.19519 10.4199C5.45946 10.472 5.73173 10.5 6 10.5C6.26827 10.5 6.54054 10.472 6.8048 10.4199C7.06106 10.3679 7.31331 10.2878 7.55756 10.1877C8.27343 9.88648 8.88574 9.38278 9.31932 8.73846C9.46346 8.52627 9.58759 8.29807 9.68769 8.05786C9.78779 7.81765 9.86787 7.56143 9.91992 7.3052C9.97197 7.04097 10 6.76874 10 6.5005H9.1" stroke="currentColor" stroke-width="0.8" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>Rotate</p>' +
      '<p class="item item-move">' +
      '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<path d="M4.59497 5.8421V2.15132C4.59497 1.97858 4.66286 1.81291 4.7837 1.69077C4.90454 1.56862 5.06844 1.5 5.23934 1.5C5.41023 1.5 5.57413 1.56862 5.69497 1.69077C5.81582 1.81291 5.8837 1.97858 5.8837 2.15132V5.40789M5.8837 5.19079V4.32237C5.8837 4.14963 5.95159 3.98396 6.07243 3.86182C6.19328 3.73967 6.35717 3.67105 6.52807 3.67105C6.69897 3.67105 6.86287 3.73967 6.9837 3.86182C7.10455 3.98396 7.17244 4.14963 7.17244 4.32237V5.40789M7.17244 4.75658C7.17244 4.58384 7.24033 4.41817 7.36117 4.29603C7.48201 4.17388 7.64591 4.10526 7.8168 4.10526C7.9877 4.10526 8.1516 4.17388 8.27244 4.29603C8.39328 4.41817 8.46117 4.58384 8.46117 4.75658V5.40789" stroke="currentColor" stroke-width="0.8" stroke-linecap="round" stroke-linejoin="round"/>' +
      '<path d="M8.46127 5.19077C8.46127 5.01803 8.52916 4.85237 8.65 4.73022C8.77084 4.60808 8.93474 4.53946 9.10563 4.53946C9.27653 4.53946 9.44043 4.60808 9.56127 4.73022C9.68211 4.85237 9.75 5.01803 9.75 5.19077V7.14472C9.75 7.83568 9.47845 8.49834 8.99508 8.98692C8.51171 9.4755 7.85612 9.74998 7.17253 9.74998H6.31338H6.40273C5.97587 9.75006 5.55568 9.64297 5.17989 9.43834C4.80409 9.23372 4.48446 8.93795 4.24969 8.57761L4.16549 8.44735C4.03146 8.23951 3.56093 7.41046 2.75389 5.96019C2.67161 5.81236 2.64963 5.63791 2.69263 5.47394C2.73563 5.30998 2.84022 5.16944 2.98415 5.08222C3.13752 4.98946 3.31712 4.95106 3.49451 4.97308C3.67191 4.9951 3.83698 5.07629 3.96359 5.2038L4.59507 5.84209M2.25 2.62633C2.57969 2.38275 2.93391 2.17506 3.30676 2.00671M7.17253 1.69885C7.73203 1.85034 8.26571 2.08631 8.75553 2.3988" stroke="currentColor" stroke-width="0.8" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>Move</p>' +
      '</div>';
    return container;
  }

  function resolveRotateMoveMode(demoProgress, segments) {
    const active = segments.find((segment) => isSegmentActive(segment, demoProgress));
    if (!active) {
      return null;
    }
    if (active.button === 'lmb') {
      return 'rotate';
    }
    if (active.button === 'rmb') {
      return 'move';
    }
    return null;
  }

  function updateRotateMoveSwitcher(demoProgress, segments) {
    if (!chartSwitcherEl || !segments) {
      return;
    }
    chartSwitcherEl.classList.remove('mode-rotate', 'mode-move');
    const mode = resolveRotateMoveMode(demoProgress, segments);
    if (mode === 'rotate') {
      chartSwitcherEl.classList.add('mode-rotate');
    } else if (mode === 'move') {
      chartSwitcherEl.classList.add('mode-move');
    }
  }

  function isVrUserAgent() {
    const ua = navigator.userAgent.toLowerCase();
    return /quest|oculus|wolvic|pico/.test(ua);
  }

  function isVrTutorialDevice() {
    if (vrTutorialForceOverride !== null) {
      return vrTutorialForceOverride;
    }
    if (!isVrTutorialPage()) {
      return false;
    }
    if (vrTutorialDeviceCache !== null) {
      return vrTutorialDeviceCache;
    }
    return isVrUserAgent();
  }

  function refreshMouseOverlayIfOpen() {
    if (!overlay.classList.contains('show')) {
      return;
    }
    if (window.VrTutorialNative && VrTutorialNative.isVisible()) {
      return;
    }
    const step = manifest.steps[currentStep];
    if (!step || !step.overlay) {
      return;
    }
    const overlayType = step.overlay.type;
    if (overlayType !== 'mouse-lmb-cycle' && overlayType !== 'mouse-drag-segments' && overlayType !== 'mouse-click-segments') {
      return;
    }
    renderOverlay(step);
    playTutorialVideo();
  }

  async function initVrTutorialDeviceDetection() {
    if (!isVrTutorialPage()) {
      vrTutorialDeviceCache = false;
      return;
    }
    vrTutorialDeviceCache = isVrUserAgent();
    if (vrTutorialDeviceCache) {
      refreshMouseOverlayIfOpen();
      refreshStepBulletsIfOpen();
    }
  }

  function controllerDisplayScale(scale) {
    return CONTROLLER_BASE_SCALE * scale;
  }

  function resolveControllerOverlayWidth(width) {
    if (!shouldUseVrTutorialMesh()) {
      return CONTROLLER_DEFAULT_WIDTH;
    }
    if (width && String(width).includes('px')) {
      return width;
    }
    return '64px';
  }

  function mouseOverlayInnerScale(state, scale) {
    if (state.controllerSizing) {
      return controllerDisplayScale(scale);
    }
    return scale;
  }

  function setOverlayInnerTransform(state, scale, rotateDeg) {
    const rotate = typeof rotateDeg === 'number' ? rotateDeg : 0;
    state.inner.style.transform = 'scale(' + mouseOverlayInnerScale(state, scale) + ') rotate(' + rotate + 'deg)';
  }

  function resolveClickSegmentControllerTargetOffsetT(state, phases, travelT, usesTravelPhase) {
    if (!state.controllerSizing || typeof state.controllerTravelRotateDeg !== 'number') {
      return 0;
    }
    if (typeof state.controllerTravelTargetOffsetLeft !== 'number'
      && typeof state.controllerTravelTargetOffsetTop !== 'number') {
      return 0;
    }
    if (phases.returnT > 0) {
      return lerp(1, 0, smoothSegmentEase(phases.returnT));
    }
    if (usesTravelPhase) {
      return travelT;
    }
    return 1;
  }

  function applyClickSegmentControllerTargetOffset(state, left, top, offsetT) {
    const offsetLeft = typeof state.controllerTravelTargetOffsetLeft === 'number'
      ? state.controllerTravelTargetOffsetLeft * offsetT
      : 0;
    const offsetTop = typeof state.controllerTravelTargetOffsetTop === 'number'
      ? state.controllerTravelTargetOffsetTop * offsetT
      : 0;
    return {
      left: left + offsetLeft,
      top: top + offsetTop
    };
  }

  function resolveVrZoomScrollIcon(local) {
    return local < 0.30 ? CONTROLLER_RIGHT_ZOOMIN_ICON : CONTROLLER_RIGHT_ZOOMOUT_ICON;
  }

  function setVrZoomScrollIcon(state, local) {
    if (!state.controllerSizing || !state.scrollIcon) {
      return;
    }
    const icon = resolveVrZoomScrollIcon(local);
    if (state.scrollIcon.getAttribute('src') !== icon) {
      state.scrollIcon.src = icon;
    }
  }

  function resolveScrollIconLocal(segment, local) {
    if (segment.zoom === 'out') {
      return 0.35 + easeInOut(local) * 0.4;
    }
    if (segment.zoom === 'in') {
      return easeInOut(local) * 0.25;
    }
    return local;
  }

  function createMouseOverlayElement(width, options) {
    const useControllerIcons = options && options.useControllerIcons === true
      ? true
      : isVrTutorialDevice();
    const useHandIcons = options && options.useMouseIcons
      ? false
      : !useControllerIcons && isTouchTutorialDevice();
    const wrapper = document.createElement('div');
    let wrapperClass = 'tutorial-mouse-overlay';
    if (useControllerIcons) {
      wrapperClass += ' tutorial-controller-mouse-overlay';
    } else if (useHandIcons) {
      wrapperClass += ' tutorial-hand-overlay';
    }
    wrapper.className = wrapperClass;
    if (useControllerIcons) {
      wrapper.style.width = resolveControllerOverlayWidth(width);
    } else {
      wrapper.style.setProperty('--mouse-width', width);
    }

    const inner = document.createElement('div');
    inner.className = 'tutorial-mouse-inner';

    const glow = document.createElement('div');
    glow.className = 'tutorial-mouse-glow';

    const pressLine = document.createElement('div');
    pressLine.className = 'tutorial-controller-press-line';

    const moveIcon = document.createElement('img');
    moveIcon.className = 'tutorial-mouse-icon tutorial-mouse-move';
    moveIcon.src = useControllerIcons
      ? CONTROLLER_RIGHT_ICON
      : (useHandIcons ? HAND_MOVE_ICON : MOUSE_MOVE_ICON);
    moveIcon.alt = '';

    const lmbIcon = document.createElement('img');
    lmbIcon.className = 'tutorial-mouse-icon tutorial-mouse-lmb';
    lmbIcon.src = useControllerIcons
      ? CONTROLLER_RIGHT_PRESSED_ICON
      : (useHandIcons ? HAND_TAP_ICON : MOUSE_LMB_ICON);
    lmbIcon.alt = '';

    const rmbIcon = document.createElement('img');
    rmbIcon.className = 'tutorial-mouse-icon tutorial-mouse-rmb';
    rmbIcon.src = useControllerIcons
      ? CONTROLLER_RIGHT_PRESSED_ICON
      : (useHandIcons ? HAND_TAP_ICON : MOUSE_RMB_ICON);
    rmbIcon.alt = '';

    const scrollIcon = document.createElement('img');
    scrollIcon.className = 'tutorial-mouse-icon tutorial-mouse-scroll';
    scrollIcon.src = useControllerIcons
      ? CONTROLLER_RIGHT_ZOOMIN_ICON
      : (useHandIcons ? HAND_SCROLL_ICON : MOUSE_SCROLL_ICON);
    scrollIcon.alt = '';

    inner.appendChild(glow);
    inner.appendChild(moveIcon);
    inner.appendChild(lmbIcon);
    inner.appendChild(rmbIcon);
    inner.appendChild(scrollIcon);
    if (useControllerIcons) {
      inner.insertBefore(pressLine, glow);
    }
    wrapper.appendChild(inner);

    return {
      wrapper,
      inner,
      glow,
      moveIcon,
      lmbIcon,
      rmbIcon,
      scrollIcon,
      pressLine: useControllerIcons ? pressLine : null,
      controllerSizing: useControllerIcons,
      useHandIcons: useHandIcons
    };
  }

  function createClickCursorElement(width) {
    const wrapper = document.createElement('div');
    wrapper.className = 'tutorial-click-cursor';
    wrapper.style.width = width;
    const icon = document.createElement('img');
    icon.src = CLICK_CURSOR_ICON;
    icon.alt = '';
    wrapper.appendChild(icon);
    return { wrapper, icon };
  }

  function shouldShowClickCursor() {
    return !isVrTutorialDevice() && !isTouchTutorialDevice();
  }

  function shouldShowLmbClickCursor() {
    return !isVrTutorialDevice() && !isTouchTutorialDevice();
  }

  function updateClickCursorVisuals(state, left, top, scale, opacity) {
    if (!state.clickCursorWrapper) {
      return;
    }
    state.clickCursorWrapper.style.left = left + '%';
    state.clickCursorWrapper.style.top = top + '%';
    state.clickCursorWrapper.style.transform = 'scale(' + scale + ')';
    if (opacity <= 0.01) {
      state.clickCursorWrapper.style.visibility = 'hidden';
      state.clickCursorWrapper.style.opacity = '0';
      return;
    }
    state.clickCursorWrapper.style.visibility = 'visible';
    state.clickCursorWrapper.style.opacity = String(opacity);
  }

  function getLmbClickEarlySec(state, duration) {
    if (state.clickEarly == null || !Number.isFinite(duration) || duration <= 0) {
      return 0;
    }
    return state.clickEarly * duration;
  }

  function getLmbPointerArriveEarly(state) {
    if (state.pointerArriveEarly != null) {
      return state.pointerArriveEarly;
    }
    if (state.handArriveEarly != null) {
      return state.handArriveEarly;
    }
    return null;
  }

  function getLmbCycleTimes(state, duration) {
    const cycleStartSec = state.videoStart * duration;
    const cycleLenSec = (state.videoEnd - state.videoStart) * duration;
    const clickLeadSec = typeof state.clickLeadSec === 'number' ? state.clickLeadSec : 2;
    const clickEarlySec = getLmbClickEarlySec(state, duration);
    return {
      cycleStartSec: cycleStartSec,
      cycleLenSec: cycleLenSec,
      cycleEndSec: state.videoEnd * duration,
      clickLeadSec: clickLeadSec,
      pressStartSec: cycleStartSec + cycleLenSec * 0.38 - clickLeadSec - clickEarlySec,
      pressEndSec: cycleStartSec + cycleLenSec * 0.48 - clickLeadSec - clickEarlySec,
      holdEndSec: cycleStartSec + cycleLenSec * 0.61 - clickLeadSec - clickEarlySec,
      releaseEndSec: cycleStartSec + cycleLenSec * 0.74 - clickLeadSec - clickEarlySec
    };
  }

  function resolveLmbMouseClickVisuals(state, videoTime, duration) {
    const defaults = { moveOpacity: 1, lmbOpacity: 0, glowOpacity: 0, scale: 1, lmbShadow: false };
    if (!Number.isFinite(duration) || duration <= 0) {
      return defaults;
    }
    const times = getLmbCycleTimes(state, duration);
    if (videoTime < times.pressStartSec || videoTime >= times.releaseEndSec) {
      return defaults;
    }
    if (videoTime < times.pressEndSec) {
      const t = easeInOut((videoTime - times.pressStartSec) / Math.max(0.001, times.pressEndSec - times.pressStartSec));
      return {
        moveOpacity: 1 - t,
        lmbOpacity: t,
        glowOpacity: t * 0.7,
        scale: 1 - 0.04 * t,
        lmbShadow: t > 0.3
      };
    }
    if (videoTime < times.holdEndSec) {
      return { moveOpacity: 0, lmbOpacity: 1, glowOpacity: 0.7, scale: 0.96, lmbShadow: true };
    }
    const t = easeInOut((videoTime - times.holdEndSec) / Math.max(0.001, times.releaseEndSec - times.holdEndSec));
    const glowOpacity = 0.7 * (1 - t);
    return {
      moveOpacity: t,
      lmbOpacity: 1 - t,
      glowOpacity: glowOpacity,
      scale: 0.96 + 0.04 * t,
      lmbShadow: glowOpacity > 0.2
    };
  }

  function getLmbPointerPresenceFadeSec(state) {
    if (state && typeof state.pointerPresenceFadeSec === 'number') {
      return Math.max(0.1, state.pointerPresenceFadeSec);
    }
    return 0.4;
  }

  function getLmbPointerApproachLeadSec(state) {
    if (state && typeof state.pointerApproachLeadSec === 'number') {
      return Math.max(0, state.pointerApproachLeadSec);
    }
    if (state && typeof state.cursorLeadSec === 'number') {
      return Math.max(0, state.cursorLeadSec);
    }
    return 0;
  }

  function getLmbPointerTravelDurationSec(cycleLenSec, state) {
    if (state && typeof state.pointerTravelSec === 'number') {
      return Math.max(0.001, state.pointerTravelSec);
    }
    if (!Number.isFinite(cycleLenSec) || cycleLenSec <= 0) {
      return 0.35;
    }
    return Math.max(0.001, Math.min(0.35, cycleLenSec * 0.4));
  }

  function getLmbPointerTravelTimes(state, duration) {
    const cycleStartSec = state.videoStart * duration;
    const cycleLenSec = (state.videoEnd - state.videoStart) * duration;
    const cycleEndSec = state.videoEnd * duration;
    const leadSec = typeof state.cursorLeadSec === 'number' ? state.cursorLeadSec : 2.5;
    const holdSec = typeof state.cursorHoldSec === 'number' ? state.cursorHoldSec : 2;
    const travelSec = getLmbPointerTravelDurationSec(cycleLenSec, state);
    const travelStartSec = cycleStartSec + cycleLenSec * 0.03 - leadSec;
    const travelEndSec = travelStartSec + travelSec;
    const holdEndSec = travelEndSec + holdSec;
    const returnStartSec = cycleStartSec + cycleLenSec * 0.74;
    return {
      travelStartSec: travelStartSec,
      travelEndSec: travelEndSec,
      holdEndSec: holdEndSec,
      returnStartSec: returnStartSec,
      returnEndSec: cycleEndSec
    };
  }

  function getLmbCursorTravelEarly(state) {
    if (state.cursorTravelEarly != null) {
      return state.cursorTravelEarly;
    }
    if (state.pointerTravelEarly != null) {
      return state.pointerTravelEarly;
    }
    return null;
  }

  function getLmbCursorApproachTimes(state, duration, options) {
    const forceFullApproachWindow = Boolean(options && options.forceFullApproachWindow);
    const base = getLmbPointerTravelTimes(state, duration);
    const cycleLenSec = Number.isFinite(duration) && duration > 0
      ? (state.videoEnd - state.videoStart) * duration
      : 0;
    const travelSec = getLmbPointerTravelDurationSec(cycleLenSec, state);

    if (!Number.isFinite(duration) || duration <= 0) {
      return {
        approachStartSec: base.travelStartSec,
        travelStartSec: base.travelStartSec,
        approachEndSec: base.travelEndSec,
        travelSec: travelSec,
        returnStartSec: base.returnStartSec,
        returnEndSec: base.returnEndSec
      };
    }

    const clickTimes = getLmbCycleTimes(state, duration);
    const travelEarly = getLmbCursorTravelEarly(state);
    const travelEarlySec = travelEarly != null ? travelEarly * cycleLenSec : 0;
    const arriveEarly = getLmbPointerArriveEarly(state);
    const arriveEarlySec = arriveEarly != null ? arriveEarly * cycleLenSec : 0;
    const approachEndSec = clickTimes.pressStartSec - arriveEarlySec;

    if (arriveEarly != null) {
      const cycleStartSec = state.videoStart * duration;
      const pressStartSec = clickTimes.pressStartSec;
      const configuredTravelSec = travelSec;
      const approachLeadSec = getLmbPointerApproachLeadSec(state);
      const presenceFadeSec = getLmbPointerPresenceFadeSec(state);
      const minHoldSec = 0.06;
      const approachStartSec = Math.max(0, cycleStartSec - approachLeadSec);
      const maxApproachEndSec = pressStartSec - minHoldSec;
      const earlyTravelStartSec = approachStartSec + presenceFadeSec;
      const lateTravelStartSec = maxApproachEndSec - configuredTravelSec;
      const useFullApproachWindow = forceFullApproachWindow
        || Boolean(state.clickCursorWrapper)
        || (
          typeof state.mouseTravelRange === 'number' && state.mouseTravelRange === 0
        ) || touchHandUsesLmbTravel(state) || vrControllerUsesLmbTravel(state)
        || vrPagePointerUsesLmbTravel(state);
      let travelStartSec;
      let approachEndSec;
      if (useFullApproachWindow) {
        travelStartSec = earlyTravelStartSec;
        approachEndSec = Math.min(travelStartSec + configuredTravelSec, maxApproachEndSec);
      } else {
        travelStartSec = Math.max(
          earlyTravelStartSec,
          lateTravelStartSec
        );
        travelStartSec = Math.max(approachStartSec, travelStartSec);
        approachEndSec = Math.min(travelStartSec + configuredTravelSec, maxApproachEndSec);
        if (approachEndSec <= travelStartSec) {
          travelStartSec = Math.max(approachStartSec, lateTravelStartSec);
          approachEndSec = Math.min(travelStartSec + configuredTravelSec, maxApproachEndSec);
        }
      }
      const actualTravelSec = Math.max(0.001, approachEndSec - travelStartSec);
      return {
        approachStartSec: approachStartSec,
        travelStartSec: travelStartSec,
        approachEndSec: approachEndSec,
        travelSec: actualTravelSec,
        presenceFadeSec: presenceFadeSec,
        returnStartSec: base.returnStartSec,
        returnEndSec: base.returnEndSec
      };
    }

    const approachStartSec = Math.min(
      base.travelStartSec - travelEarlySec,
      approachEndSec - travelSec
    );
    const resolvedStartSec = Math.max(0, approachStartSec);
    return {
      approachStartSec: resolvedStartSec,
      travelStartSec: resolvedStartSec,
      approachEndSec: approachEndSec,
      travelSec: Math.max(0.001, approachEndSec - resolvedStartSec),
      returnStartSec: base.returnStartSec,
      returnEndSec: base.returnEndSec
    };
  }

  function lmbCycleUsesApproachPointer(state) {
    if (!state || state.type !== 'mouse-lmb-cycle') {
      return false;
    }
    if (getLmbPointerArriveEarly(state) == null) {
      return false;
    }
    if (state.useHandIcons && isTouchTutorialDevice()) {
      return true;
    }
    if (state.controllerSizing) {
      return true;
    }
    return !state.useHandIcons && !state.controllerSizing;
  }

  function getLmbPointerTravelEndSec(state, duration) {
    if (!lmbCycleUsesApproachPointer(state)) {
      return getLmbPointerTravelTimes(state, duration).travelEndSec;
    }
    const arriveEarly = getLmbPointerArriveEarly(state);
    if (arriveEarly == null || !Number.isFinite(duration) || duration <= 0) {
      return getLmbPointerTravelTimes(state, duration).travelEndSec;
    }
    return getLmbCursorApproachTimes(state, duration).approachEndSec;
  }

  function lmbPointerUsesTravel(state) {
    return lmbCycleUsesApproachPointer(state);
  }

  function lmbMouseUsesTravel(state) {
    if (!lmbPointerUsesTravel(state)) {
      return false;
    }
    if (touchHandUsesLmbTravel(state)) {
      return true;
    }
    if (vrControllerUsesLmbTravel(state)) {
      return true;
    }
    if (vrPagePointerUsesLmbTravel(state)) {
      return true;
    }
    if (typeof state.mouseTravelRange === 'number') {
      return state.mouseTravelRange > 0;
    }
    if (typeof state.mouseToLeft === 'number' && typeof state.mouseToTop === 'number') {
      return state.mouseToLeft !== state.fromLeft || state.mouseToTop !== state.fromTop;
    }
    return true;
  }

  function getLmbPointerTargetPosition(state) {
    if (state.useHandIcons) {
      if (touchHandUsesLmbTravel(state)) {
        return { left: state.cursorToLeft, top: state.cursorToTop };
      }
      return { left: state.toLeft, top: state.toTop };
    }
    if (state.controllerSizing) {
      if (vrControllerUsesLmbTravel(state)) {
        return { left: state.cursorToLeft, top: state.cursorToTop };
      }
      return offsetControllerPosition(state, state.toLeft, state.toTop);
    }
    return { left: state.toLeft, top: state.toTop };
  }

  function getLmbMouseTargetPosition(state) {
    const clickTarget = getLmbPointerTargetPosition(state);
    if (touchHandUsesLmbTravel(state)) {
      return clickTarget;
    }
    if (vrControllerUsesLmbTravel(state)) {
      return clickTarget;
    }
    if (vrPagePointerUsesLmbTravel(state)) {
      return clickTarget;
    }
    if (typeof state.mouseToLeft === 'number' && typeof state.mouseToTop === 'number') {
      return { left: state.mouseToLeft, top: state.mouseToTop };
    }
    if (typeof state.mouseTravelRange === 'number') {
      const range = Math.max(0, Math.min(1, state.mouseTravelRange));
      return {
        left: lerp(state.fromLeft, clickTarget.left, range),
        top: lerp(state.fromTop, clickTarget.top, range)
      };
    }
    return clickTarget;
  }

  function resolveLmbPointerTravelT(state, videoTime, duration) {
    if (!lmbPointerUsesTravel(state) || !Number.isFinite(duration) || duration <= 0) {
      return 0;
    }
    const times = getLmbCursorApproachTimes(state, duration);
    const approachStartSec = times.approachStartSec;
    const travelStartSec = times.travelStartSec;
    const approachEndSec = times.approachEndSec;
    const moveSec = times.travelSec;
    if (videoTime < approachStartSec) {
      return 0;
    }
    if (videoTime < travelStartSec) {
      return 0;
    }
    if (videoTime < approachEndSec) {
      return easeInOut((videoTime - travelStartSec) / Math.max(0.001, moveSec));
    }
    if (videoTime < times.returnStartSec) {
      return 1;
    }
    if (videoTime < times.returnEndSec) {
      return 1 - easeInOut((videoTime - times.returnStartSec) / Math.max(0.001, times.returnEndSec - times.returnStartSec));
    }
    return 0;
  }

  function resolveLmbHandTravelT(state, videoTime, duration) {
    return resolveLmbPointerTravelT(state, videoTime, duration);
  }

  function mouseOverlayHomeLeft(state) {
    if (state.controllerSizing && typeof state.controllerLeft === 'number') {
      return state.controllerLeft;
    }
    return state.fromLeft;
  }

  function mouseOverlayHomeTop(state) {
    if (state.controllerSizing && typeof state.controllerTop === 'number') {
      return state.controllerTop;
    }
    return state.fromTop;
  }

  function setMouseOverlayPosition(state, left, top) {
    state.wrapper.style.left = left + '%';
    state.wrapper.style.top = top + '%';
    const svgRoot = getOverlaySvgEl();
    if (svgRoot) {
      svgRoot.style.setProperty('--mouse-from-left', left + '%');
      svgRoot.style.setProperty('--mouse-from-top', top + '%');
    }
  }

  function resolveTouchHandLmbTargetLeft(cursorStopLeft) {
    if (isVrTutorialPage()) {
      return cursorStopLeft;
    }
    return MOBILE_SITE_LAYOUT_HAND_TARGET.left;
  }

  function resolveTouchHandLmbTargetTop(cursorStopTop) {
    if (isVrTutorialPage()) {
      return cursorStopTop;
    }
    return MOBILE_SITE_LAYOUT_HAND_TARGET.top;
  }

  function applyMouseHomeVisuals(state, clickVisuals, videoTime, duration) {
    const travelT = resolveLmbPointerTravelT(state, videoTime, duration);
    let left;
    let top;
    if (touchHandUsesLmbTravel(state)) {
      left = lerp(state.fromLeft, state.cursorToLeft, travelT);
      top = lerp(state.fromTop, state.cursorToTop, travelT);
    } else if (vrControllerUsesLmbTravel(state)) {
      left = lerp(state.fromLeft, state.cursorToLeft, travelT);
      top = lerp(state.fromTop, state.cursorToTop, travelT);
    } else if (vrPagePointerUsesLmbTravel(state)) {
      left = lerp(state.fromLeft, state.cursorToLeft, travelT);
      top = lerp(state.fromTop, state.cursorToTop, travelT);
    } else if (lmbMouseUsesTravel(state)) {
      const target = getLmbMouseTargetPosition(state);
      left = lerp(state.fromLeft, target.left, travelT);
      top = lerp(state.fromTop, target.top, travelT);
    } else {
      left = mouseOverlayHomeLeft(state);
      top = mouseOverlayHomeTop(state);
    }
    const presenceOpacity = resolveLmbPointerPresenceOpacity(state, videoTime, duration);
    state.wrapper.style.visibility = presenceOpacity > 0.01 ? 'visible' : 'hidden';
    state.wrapper.style.opacity = String(presenceOpacity);
    setMouseOverlayPosition(state, left, top);
    state.inner.style.transform = 'scale(' + mouseOverlayInnerScale(state, clickVisuals.scale) + ')';
    state.moveIcon.style.opacity = String(clickVisuals.moveOpacity);
    state.lmbIcon.style.opacity = String(clickVisuals.lmbOpacity);
    state.rmbIcon.style.opacity = '0';
    state.lmbIcon.style.filter = clickVisuals.lmbShadow
      ? 'drop-shadow(0 0 8px rgba(54, 54, 227, 0.8))'
      : 'none';
    state.rmbIcon.style.filter = 'none';
    if (state.scrollIcon) {
      state.scrollIcon.style.opacity = '0';
      state.scrollIcon.style.filter = 'none';
    }
    if (state.pressLine) {
      state.pressLine.style.opacity = String(clickVisuals.lmbOpacity);
    }
    state.glow.style.opacity = String(clickVisuals.glowOpacity * presenceOpacity);
    state.glow.style.transform = 'translate(-50%, -50%) scale(' + (0.8 + clickVisuals.glowOpacity * 0.4) + ')';
  }

  function resolveLmbCursorState(state, videoTime, duration) {
    if (!Number.isFinite(duration) || duration <= 0 || !state.clickCursorWrapper) {
      return { active: false, posT: 0, opacity: 0, scale: 1 };
    }

    const times = getLmbCursorApproachTimes(state, duration, { forceFullApproachWindow: true });
    const approachStartSec = times.approachStartSec;
    const travelStartSec = times.travelStartSec;
    const approachEndSec = times.approachEndSec;
    const moveSec = times.travelSec;
    const returnStartSec = times.returnStartSec;
    const returnEndSec = times.returnEndSec;
    const fadeInSec = times.presenceFadeSec != null
      ? times.presenceFadeSec
      : getLmbPointerPresenceFadeSec(state);
    const fadeOutStartSec = state.videoStart * duration + (state.videoEnd - state.videoStart) * duration * 0.88;

    if (videoTime < approachStartSec || videoTime > returnEndSec) {
      return { active: false, posT: 0, opacity: 0, scale: 1 };
    }

    let posT = 0;
    let opacity = 1;
    let scale = 1;

    if (videoTime < approachEndSec) {
      if (videoTime < travelStartSec) {
        posT = 0;
        opacity = easeInOut(Math.min(1, (videoTime - approachStartSec) / Math.max(0.001, fadeInSec)));
      } else {
        posT = easeInOut((videoTime - travelStartSec) / Math.max(0.001, moveSec));
        opacity = 1;
      }
    } else if (videoTime < returnStartSec) {
      posT = 1;
      opacity = 1;
    } else if (videoTime < returnEndSec) {
      const t = easeInOut((videoTime - returnStartSec) / Math.max(0.001, returnEndSec - returnStartSec));
      posT = 1 - t;
      opacity = videoTime > fadeOutStartSec
        ? easeInOut((returnEndSec - videoTime) / Math.max(0.001, returnEndSec - fadeOutStartSec))
        : 1;
    }

    const clickTimes = getLmbCycleTimes(state, duration);
    if (videoTime >= clickTimes.pressStartSec && videoTime < clickTimes.holdEndSec && posT >= 0.99) {
      scale = 0.96;
    }

    return { active: true, posT: posT, opacity: opacity, scale: scale };
  }

  function showMouseAtHomeIdle(state) {
    state.wrapper.style.visibility = 'visible';
    state.wrapper.style.opacity = '1';
    state.wrapper.style.left = mouseOverlayHomeLeft(state) + '%';
    state.wrapper.style.top = mouseOverlayHomeTop(state) + '%';
    state.inner.style.transform = 'scale(' + mouseOverlayInnerScale(state, 1) + ')';
    state.moveIcon.style.opacity = '1';
    state.lmbIcon.style.opacity = '0';
    state.rmbIcon.style.opacity = '0';
    state.lmbIcon.style.filter = 'none';
    state.rmbIcon.style.filter = 'none';
    if (state.scrollIcon) {
      state.scrollIcon.style.opacity = '0';
      state.scrollIcon.style.filter = 'none';
    }
    state.glow.style.opacity = '0';
    state.glow.style.transform = 'translate(-50%, -50%) scale(0.8)';
  }

  function updateLmbCursorVisuals(state, cursorState) {
    if (!state.clickCursorWrapper || !cursorState.active) {
      updateClickCursorVisuals(state, state.cursorFromLeft, state.cursorFromTop, 1, 0);
      return;
    }
    const cursorLeft = lerp(state.cursorFromLeft, state.cursorToLeft, cursorState.posT);
    const cursorTop = lerp(state.cursorFromTop, state.cursorToTop, cursorState.posT);
    updateClickCursorVisuals(state, cursorLeft, cursorTop, cursorState.scale, cursorState.opacity);
  }

  function createControllerOverlayElement(width) {
    const wrapper = document.createElement('div');
    wrapper.className = 'tutorial-controller-overlay';
    wrapper.style.width = width;

    const inner = document.createElement('div');
    inner.className = 'tutorial-controller-inner';

    const glow = document.createElement('div');
    glow.className = 'tutorial-controller-glow';

    const pressLine = document.createElement('div');
    pressLine.className = 'tutorial-controller-press-line';

    const icon = document.createElement('img');
    icon.className = 'tutorial-controller-icon tutorial-controller-default';
    icon.src = CONTROLLER_RIGHT_ICON;
    icon.alt = '';

    const pressedIcon = document.createElement('img');
    pressedIcon.className = 'tutorial-controller-icon tutorial-controller-pressed';
    pressedIcon.src = CONTROLLER_RIGHT_PRESSED_ICON;
    pressedIcon.alt = '';

    const leftIcon = document.createElement('img');
    leftIcon.className = 'tutorial-controller-icon tutorial-controller-left';
    leftIcon.src = CONTROLLER_LEFT_ICON;
    leftIcon.alt = '';

    const leftPressedIcon = document.createElement('img');
    leftPressedIcon.className = 'tutorial-controller-icon tutorial-controller-left-pressed';
    leftPressedIcon.src = CONTROLLER_LEFT_PRESSED_ICON;
    leftPressedIcon.alt = '';

    inner.appendChild(glow);
    inner.appendChild(icon);
    inner.appendChild(pressedIcon);
    inner.appendChild(leftIcon);
    inner.appendChild(leftPressedIcon);
    inner.insertBefore(pressLine, glow);
    wrapper.appendChild(inner);

    return { wrapper, inner, glow, pressLine, icon, pressedIcon, leftIcon, leftPressedIcon };
  }

  function hideMouseOverlay(state) {
    state.wrapper.style.opacity = '0';
    state.wrapper.style.visibility = 'hidden';
    if (state.pressLine) {
      state.pressLine.style.opacity = '0';
    }
  }

  function applyMouseIdleVisuals(state, presenceOpacity) {
    state.moveIcon.style.opacity = String(presenceOpacity);
    state.lmbIcon.style.opacity = '0';
    state.rmbIcon.style.opacity = '0';
    state.lmbIcon.style.filter = 'none';
    state.rmbIcon.style.filter = 'none';
    if (state.scrollIcon) {
      state.scrollIcon.style.opacity = '0';
      state.scrollIcon.style.filter = 'none';
    }
    if (state.pressLine) {
      state.pressLine.style.opacity = '0';
    }
    state.inner.style.transform = 'scale(' + mouseOverlayInnerScale(state, 1) + ')';
    state.glow.style.opacity = '0';
    state.glow.style.transform = 'translate(-50%, -50%) scale(0.8)';
  }

  function applyMouseMoveWithClickFeedback(state, button, pressOpacity) {
    state.moveIcon.style.opacity = '1';
    state.lmbIcon.style.opacity = button === 'lmb' ? String(pressOpacity) : '0';
    state.rmbIcon.style.opacity = button === 'rmb' ? String(pressOpacity) : '0';
    state.lmbIcon.style.filter = button === 'lmb' && pressOpacity > 0
      ? 'drop-shadow(0 0 8px rgba(54, 54, 227, 0.8))'
      : 'none';
    state.rmbIcon.style.filter = 'none';
    if (state.scrollIcon) {
      state.scrollIcon.style.opacity = '0';
      state.scrollIcon.style.filter = 'none';
    }
    if (state.pressLine) {
      state.pressLine.style.opacity = '0';
    }
    const glowOpacity = 0.7 * pressOpacity;
    state.inner.style.transform = 'scale(' + mouseOverlayInnerScale(state, 1 - 0.04 * pressOpacity) + ')';
    state.glow.style.opacity = String(glowOpacity);
    state.glow.style.transform = 'translate(-50%, -50%) scale(' + (0.8 + glowOpacity * 0.4) + ')';
  }

  function applyMouseMoveOnlyVisuals(state) {
    state.moveIcon.style.opacity = '1';
    state.lmbIcon.style.opacity = '0';
    state.rmbIcon.style.opacity = '0';
    state.lmbIcon.style.filter = 'none';
    state.rmbIcon.style.filter = 'none';
    if (state.scrollIcon) {
      state.scrollIcon.style.opacity = '0';
      state.scrollIcon.style.filter = 'none';
    }
    if (state.pressLine) {
      state.pressLine.style.opacity = '0';
    }
    state.inner.style.transform = 'scale(' + mouseOverlayInnerScale(state, 1) + ')';
    state.glow.style.opacity = '0';
    state.glow.style.transform = 'translate(-50%, -50%) scale(0.8)';
  }

  function applyPressedVisuals(state, button, opacity) {
    const glowOpacity = 0.7 * opacity;
    state.moveIcon.style.opacity = String(1 - opacity);
    state.lmbIcon.style.opacity = button === 'lmb' ? String(opacity) : '0';
    state.rmbIcon.style.opacity = button === 'rmb' ? String(opacity) : '0';
    if (state.scrollIcon) {
      state.scrollIcon.style.opacity = '0';
      state.scrollIcon.style.filter = 'none';
    }
    state.lmbIcon.style.filter = button === 'lmb' && opacity > 0
      ? 'drop-shadow(0 0 8px rgba(54, 54, 227, 0.8))'
      : 'none';
    state.rmbIcon.style.filter = button === 'rmb' && opacity > 0
      ? 'drop-shadow(0 0 8px rgba(54, 54, 227, 0.8))'
      : 'none';
    if (state.pressLine) {
      state.pressLine.style.opacity = button === 'lmb' || button === 'rmb' ? String(opacity) : '0';
    }
    state.inner.style.transform = 'scale(' + mouseOverlayInnerScale(state, 0.96 + 0.04 * (1 - opacity)) + ')';
    state.glow.style.opacity = String(glowOpacity);
    state.glow.style.transform = 'translate(-50%, -50%) scale(' + (0.8 + glowOpacity * 0.4) + ')';
  }

  function applyScrollVisuals(state, opacity) {
    state.moveIcon.style.opacity = '0';
    state.lmbIcon.style.opacity = '0';
    state.rmbIcon.style.opacity = '0';
    state.lmbIcon.style.filter = 'none';
    state.rmbIcon.style.filter = 'none';
    if (state.pressLine) {
      state.pressLine.style.opacity = '0';
    }
    if (state.scrollIcon) {
      state.scrollIcon.style.opacity = String(opacity);
      state.scrollIcon.style.filter = 'drop-shadow(0 0 8px rgba(54, 54, 227, 0.8))';
    }
    state.inner.style.transform = 'scale(' + mouseOverlayInnerScale(state, 1) + ')';
    const glowOpacity = 0.7 * opacity;
    state.glow.style.opacity = String(glowOpacity);
    state.glow.style.transform = 'translate(-50%, -50%) scale(' + (0.8 + glowOpacity * 0.4) + ')';
  }

  function isSegmentActive(segment, videoProgress) {
    if (videoProgress < segment.start) {
      return false;
    }
    if (segment.end >= 1) {
      return videoProgress <= 1;
    }
    return videoProgress < segment.end;
  }

  function deriveBulletHighlightsFromControllerSegments(segments) {
    if (segments.length > 1) {
      const first = segments[0];
      const repositionStart = segments[1].start;
      const repositionEnd = segments[segments.length - 1].end;
      const lead = 0.12;
      const secondBulletStart = Math.max(first.end, repositionStart - lead);

      return normalizeBulletHighlights([
        { index: 0, start: first.start, end: secondBulletStart },
        { index: 1, start: secondBulletStart, end: repositionEnd }
      ]);
    }

    return normalizeBulletHighlights(segments.map((segment, index) => ({
      index: index,
      start: segment.start,
      end: segment.end
    })));
  }

  function deriveBulletHighlightsFromDragSegments(segments) {
    const groups = isVrTutorialPage()
      ? [
        { button: 'lmb', index: 0 },
        { button: 'scroll', index: 1 },
        { button: 'rmb', index: 2 }
      ]
      : [
        { button: 'lmb', index: 0 },
        { button: 'rmb', index: 1 },
        { button: 'scroll', index: 2 }
      ];
    const highlights = [];

    for (let i = 0; i < groups.length; i++) {
      const group = groups[i];
      const matching = segments.filter((segment) => segment.button === group.button);
      if (!matching.length) {
        continue;
      }

      const start = Math.min(...matching.map((segment) => segment.start));
      let end = Math.max(...matching.map((segment) => segment.end));
      const nextGroup = groups[i + 1];

      if (nextGroup) {
        const nextSegments = segments.filter((segment) => segment.button === nextGroup.button);
        if (nextSegments.length) {
          end = Math.min(...nextSegments.map((segment) => segment.start));
        }
      }

      highlights.push({ index: group.index, start, end });
    }

    return highlights;
  }

  function resolveBulletHighlights(step) {
    const overlay = step.overlay;
    if (overlay && overlay.type === 'mouse-drag-segments' && Array.isArray(overlay.segments)) {
      if (step.bulletHighlights && step.bulletHighlights.length > 0) {
        return normalizeBulletHighlights(step.bulletHighlights);
      }
      return normalizeBulletHighlights(deriveBulletHighlightsFromDragSegments(overlay.segments));
    }
    if (overlay && overlay.type === 'controller-segments' && Array.isArray(overlay.segments)) {
      if (step.bulletHighlights && step.bulletHighlights.length > 0) {
        return normalizeBulletHighlights(step.bulletHighlights);
      }
      return deriveBulletHighlightsFromControllerSegments(overlay.segments);
    }
    return normalizeBulletHighlights(
      step.bulletHighlights && step.bulletHighlights.length > 0 ? step.bulletHighlights : null
    );
  }

  function resolveNoteHighlight(step) {
    const overlay = step.overlay;
    if (overlay && overlay.type === 'mouse-lmb-cycle') {
      const start = typeof overlay.videoStart === 'number' ? overlay.videoStart : null;
      const end = typeof overlay.videoEnd === 'number' ? overlay.videoEnd : null;
      if (start != null && end != null) {
        return { start, end };
      }
    }
    return step.noteHighlight || null;
  }

  function segmentEdgeOpacity(local, options) {
    const fadeIn = options && options.fadeIn != null ? options.fadeIn : 0.06;
    const fadeOut = options && options.fadeOut != null ? options.fadeOut : 0.06;
    const skipFadeOut = Boolean(options && options.skipFadeOut);
    const skipFadeIn = Boolean(options && options.skipFadeIn);
    if (local < fadeIn) {
      if (skipFadeIn) {
        return 1;
      }
      return easeInOut(local / fadeIn);
    }
    if (!skipFadeOut && local > 1 - fadeOut) {
      return easeInOut((1 - local) / fadeOut);
    }
    return 1;
  }

  const CLICK_SEGMENT_TRAVEL_RATIO = 0.45;
  const CLICK_SEGMENT_PRESS_FADE = { fadeIn: 0.2, fadeOut: 0.2 };
  const CLICK_SEGMENT_GROUP_FADE = 0.08;
  const CLICK_SEGMENT_RETURN_TRAVEL_RATIO = 0.35;
  const CLICK_SEGMENT_RETURN_OUT_RATIO = 0.3;

  function clickSegmentHasClick(segment) {
    return Boolean(segment && segment.noClick !== true);
  }

  function getClickSegmentTravelRatio(segment) {
    if (segment && segment.sharpTravel) {
      return 0.18;
    }
    if (segment && segment.fastTravel) {
      return 0.3;
    }
    if (segment && segment.noClick) {
      return 0.7;
    }
    return CLICK_SEGMENT_TRAVEL_RATIO;
  }

  function clickSegmentHasReturnHome(segment) {
    return Boolean(segment && segment.returnHome);
  }

  function resolveClickSegmentPhases(local, segment, state) {
    const hasReturn = clickSegmentHasReturnHome(segment);
    const usesTravel = hasReturn || clickSegmentUsesTravelPhase(state, segment);

    if (!hasReturn) {
      return {
        usesTravel: usesTravel,
        travelInT: usesTravel ? getClickSegmentTravelT(local, segment) : 1,
        returnT: 0,
        pressOpacity: getClickSegmentPressOpacity(local, usesTravel, segment)
      };
    }

    const clickRatio = 1 - CLICK_SEGMENT_RETURN_TRAVEL_RATIO - CLICK_SEGMENT_RETURN_OUT_RATIO;
    if (local < CLICK_SEGMENT_RETURN_TRAVEL_RATIO) {
      return {
        usesTravel: true,
        travelInT: smoothSegmentEase(local / CLICK_SEGMENT_RETURN_TRAVEL_RATIO),
        returnT: 0,
        pressOpacity: 0
      };
    }
    if (local < CLICK_SEGMENT_RETURN_TRAVEL_RATIO + clickRatio) {
      const clickLocal = (local - CLICK_SEGMENT_RETURN_TRAVEL_RATIO) / clickRatio;
      return {
        usesTravel: true,
        travelInT: 1,
        returnT: 0,
        pressOpacity: segmentEdgeOpacity(clickLocal, CLICK_SEGMENT_PRESS_FADE)
      };
    }
    const returnLocal = (local - CLICK_SEGMENT_RETURN_TRAVEL_RATIO - clickRatio) / CLICK_SEGMENT_RETURN_OUT_RATIO;
    return {
      usesTravel: true,
      travelInT: 1,
      returnT: smoothSegmentEase(Math.min(1, returnLocal)),
      pressOpacity: 0
    };
  }

  function resolveClickSegmentControllerRotateDeg(state, segment, phases) {
    if (!state.controllerSizing || typeof state.controllerTravelRotateDeg !== 'number') {
      return 0;
    }
    const endDeg = state.controllerTravelRotateDeg;
    if (phases.returnT > 0) {
      return lerp(endDeg, 0, smoothSegmentEase(phases.returnT));
    }
    if (clickSegmentHasReturnHome(segment)) {
      return lerp(0, endDeg, phases.travelInT);
    }
    return lerp(0, endDeg, phases.travelInT);
  }

  function clickSegmentCursorHomeLeft(state, segment) {
    if (segment && segment.returnLeft != null) {
      return parsePercentValue(segment.returnLeft, state.cursorFromLeft);
    }
    return state.cursorFromLeft;
  }

  function clickSegmentCursorHomeTop(state, segment) {
    if (segment && segment.returnTop != null) {
      return parsePercentValue(segment.returnTop, state.cursorFromTop);
    }
    return state.cursorFromTop;
  }

  function clickSegmentControllerHomeLeft(state, segment) {
    const fallback = state.controllerFromLeft != null
      ? parsePercentValue(state.controllerFromLeft, state.cursorFromLeft)
      : state.cursorFromLeft;
    if (segment && segment.returnLeft != null) {
      return parsePercentValue(segment.returnLeft, fallback);
    }
    return fallback;
  }

  function clickSegmentControllerHomeTop(state, segment) {
    const fallback = state.controllerFromTop != null
      ? parsePercentValue(state.controllerFromTop, state.cursorFromTop)
      : state.cursorFromTop;
    if (segment && segment.returnTop != null) {
      return parsePercentValue(segment.returnTop, fallback);
    }
    return fallback;
  }

  function getClickSegmentIndex(segments, segment) {
    return segments.indexOf(segment);
  }

  function clickSegmentPointerTravelsContinuously(state) {
    return Boolean(state.continuousCursor);
  }

  function clickSegmentGapUsesReturnHome(state, gap) {
    return Boolean(gap && (clickSegmentHasReturnHome(gap.prev) || clickSegmentHasReturnHome(gap.next)));
  }

  function resolveClickSegmentGapHomePosition(state, gap, homeLeftFn, homeTopFn) {
    const segment = clickSegmentHasReturnHome(gap.prev) ? gap.prev : gap.next;
    return {
      left: homeLeftFn(state, segment),
      top: homeTopFn(state, segment)
    };
  }

  function showClickSegmentGapHome(state, gap, presenceOpacity) {
    const cursorHome = resolveClickSegmentGapHomePosition(
      state,
      gap,
      clickSegmentCursorHomeLeft,
      clickSegmentCursorHomeTop
    );
    if (state.clickCursorWrapper) {
      updateClickCursorVisuals(state, cursorHome.left, cursorHome.top, 1, presenceOpacity);
    }
    if (state.useHandIcons || (state.alignPointerToTarget && state.controllerSizing)) {
      const pointerHome = state.alignPointerToTarget && state.controllerSizing
        ? resolveClickSegmentGapHomePosition(
          state,
          gap,
          clickSegmentControllerHomeLeft,
          clickSegmentControllerHomeTop
        )
        : resolveClickSegmentGapHomePosition(
          state,
          gap,
          clickSegmentCursorHomeLeft,
          clickSegmentCursorHomeTop
        );
      state.wrapper.style.visibility = 'visible';
      state.wrapper.style.opacity = '1';
      setClickSegmentPointerWrapperPosition(state, pointerHome.left, pointerHome.top);
      applyDragIconOpacities(state, { move: 1, lmb: 0, rmb: 0, scroll: 0 }, 1, undefined, 0);
    } else if (state.clickCursorWrapper) {
      applyClickSegmentMouseWrapper(state, presenceOpacity);
    }
  }

  function usesClickSegmentSmoothPointerIcons(state) {
    return Boolean(state.useHandIcons || (state.alignPointerToTarget && state.controllerSizing));
  }

  function offsetControllerPosition(state, left, top) {
    if (!state.controllerSizing) {
      return { left: left, top: top };
    }
    const offsetLeft = state.controllerOffsetLeft != null
      ? parsePercentValue(state.controllerOffsetLeft, 0)
      : 0;
    const offsetTop = state.controllerOffsetTop != null
      ? parsePercentValue(state.controllerOffsetTop, 0)
      : 0;
    return {
      left: left + offsetLeft,
      top: top + offsetTop
    };
  }

  function offsetClickSegmentControllerPosition(state, left, top) {
    if (!state.alignPointerToTarget || !state.controllerSizing) {
      return { left: left, top: top };
    }
    return offsetControllerPosition(state, left, top);
  }

  function setClickSegmentPointerWrapperPosition(state, left, top) {
    const pos = offsetClickSegmentControllerPosition(state, left, top);
    state.wrapper.style.left = pos.left + '%';
    state.wrapper.style.top = pos.top + '%';
  }

  function resolveClickSegmentTravelOrigin(state, segment, homeLeftFn, homeTopFn) {
    const resolveHomeLeft = homeLeftFn || clickSegmentCursorHomeLeft;
    const resolveHomeTop = homeTopFn || clickSegmentCursorHomeTop;
    const segments = state.segments;
    const index = getClickSegmentIndex(segments, segment);
    if (clickSegmentHasReturnHome(segment) || !clickSegmentPointerTravelsContinuously(state) || index <= 0) {
      return {
        left: resolveHomeLeft(state, segment),
        top: resolveHomeTop(state, segment)
      };
    }
    const prev = segments[index - 1];
    return {
      left: clickSegmentTargetLeft(prev),
      top: clickSegmentTargetTop(prev)
    };
  }

  function clickSegmentGapShouldHold(state, gap) {
    if (!gap || !clickSegmentPointerTravelsContinuously(state)) {
      return true;
    }
    if (gap.prev && gap.prev.noClick) {
      return true;
    }
    const fromLeft = clickSegmentTargetLeft(gap.prev);
    const fromTop = clickSegmentTargetTop(gap.prev);
    const toLeft = clickSegmentTargetLeft(gap.next);
    const toTop = clickSegmentTargetTop(gap.next);
    if (Math.abs(fromLeft - toLeft) <= 0.5 && Math.abs(fromTop - toTop) <= 0.5) {
      return true;
    }
    return false;
  }

  function clickSegmentGapWasLerping(state, segment, segments) {
    const index = getClickSegmentIndex(segments, segment);
    if (index <= 0) {
      return false;
    }
    const prev = segments[index - 1];
    if (!clickSegmentHasPrecedingGap(segment, segments)) {
      return false;
    }
    const gap = { prev: prev, next: segment, gapT: 1 };
    if (clickSegmentGapShouldHold(state, gap)) {
      return false;
    }
    const fromLeft = clickSegmentTargetLeft(prev);
    const toLeft = clickSegmentTargetLeft(segment);
    const fromTop = clickSegmentTargetTop(prev);
    const toTop = clickSegmentTargetTop(segment);
    return Math.abs(fromLeft - toLeft) > 0.5 || Math.abs(fromTop - toTop) > 0.5;
  }

  function resolveClickSegmentGapPosition(state, gap) {
    const fromLeft = clickSegmentTargetLeft(gap.prev);
    const fromTop = clickSegmentTargetTop(gap.prev);
    if (!clickSegmentPointerTravelsContinuously(state) || clickSegmentGapShouldHold(state, gap)) {
      return { left: fromLeft, top: fromTop };
    }
    const toLeft = clickSegmentTargetLeft(gap.next);
    const toTop = clickSegmentTargetTop(gap.next);
    const t = smoothSegmentEase(gap.gapT);
    return {
      left: lerp(fromLeft, toLeft, t),
      top: lerp(fromTop, toTop, t)
    };
  }

  function clickSegmentHasPrecedingGap(segment, segments) {
    const index = segments.indexOf(segment);
    if (index <= 0) {
      return false;
    }
    return segment.start > segments[index - 1].end;
  }

  function resolveClickSegmentEffectiveTravelT(state, segment, local, travelT) {
    if (!clickSegmentPointerTravelsContinuously(state) || (segment && (segment.sharpTravel || segment.fastTravel))) {
      return travelT;
    }
    if (!clickSegmentGapWasLerping(state, segment, state.segments)) {
      return travelT;
    }
    const origin = state.alignPointerToTarget && state.controllerSizing
      ? resolveClickSegmentTravelOrigin(
        state,
        segment,
        clickSegmentControllerHomeLeft,
        clickSegmentControllerHomeTop
      )
      : resolveClickSegmentTravelOrigin(state, segment);
    const targetLeft = clickSegmentTargetLeft(segment);
    const targetTop = clickSegmentTargetTop(segment);
    if (Math.abs(origin.left - targetLeft) <= 0.5 && Math.abs(origin.top - targetTop) <= 0.5) {
      return travelT;
    }
    const ratio = getClickSegmentTravelRatio(segment);
    if (local < ratio) {
      return 1;
    }
    return travelT;
  }

  function resolveClickSegmentPressFadeOptions(segment, hasNext) {
    return {
      fadeIn: CLICK_SEGMENT_PRESS_FADE.fadeIn,
      fadeOut: CLICK_SEGMENT_PRESS_FADE.fadeOut,
      skipFadeOut: true
    };
  }

  function resolveClickSegmentGapIcons(gap, videoProgress) {
    const span = Math.max(0.0001, gap.next.start - gap.prev.end);
    const gapLocal = (videoProgress - gap.prev.end) / span;
    const icons = { move: 1, lmb: 0, rmb: 0, scroll: 0 };

    if (clickSegmentHasReturnHome(gap.prev) || !clickSegmentHasClick(gap.prev) || gapLocal > 0.2) {
      return icons;
    }

    const releaseT = smoothSegmentEase(Math.min(1, gapLocal / 0.2));
    icons = { move: releaseT, lmb: 0, rmb: 0, scroll: 0 };
    if (gap.prev.button === 'lmb') {
      icons.lmb = 1 - releaseT;
    } else if (gap.prev.button === 'rmb') {
      icons.rmb = 1 - releaseT;
    } else if (gap.prev.button === 'scroll') {
      icons.scroll = 1 - releaseT;
    }
    return icons;
  }

  function resolveClickSegmentPointerPresenceOpacity(state, presenceOpacity, segment, local, usesTravelPhase) {
    if (!usesClickSegmentSmoothPointerIcons(state)) {
      return presenceOpacity;
    }
    if (!segment) {
      return presenceOpacity;
    }
    const hasPrev = hasPrecedingSegment(state.segments, segment);
    const hasNext = hasFollowingSegment(state.segments, segment);
    if (hasPrev || hasNext || usesTravelPhase || !clickSegmentHasClick(segment)) {
      return 1;
    }
    return presenceOpacity;
  }

  function buildClickSegmentPointerPressIcons(segment, pressOpacity) {
    const press = Math.max(0, Math.min(1, pressOpacity));
    const icons = {
      move: Math.max(0, 1 - press),
      lmb: 0,
      rmb: 0,
      scroll: 0
    };
    if (segment.button === 'lmb') {
      icons.lmb = press;
    } else if (segment.button === 'rmb') {
      icons.rmb = press;
    } else if (segment.button === 'scroll') {
      icons.scroll = press;
    }
    return icons;
  }

  function applyClickSegmentPointerVisuals(state, segment, local, usesTravelPhase, presenceOpacity, rotateDeg) {
    if (!clickSegmentHasClick(segment)) {
      applyDragIconOpacities(state, { move: 1, lmb: 0, rmb: 0, scroll: 0 }, presenceOpacity, undefined, rotateDeg);
      return;
    }

    if (clickSegmentHasReturnHome(segment)) {
      const phases = resolveClickSegmentPhases(local, segment, state);
      if (phases.pressOpacity > 0) {
        applyDragIconOpacities(
          state,
          buildClickSegmentPointerPressIcons(segment, phases.pressOpacity),
          presenceOpacity,
          undefined,
          rotateDeg
        );
        return;
      }
      applyDragIconOpacities(state, { move: 1, lmb: 0, rmb: 0, scroll: 0 }, presenceOpacity, undefined, rotateDeg);
      return;
    }

    const travelRatio = getClickSegmentTravelRatio(segment);
    const hasNext = hasFollowingSegment(state.segments, segment);
    const hasPrev = hasPrecedingSegment(state.segments, segment);
    let icons;

    if (usesTravelPhase && local < travelRatio) {
      icons = { move: 1, lmb: 0, rmb: 0, scroll: 0 };
      const prev = hasPrev ? state.segments[getClickSegmentIndex(state.segments, segment) - 1] : null;
      if (prev
        && local < 0.12
        && clickSegmentHasPrecedingGap(segment, state.segments)
        && clickSegmentHasClick(prev)
        && !clickSegmentHasReturnHome(prev)) {
        const releaseT = smoothSegmentEase(local / 0.12);
        icons = { move: releaseT, lmb: 0, rmb: 0, scroll: 0 };
        if (prev.button === 'lmb') {
          icons.lmb = 1 - releaseT;
        } else if (prev.button === 'rmb') {
          icons.rmb = 1 - releaseT;
        }
      }
      applyDragIconOpacities(state, icons, presenceOpacity, undefined, rotateDeg);
      return;
    }

    if (usesTravelPhase) {
      const clickLocal = resolveClickSegmentClickLocal(local, usesTravelPhase, segment);
      if (clickLocal == null) {
        applyDragIconOpacities(state, { move: 1, lmb: 0, rmb: 0, scroll: 0 }, presenceOpacity, undefined, rotateDeg);
        return;
      }
      const pressFade = resolveClickSegmentPressFadeOptions(segment, hasNext);
      icons = buildClickSegmentPointerPressIcons(
        segment,
        segmentEdgeOpacity(clickLocal, pressFade)
      );
      applyDragIconOpacities(state, icons, presenceOpacity, undefined, rotateDeg);
      return;
    }

    const clickLocal = resolveClickSegmentClickLocal(local, usesTravelPhase, segment);
    if (clickLocal == null) {
      applyDragIconOpacities(state, { move: 1, lmb: 0, rmb: 0, scroll: 0 }, presenceOpacity, undefined, rotateDeg);
      return;
    }
    icons = buildClickSegmentPointerPressIcons(
      segment,
      segmentEdgeOpacity(clickLocal, resolveClickSegmentPressFadeOptions(segment, hasNext))
    );
    applyDragIconOpacities(state, icons, presenceOpacity, undefined, rotateDeg);
  }

  function findClickSegmentGap(segments, videoProgress) {
    for (let i = 0; i < segments.length - 1; i++) {
      const prev = segments[i];
      const next = segments[i + 1];
      if (videoProgress >= prev.end && videoProgress < next.start) {
        const span = Math.max(0.0001, next.start - prev.end);
        return {
          prev: prev,
          next: next,
          gapT: (videoProgress - prev.end) / span
        };
      }
    }
    return null;
  }

  function resolveClickSegmentGroupOpacity(state, videoProgress, groupStart, groupEnd, fade, lastSegment) {
    const groupFadeIn = videoProgress < groupStart + fade
      ? smoothSegmentEase((videoProgress - groupStart) / fade)
      : 1;
    const groupFadeOut = clickSegmentHasReturnHome(lastSegment) && videoProgress > groupEnd
      ? 1
      : videoProgress > groupEnd
        ? smoothSegmentEase((groupEnd + fade - videoProgress) / fade)
        : 1;
    return Math.max(0, Math.min(1, groupFadeIn, groupFadeOut));
  }

  function applyClickSegmentMouseWrapper(state, presenceOpacity) {
    const mouseLeft = parsePercentValue(state.mouseLeft, 88);
    const mouseTop = parsePercentValue(state.mouseTop, 68);
    state.wrapper.style.visibility = 'visible';
    state.wrapper.style.opacity = String(presenceOpacity);
    state.wrapper.style.left = mouseLeft + '%';
    state.wrapper.style.top = mouseTop + '%';
    if (state.keepMouseVisible && state.clickCursorWrapper) {
      applyMouseMoveOnlyVisuals(state);
    }
  }

  function clickSegmentTargetLeft(segment) {
    return parsePercentValue(segment.left, 50);
  }

  function clickSegmentTargetTop(segment) {
    return parsePercentValue(segment.top, 50);
  }

  function clickSegmentUsesTravelPhase(state, segment) {
    const targetLeft = clickSegmentTargetLeft(segment);
    const targetTop = clickSegmentTargetTop(segment);
    if (state.clickCursorWrapper) {
      const origin = resolveClickSegmentTravelOrigin(
        state,
        segment,
        clickSegmentCursorHomeLeft,
        clickSegmentCursorHomeTop
      );
      if (Math.abs(origin.left - targetLeft) > 0.5 || Math.abs(origin.top - targetTop) > 0.5) {
        return true;
      }
    }
    if (state.useHandIcons) {
      const origin = resolveClickSegmentTravelOrigin(
        state,
        segment,
        clickSegmentCursorHomeLeft,
        clickSegmentCursorHomeTop
      );
      if (Math.abs(origin.left - targetLeft) > 0.5 || Math.abs(origin.top - targetTop) > 0.5) {
        return true;
      }
    }
    if (state.alignPointerToTarget && state.controllerSizing) {
      const origin = resolveClickSegmentTravelOrigin(
        state,
        segment,
        clickSegmentControllerHomeLeft,
        clickSegmentControllerHomeTop
      );
      if (Math.abs(origin.left - targetLeft) > 0.5 || Math.abs(origin.top - targetTop) > 0.5) {
        return true;
      }
    }
    return false;
  }

  function getClickSegmentClickDelayLocal(segment) {
    if (!segment || typeof segment.clickDelay !== 'number' || segment.clickDelay <= 0) {
      return 0;
    }
    const span = segment.end - segment.start;
    if (span <= 0) {
      return 0;
    }
    return Math.min(0.9, segment.clickDelay / span);
  }

  function resolveClickSegmentClickLocal(local, usesTravelPhase, segment) {
    const delayLocal = getClickSegmentClickDelayLocal(segment);
    const ratio = getClickSegmentTravelRatio(segment);
    let clickStart;
    let clickSpan;
    if (!usesTravelPhase) {
      clickStart = delayLocal;
      clickSpan = 1 - delayLocal;
    } else {
      clickStart = ratio + delayLocal;
      clickSpan = 1 - ratio - delayLocal;
    }
    if (clickSpan <= 0.001 || local < clickStart) {
      return null;
    }
    return (local - clickStart) / clickSpan;
  }

  function getClickSegmentTravelT(local, segment) {
    const ratio = getClickSegmentTravelRatio(segment);
    if (local >= ratio) {
      return 1;
    }
    if (segment && (segment.sharpTravel || segment.fastTravel)) {
      return local / ratio;
    }
    return smoothSegmentEase(local / ratio);
  }

  function getClickSegmentPressOpacity(local, usesTravelPhase, segment) {
    if (!clickSegmentHasClick(segment)) {
      return 0;
    }
    const clickLocal = resolveClickSegmentClickLocal(local, usesTravelPhase, segment);
    if (clickLocal == null) {
      return 0;
    }
    return segmentEdgeOpacity(clickLocal, CLICK_SEGMENT_PRESS_FADE);
  }

  function hasAdjacentSegmentAfter(segments, segment) {
    return segments.some((item) => (
      item !== segment && Math.abs(item.start - segment.end) < 0.001
    ));
  }

  function hasFollowingSegment(segments, segment) {
    const index = segments.indexOf(segment);
    return index >= 0 && index < segments.length - 1;
  }

  function hasPrecedingSegment(segments, segment) {
    return segments.indexOf(segment) > 0;
  }

  function resolveGapIconBlend(segments, progress) {
    for (let i = 0; i < segments.length - 1; i++) {
      const prev = segments[i];
      const next = segments[i + 1];
      if (progress <= prev.end || progress >= next.start) {
        continue;
      }
      const t = easeInOut((progress - prev.end) / (next.start - prev.end));
      const icons = { move: t, lmb: 0, rmb: 0, scroll: 0 };
      const prevOpacity = 1 - t;
      if (prev.button === 'lmb') {
        icons.lmb = prevOpacity;
      } else if (prev.button === 'rmb') {
        icons.rmb = prevOpacity;
      } else if (prev.button === 'scroll') {
        icons.scroll = prevOpacity;
      }
      return icons;
    }
    return null;
  }

  function computeSegmentIconOpacities(segment, local, hasNext, hasPrev) {
    const fade = 0.1;
    const icons = { move: 0, lmb: 0, rmb: 0, scroll: 0 };
    let pressT = 1;

    if (local < fade) {
      pressT = easeInOut(local / fade);
      icons.move = 1 - pressT;
    } else if (!hasNext && local > 1 - fade) {
      pressT = easeInOut((1 - local) / fade);
      icons.move = 1 - pressT;
    }

    if (segment.button === 'lmb') {
      icons.lmb = pressT;
    } else if (segment.button === 'rmb') {
      icons.rmb = pressT;
    } else if (segment.button === 'scroll') {
      icons.scroll = pressT;
      if (local < fade) {
        icons.move = 1 - pressT;
      } else if (!hasNext && local > 1 - fade) {
        icons.move = 1 - pressT;
      }
    }

    if (!icons.move && pressT < 1 && local >= fade && (hasNext || local <= 1 - fade)) {
      icons.move = 0;
    }

    return icons;
  }

  function applyDragIconOpacities(state, icons, multiplier, scrollLocal, rotateDeg) {
    const m = typeof multiplier === 'number' ? multiplier : 1;
    const moveOp = (icons.move || 0) * m;
    const lmbOp = (icons.lmb || 0) * m;
    const rmbOp = (icons.rmb || 0) * m;
    const scrollOp = (icons.scroll || 0) * m;
    const pressOp = Math.max(lmbOp, rmbOp, scrollOp);

    state.moveIcon.style.opacity = String(moveOp);
    state.lmbIcon.style.opacity = String(lmbOp);
    state.rmbIcon.style.opacity = String(rmbOp);
    state.lmbIcon.style.filter = lmbOp > 0.01
      ? 'drop-shadow(0 0 8px rgba(54, 54, 227, 0.8))'
      : 'none';
    state.rmbIcon.style.filter = rmbOp > 0.01
      ? 'drop-shadow(0 0 8px rgba(54, 54, 227, 0.8))'
      : 'none';
    if (state.scrollIcon) {
      if (scrollOp > 0.01 && typeof scrollLocal === 'number') {
        setVrZoomScrollIcon(state, scrollLocal);
      }
      state.scrollIcon.style.opacity = String(scrollOp);
      state.scrollIcon.style.filter = scrollOp > 0.01
        ? 'drop-shadow(0 0 8px rgba(54, 54, 227, 0.8))'
        : 'none';
    }
    if (state.pressLine) {
      const showPressLine = state.controllerSizing && pressOp > 0.01 && moveOp <= 0.05;
      state.pressLine.style.opacity = String(showPressLine ? pressOp : 0);
    }

    const pressLevel = m > 0.01 ? pressOp / m : 0;
    const scale = scrollOp > 0.01
      ? 1
      : (pressOp > 0.01 ? 0.96 + 0.04 * (1 - pressLevel) : 1);
    setOverlayInnerTransform(state, scale, rotateDeg);
    const glowOpacity = 0.7 * pressOp;
    state.glow.style.opacity = String(glowOpacity);
    state.glow.style.transform = 'translate(-50%, -50%) scale(' + (0.8 + glowOpacity * 0.4) + ')';
  }

  function getDragTravelTiming(state, duration) {
    const startDelay = typeof state.videoStartDelaySec === 'number'
      ? state.videoStartDelaySec
      : 1;
    let introSec = typeof state.introSec === 'number' ? state.introSec : startDelay;
    let outroSec = typeof state.outroSec === 'number' ? state.outroSec : startDelay;
    if (Number.isFinite(duration) && duration > 0) {
      const maxTravel = Math.max(0.15, (duration - 0.2) / 2);
      introSec = Math.min(introSec, maxTravel);
      outroSec = Math.min(outroSec, maxTravel);
    }
    return { introSec: introSec, outroSec: outroSec };
  }

  function isDragIntroPhase(state, videoTime, duration) {
    const timing = getDragTravelTiming(state, duration);
    return videoTime < timing.introSec;
  }

  function getDragIntroT(state, videoTime, duration) {
    const timing = getDragTravelTiming(state, duration);
    if (timing.introSec <= 0) {
      return 1;
    }
    return easeInOut(Math.min(1, videoTime / timing.introSec));
  }

  function getDragOutroT(state, videoTime, duration, demoProgress) {
    const timing = getDragTravelTiming(state, duration);
    let t = 0;
    const lastSegment = state.segments[state.segments.length - 1];
    if (lastSegment && isSegmentActive(lastSegment, demoProgress)) {
      const local = (demoProgress - lastSegment.start) / (lastSegment.end - lastSegment.start);
      if (local > 0.85) {
        t = Math.max(t, easeInOut((local - 0.85) / 0.15));
      }
    }
    if (Number.isFinite(duration) && duration > 0) {
      const outroStart = duration - timing.outroSec;
      if (videoTime >= outroStart) {
        t = Math.max(t, easeInOut((videoTime - outroStart) / timing.outroSec));
      }
    }
    if (demoProgress >= 0.99) {
      t = Math.max(t, easeInOut((demoProgress - 0.99) / 0.01));
    }
    return Math.min(1, t);
  }

  function isDragOutroPhase(state, videoTime, duration, demoProgress) {
    return getDragOutroT(state, videoTime, duration, demoProgress) > 0;
  }

  function showDragOverlayTravel(state, t, direction, options) {
    const fromLeft = direction === 'intro' ? state.fromLeft : state.centerLeft;
    const toLeft = direction === 'intro' ? state.centerLeft : state.fromLeft;
    const fromTop = direction === 'intro' ? state.fromTop : state.centerTop;
    const toTop = direction === 'intro' ? state.centerTop : state.fromTop;
    const left = lerp(fromLeft, toLeft, t);
    const top = lerp(fromTop, toTop, t);
    const icons = { move: 1, lmb: 0, rmb: 0, scroll: 0 };
    const scrollBlend = options && typeof options.scrollBlend === 'number' ? options.scrollBlend : 0;
    if (scrollBlend > 0) {
      icons.move = 1 - scrollBlend;
      icons.scroll = scrollBlend;
    }
    state.wrapper.style.visibility = 'visible';
    state.wrapper.style.opacity = '1';
    state.wrapper.style.left = left + '%';
    state.wrapper.style.top = top + '%';
    applyMouseOverlayAnchor(state, null);
    const scrollLocal = scrollBlend > 0 ? (scrollBlend > 0.5 ? 0.15 : 0.35) : null;
    applyDragIconOpacities(state, icons, 1, scrollLocal);
  }

  function showDragOverlayAtHome(state) {
    showDragOverlayTravel(state, 1, 'outro');
  }

  function showDragOverlayWithIcons(state, icons) {
    state.wrapper.style.visibility = 'visible';
    state.wrapper.style.opacity = '1';
    state.wrapper.style.left = state.centerLeft + '%';
    state.wrapper.style.top = state.centerTop + '%';
    applyMouseOverlayAnchor(state, null);
    const scrollLocal = icons.scroll > 0 ? 0.35 : null;
    applyDragIconOpacities(state, icons, 1, scrollLocal);
  }

  function isWithinDragTutorialRange(state, progress) {
    if (!state.segments || !state.segments.length) {
      return false;
    }
    const first = state.segments[0].start;
    const last = state.segments[state.segments.length - 1].end;
    return progress >= first && progress <= last;
  }

  function showDragOverlayIdle(state) {
    showDragOverlayWithIcons(state, { move: 1, lmb: 0, rmb: 0, scroll: 0 });
  }

  function resolveDragSegment(state, videoProgress) {
    const segments = state.segments;
    const active = segments.find((item) => isSegmentActive(item, videoProgress));
    if (active) {
      return { segment: active, opacityMul: 1, local: null };
    }

    for (let i = 0; i < segments.length - 1; i++) {
      const seg = segments[i];
      const next = segments[i + 1];
      if (videoProgress > seg.end && videoProgress < next.start) {
        return { idle: true, opacityMul: 1 };
      }
    }

    const gapBridge = 0.02;
    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i];
      if (videoProgress >= seg.end && videoProgress < seg.end + gapBridge) {
        const next = segments[i + 1];
        if (next && Math.abs(next.start - seg.end) < 0.001) {
          const opacityMul = 1 - (videoProgress - seg.end) / gapBridge;
          return { segment: seg, opacityMul, local: 1 };
        }
      }
      if (videoProgress < seg.start && videoProgress >= seg.start - gapBridge) {
        const opacityMul = (videoProgress - (seg.start - gapBridge)) / gapBridge;
        return { segment: seg, opacityMul, local: 0 };
      }
    }
    return null;
  }

  function shouldHideLmbPointerOutsideCycle(state) {
    return lmbCycleUsesApproachPointer(state);
  }

  function resolveLmbPointerPresenceOpacity(state, videoTime, duration) {
    if (!shouldHideLmbPointerOutsideCycle(state)) {
      return 1;
    }
    if (!Number.isFinite(duration) || duration <= 0) {
      return 0;
    }
    const times = getLmbCursorApproachTimes(state, duration);
    if (videoTime < times.approachStartSec || videoTime > times.returnEndSec) {
      return 0;
    }
    const fadeInSec = times.presenceFadeSec != null
      ? times.presenceFadeSec
      : getLmbPointerPresenceFadeSec(state);
    let opacity = 1;
    if (videoTime < times.approachStartSec + fadeInSec) {
      opacity = easeInOut((videoTime - times.approachStartSec) / Math.max(0.001, fadeInSec));
    }
    const fadeOutStartSec = state.videoStart * duration + (state.videoEnd - state.videoStart) * duration * 0.88;
    if (videoTime > fadeOutStartSec && videoTime < times.returnEndSec) {
      opacity = easeInOut((times.returnEndSec - videoTime) / Math.max(0.001, times.returnEndSec - fadeOutStartSec));
    }
    return opacity;
  }

  function isLmbPointerCycleActive(state, videoTime, duration) {
    if (!shouldHideLmbPointerOutsideCycle(state)) {
      return true;
    }
    if (!Number.isFinite(duration) || duration <= 0) {
      return false;
    }
    const travelTimes = getLmbCursorApproachTimes(state, duration);
    return videoTime >= travelTimes.approachStartSec && videoTime <= travelTimes.returnEndSec;
  }

  function setMouseOverlayIdleAtHome(state) {
    state.wrapper.style.visibility = 'visible';
    state.wrapper.style.opacity = '1';
    state.wrapper.style.left = mouseOverlayHomeLeft(state) + '%';
    state.wrapper.style.top = mouseOverlayHomeTop(state) + '%';
    state.inner.style.transform = 'scale(' + mouseOverlayInnerScale(state, 1) + ')';
    state.moveIcon.style.opacity = '1';
    state.lmbIcon.style.opacity = '0';
    state.rmbIcon.style.opacity = '0';
    state.lmbIcon.style.filter = 'none';
    state.rmbIcon.style.filter = 'none';
    if (state.scrollIcon) {
      state.scrollIcon.style.opacity = '0';
      state.scrollIcon.style.filter = 'none';
    }
    if (state.pressLine) {
      state.pressLine.style.opacity = '0';
    }
    state.glow.style.opacity = '0';
    state.glow.style.transform = 'translate(-50%, -50%) scale(0.8)';
    updateClickCursorVisuals(state, state.cursorFromLeft, state.cursorFromTop, 1, 0);
  }

  function updateLmbCycleOverlay(state, videoProgress, videoTime, duration) {
    const cursorState = resolveLmbCursorState(state, videoTime, duration);
    const clickVisuals = resolveLmbMouseClickVisuals(state, videoTime, duration);
    const times = Number.isFinite(duration) && duration > 0 ? getLmbCycleTimes(state, duration) : null;
    const travelTimes = Number.isFinite(duration) && duration > 0 ? getLmbCursorApproachTimes(state, duration) : null;
    const inMouseCycle = videoProgress >= state.videoStart && videoProgress <= state.videoEnd;
    const inClickPhase = times
      && videoTime >= times.pressStartSec
      && videoTime < times.releaseEndSec;
    const pointerTravelActive = travelTimes
      && videoTime >= travelTimes.approachStartSec
      && videoTime <= travelTimes.returnEndSec
      && lmbPointerUsesTravel(state);

    if (shouldHideLmbPointerOutsideCycle(state)) {
      if (!isLmbPointerCycleActive(state, videoTime, duration)) {
        hideMouseOverlay(state);
        updateLmbCursorVisuals(state, cursorState);
        return;
      }
    } else if (!inMouseCycle && !cursorState.active && !inClickPhase && !pointerTravelActive) {
      setMouseOverlayIdleAtHome(state);
      return;
    }

    applyMouseHomeVisuals(state, clickVisuals, videoTime, duration);
    updateLmbCursorVisuals(state, cursorState);
  }

  function updateDragSegmentsOverlay(state, videoProgress) {
    const resolved = resolveDragSegment(state, videoProgress);
    if (!resolved) {
      if (isVrTutorialPage()) {
        hideMouseOverlay(state);
        return;
      }
      showDragOverlayAtHome(state);
      return;
    }

    if (resolved.idle) {
      const approach = resolvePathApproachTravel(state, videoProgress);
      if (approach) {
        showPathApproachTravel(state, approach);
        return;
      }
      const gapIcons = resolveGapIconBlend(state.segments, videoProgress);
      if (gapIcons) {
        showDragOverlayWithIcons(state, gapIcons);
      } else {
        showDragOverlayIdle(state);
      }
      return;
    }

    const segment = resolved.segment;
    const local = resolved.local != null
      ? resolved.local
      : (videoProgress - segment.start) / (segment.end - segment.start);
    const hasNext = hasFollowingSegment(state.segments, segment);
    const hasPrev = hasPrecedingSegment(state.segments, segment);
    const skipFadeOut = segment.button === 'scroll' || hasNext;
    const skipFadeIn = hasPrev || (typeof state.fromLeft === 'number' && typeof state.fromTop === 'number');
    const edgeOpacity = segmentEdgeOpacity(local, {
      skipFadeOut: skipFadeOut,
      skipFadeIn: skipFadeIn,
      fadeIn: 0.08,
      fadeOut: 0.08
    });
    const iconOpacities = computeSegmentIconOpacities(segment, local, hasNext, hasPrev);

    if (segment.button === 'scroll') {
      const left = segment.left != null
        ? parsePercentValue(segment.left, state.centerLeft)
        : state.centerLeft;
      const top = segment.top != null
        ? parsePercentValue(segment.top, state.centerTop)
        : state.centerTop;

      state.wrapper.style.visibility = 'visible';
      state.wrapper.style.opacity = String(edgeOpacity);
      applyMouseOverlayAnchor(state, segment);
      state.wrapper.style.left = left + '%';
      state.wrapper.style.top = top + '%';
      applyDragIconOpacities(state, iconOpacities, edgeOpacity, resolveScrollIconLocal(segment, local));
      return;
    }

    const anchor = resolveDragSegmentAnchor(state, segment);
    let left = anchor.left;
    let top = anchor.top;
    const pathDragRange = resolvePathDragRange(state);
    const pathOffset = resolveDragPathOffset(segment, local);

    if (pathOffset) {
      left += pathOffset.x * pathDragRange;
      top += pathOffset.y * pathDragRange;
    } else if (segment.axis === 'x') {
      const wave = getSegmentWave(segment, local);
      const xWave = segment.button === 'rmb' ? -wave : wave;
      left += xWave * state.dragRange;
    } else {
      const wave = getSegmentWave(segment, local);
      top += wave * state.dragRange;
    }

    state.wrapper.style.visibility = 'visible';
    state.wrapper.style.opacity = String(edgeOpacity);
    applyMouseOverlayAnchor(state, segment);
    state.wrapper.style.left = left + '%';
    state.wrapper.style.top = top + '%';
    applyDragIconOpacities(state, iconOpacities, edgeOpacity);
  }

  function updateClickSegmentsOverlay(state, videoProgress) {
    const segment = state.segments.find((item) => isSegmentActive(item, videoProgress));
    const firstSegment = state.segments[0];
    const lastSegment = state.segments[state.segments.length - 1];
    const fade = CLICK_SEGMENT_GROUP_FADE;
    const groupStart = firstSegment ? firstSegment.start : 0;
    const groupEnd = lastSegment ? lastSegment.end : 0;
    const isGroupVisible = firstSegment && lastSegment && videoProgress >= groupStart && videoProgress <= groupEnd + fade;
    if (!segment && !isGroupVisible) {
      hideMouseOverlay(state);
      if (state.clickCursorWrapper) {
        updateClickCursorVisuals(state, state.cursorFromLeft, state.cursorFromTop, 1, 0);
      }
      return;
    }

    const presenceOpacityBase = resolveClickSegmentGroupOpacity(
      state,
      videoProgress,
      groupStart,
      groupEnd,
      fade,
      lastSegment
    );

    if (!segment && isGroupVisible) {
      const gap = findClickSegmentGap(state.segments, videoProgress);
      if (gap) {
        if (clickSegmentPointerTravelsContinuously(state)) {
          const gapPosition = resolveClickSegmentGapPosition(state, gap);
          const cursorLeft = gapPosition.left;
          const cursorTop = gapPosition.top;
          if (state.clickCursorWrapper) {
            updateClickCursorVisuals(state, cursorLeft, cursorTop, 1, presenceOpacityBase);
          }
          if (state.useHandIcons || (state.alignPointerToTarget && state.controllerSizing)) {
            state.wrapper.style.visibility = 'visible';
            state.wrapper.style.opacity = '1';
            setClickSegmentPointerWrapperPosition(state, cursorLeft, cursorTop);
            applyDragIconOpacities(state, resolveClickSegmentGapIcons(gap, videoProgress), 1, undefined, 0);
          } else if (state.clickCursorWrapper) {
            applyClickSegmentMouseWrapper(state, presenceOpacityBase);
          }
          return;
        }
        if (clickSegmentGapUsesReturnHome(state, gap)) {
          showClickSegmentGapHome(state, gap, presenceOpacityBase);
          return;
        }
      }
    }

    if (!segment && isGroupVisible && lastSegment && videoProgress >= lastSegment.end
      && clickSegmentHasReturnHome(lastSegment)) {
      const tailOpacity = resolveClickSegmentGroupOpacity(
        state,
        videoProgress,
        groupStart,
        groupEnd,
        fade,
        null
      );
      const cursorHomeLeft = clickSegmentCursorHomeLeft(state, lastSegment);
      const cursorHomeTop = clickSegmentCursorHomeTop(state, lastSegment);
      const controllerHomeLeft = clickSegmentControllerHomeLeft(state, lastSegment);
      const controllerHomeTop = clickSegmentControllerHomeTop(state, lastSegment);
      if (state.clickCursorWrapper) {
        updateClickCursorVisuals(state, cursorHomeLeft, cursorHomeTop, 1, 0);
      }
      if (state.useHandIcons || (state.alignPointerToTarget && state.controllerSizing)) {
        const pointerHomeLeft = state.alignPointerToTarget && state.controllerSizing
          ? controllerHomeLeft
          : cursorHomeLeft;
        const pointerHomeTop = state.alignPointerToTarget && state.controllerSizing
          ? controllerHomeTop
          : cursorHomeTop;
        state.wrapper.style.visibility = 'visible';
        state.wrapper.style.opacity = String(tailOpacity);
        setClickSegmentPointerWrapperPosition(state, pointerHomeLeft, pointerHomeTop);
        applyDragIconOpacities(state, { move: 1, lmb: 0, rmb: 0, scroll: 0 }, tailOpacity, undefined, 0);
      } else if (state.keepMouseVisible && state.clickCursorWrapper) {
        applyClickSegmentMouseWrapper(state, tailOpacity);
      } else {
        hideMouseOverlay(state);
      }
      return;
    }

    if (!segment && isGroupVisible && lastSegment && videoProgress >= lastSegment.end) {
      const lastLeft = clickSegmentTargetLeft(lastSegment);
      const lastTop = clickSegmentTargetTop(lastSegment);
      if (state.clickCursorWrapper) {
        updateClickCursorVisuals(state, lastLeft, lastTop, 1, presenceOpacityBase);
      }
      if (state.useHandIcons || (state.alignPointerToTarget && state.controllerSizing)) {
        state.wrapper.style.visibility = 'visible';
        state.wrapper.style.opacity = '1';
        setClickSegmentPointerWrapperPosition(state, lastLeft, lastTop);
        applyDragIconOpacities(state, { move: 1, lmb: 0, rmb: 0, scroll: 0 }, 1, undefined, 0);
      } else if (state.keepMouseVisible && state.clickCursorWrapper) {
        applyClickSegmentMouseWrapper(state, presenceOpacityBase);
      }
      return;
    }

    const activeSegment = segment || firstSegment;
    const segmentSpan = activeSegment.end - activeSegment.start;
    const local = segment && segmentSpan > 0
      ? (videoProgress - segment.start) / segmentSpan
      : 0;
    const phases = resolveClickSegmentPhases(local, activeSegment, state);
    const usesTravelPhase = phases.usesTravel;
    const presenceOpacity = resolveClickSegmentPointerPresenceOpacity(
      state,
      presenceOpacityBase,
      segment,
      local,
      usesTravelPhase
    );
    const pressOpacity = segment ? phases.pressOpacity : 0;
    const targetLeft = clickSegmentTargetLeft(activeSegment);
    const targetTop = clickSegmentTargetTop(activeSegment);
    const cursorOrigin = resolveClickSegmentTravelOrigin(
      state,
      activeSegment,
      clickSegmentCursorHomeLeft,
      clickSegmentCursorHomeTop
    );
    const controllerOrigin = resolveClickSegmentTravelOrigin(
      state,
      activeSegment,
      clickSegmentControllerHomeLeft,
      clickSegmentControllerHomeTop
    );
    const cursorHomeLeft = clickSegmentCursorHomeLeft(state, activeSegment);
    const cursorHomeTop = clickSegmentCursorHomeTop(state, activeSegment);
    const controllerHomeLeft = clickSegmentControllerHomeLeft(state, activeSegment);
    const controllerHomeTop = clickSegmentControllerHomeTop(state, activeSegment);
    const travelT = resolveClickSegmentEffectiveTravelT(
      state,
      activeSegment,
      local,
      phases.travelInT
    );
    const returnT = phases.returnT;
    const rotateDeg = resolveClickSegmentControllerRotateDeg(state, activeSegment, phases);
    const targetOffsetT = resolveClickSegmentControllerTargetOffsetT(
      state,
      phases,
      travelT,
      usesTravelPhase
    );
    const button = activeSegment.button || 'lmb';
    let wrapperLeft;
    let wrapperTop;

    if (state.alignPointerToTarget && state.controllerSizing) {
      if (returnT > 0) {
        wrapperLeft = lerp(targetLeft, controllerHomeLeft, returnT);
        wrapperTop = lerp(targetTop, controllerHomeTop, returnT);
      } else if (usesTravelPhase) {
        wrapperLeft = lerp(controllerOrigin.left, targetLeft, travelT);
        wrapperTop = lerp(controllerOrigin.top, targetTop, travelT);
      } else {
        wrapperLeft = targetLeft;
        wrapperTop = targetTop;
      }
    } else if (state.useHandIcons) {
      if (returnT > 0) {
        wrapperLeft = lerp(targetLeft, cursorHomeLeft, returnT);
        wrapperTop = lerp(targetTop, cursorHomeTop, returnT);
      } else if (usesTravelPhase) {
        wrapperLeft = lerp(cursorOrigin.left, targetLeft, travelT);
        wrapperTop = lerp(cursorOrigin.top, targetTop, travelT);
      } else {
        wrapperLeft = targetLeft;
        wrapperTop = targetTop;
      }
    } else if (state.clickCursorWrapper) {
      wrapperLeft = parsePercentValue(activeSegment.mouseLeft, parsePercentValue(state.mouseLeft, targetLeft));
      wrapperTop = parsePercentValue(activeSegment.mouseTop, parsePercentValue(state.mouseTop, targetTop));
    } else {
      wrapperLeft = targetLeft;
      wrapperTop = targetTop;
    }

    if (state.controllerSizing && typeof state.controllerTravelRotateDeg === 'number') {
      const offsetPos = applyClickSegmentControllerTargetOffset(
        state,
        wrapperLeft,
        wrapperTop,
        targetOffsetT
      );
      wrapperLeft = offsetPos.left;
      wrapperTop = offsetPos.top;
    }

    if (state.clickCursorWrapper) {
      let cursorLeft;
      let cursorTop;
      let cursorScale = 1;
      if (returnT > 0) {
        cursorLeft = lerp(targetLeft, cursorHomeLeft, returnT);
        cursorTop = lerp(targetTop, cursorHomeTop, returnT);
      } else if (usesTravelPhase) {
        cursorLeft = lerp(cursorOrigin.left, targetLeft, travelT);
        cursorTop = lerp(cursorOrigin.top, targetTop, travelT);
      } else {
        cursorLeft = targetLeft;
        cursorTop = targetTop;
      }
      if (pressOpacity > 0) {
        cursorScale = 0.96 + 0.04 * (1 - pressOpacity);
      }
      let cursorOpacity = presenceOpacity;
      if (state.continuousCursor) {
        if (returnT > 0) {
          cursorOpacity = presenceOpacity * (1 - returnT * 0.85);
        } else if (getClickSegmentIndex(state.segments, activeSegment) === 0
          && local < CLICK_SEGMENT_TRAVEL_RATIO
          && usesTravelPhase
          && !clickSegmentHasReturnHome(activeSegment)) {
          cursorOpacity = smoothSegmentEase(Math.min(1, local / (CLICK_SEGMENT_TRAVEL_RATIO * 0.5))) * presenceOpacity;
        }
      } else if (clickSegmentHasReturnHome(activeSegment) && returnT <= 0 && local < CLICK_SEGMENT_RETURN_TRAVEL_RATIO) {
        cursorOpacity = smoothSegmentEase(Math.min(1, local / (CLICK_SEGMENT_RETURN_TRAVEL_RATIO * 0.5))) * presenceOpacity;
      } else if (usesTravelPhase && local < CLICK_SEGMENT_TRAVEL_RATIO && !clickSegmentHasReturnHome(activeSegment)) {
        cursorOpacity = smoothSegmentEase(Math.min(1, local / (CLICK_SEGMENT_TRAVEL_RATIO * 0.5))) * presenceOpacity;
      } else if (returnT > 0) {
        cursorOpacity = presenceOpacity * (1 - returnT * 0.85);
      }
      updateClickCursorVisuals(state, cursorLeft, cursorTop, cursorScale, cursorOpacity);
    }

    state.wrapper.style.visibility = 'visible';
    state.wrapper.style.opacity = usesClickSegmentSmoothPointerIcons(state) ? '1' : String(presenceOpacity);
    setClickSegmentPointerWrapperPosition(state, wrapperLeft, wrapperTop);
    if (state.keepMouseVisible && state.clickCursorWrapper) {
      applyMouseMoveWithClickFeedback(state, button, pressOpacity);
    } else if (usesClickSegmentSmoothPointerIcons(state)) {
      applyClickSegmentPointerVisuals(state, activeSegment, local, usesTravelPhase, presenceOpacity, rotateDeg);
    } else if (state.clickCursorWrapper && clickSegmentHasReturnHome(activeSegment)) {
      applyMouseMoveOnlyVisuals(state);
    } else {
      applyPressedVisuals(state, button, pressOpacity);
    }
  }

  function applyControllerClickVisuals(state, opacity, local) {
    let scale = 1;
    let glowOpacity = 0;
    let shadow = false;
    let defaultIconOpacity = opacity;
    let pressedIconOpacity = 0;

    if (local < 0.25) {
      const t = easeInOut(local / 0.25);
      scale = 1 - 0.04 * t;
      glowOpacity = t * 0.7;
      shadow = t > 0.3;
      defaultIconOpacity = (1 - t) * opacity;
      pressedIconOpacity = t * opacity;
    } else if (local < 0.75) {
      scale = 0.96;
      glowOpacity = 0.7;
      shadow = true;
      defaultIconOpacity = 0;
      pressedIconOpacity = opacity;
    } else {
      const t = easeInOut((local - 0.75) / 0.25);
      scale = 0.96 + 0.04 * t;
      glowOpacity = 0.7 * (1 - t);
      shadow = glowOpacity > 0.2;
      defaultIconOpacity = t * opacity;
      pressedIconOpacity = (1 - t) * opacity;
    }

    state.icon.style.opacity = String(defaultIconOpacity);
    state.pressedIcon.style.opacity = String(pressedIconOpacity);
    state.leftIcon.style.opacity = '0';
    state.leftPressedIcon.style.opacity = '0';
    state.pressLine.style.opacity = String(pressedIconOpacity);
    state.icon.style.filter = shadow && defaultIconOpacity > 0
      ? 'drop-shadow(0 0 8px rgba(54, 54, 227, 0.8))'
      : 'none';
    state.pressedIcon.style.filter = shadow && pressedIconOpacity > 0
      ? 'drop-shadow(0 0 8px rgba(54, 54, 227, 0.8))'
      : 'none';
    state.leftIcon.style.filter = 'none';
    state.leftPressedIcon.style.filter = 'none';
    state.inner.style.transform = 'scale(' + controllerDisplayScale(scale) + ')';
    state.glow.style.opacity = String(glowOpacity * opacity);
    state.glow.style.transform = 'translate(-50%, -50%) scale(' + (0.8 + glowOpacity * opacity * 0.4) + ')';
  }

  function applyControllerDragVisuals(state, opacity) {
    state.icon.style.opacity = '0';
    state.pressedIcon.style.opacity = '0';
    state.leftIcon.style.opacity = String(opacity);
    state.leftPressedIcon.style.opacity = '0';
    state.pressLine.style.opacity = '0';
    state.icon.style.filter = 'none';
    state.pressedIcon.style.filter = 'none';
    state.leftIcon.style.filter = 'drop-shadow(0 0 8px rgba(54, 54, 227, 0.8))';
    state.leftPressedIcon.style.filter = 'none';
    state.inner.style.transform = 'scale(' + controllerDisplayScale(0.96) + ')';
    const glowOpacity = 0.7 * opacity;
    state.glow.style.opacity = String(glowOpacity);
    state.glow.style.transform = 'translate(-50%, -50%) scale(' + (0.8 + glowOpacity * 0.4) + ')';
  }

  function updateControllerSegmentsOverlay(state, videoProgress) {
    const resolved = resolveDragSegment(state, videoProgress);
    if (!resolved) {
      hideMouseOverlay(state);
      return;
    }

    const segment = resolved.segment;
    const local = resolved.local != null
      ? resolved.local
      : (videoProgress - segment.start) / (segment.end - segment.start);
    const skipFadeOut = hasAdjacentSegmentAfter(state.segments, segment);
    const opacity = segmentEdgeOpacity(local, { skipFadeOut: skipFadeOut }) * resolved.opacityMul;
    const centerLeft = parsePercentValue(segment.left, 50);
    const centerTop = parsePercentValue(segment.top, 50);
    let left = centerLeft;
    let top = centerTop;

    if (segment.action === 'drag') {
      const dragRange = typeof segment.dragRange === 'number' ? segment.dragRange : state.dragRange;
      const wave = Math.sin(local * Math.PI * 2);
      if (segment.axis === 'y') {
        top += wave * dragRange;
      } else {
        left += wave * dragRange;
      }
      applyControllerDragVisuals(state, opacity);
    } else {
      applyControllerClickVisuals(state, opacity, local);
    }

    state.wrapper.style.visibility = 'visible';
    state.wrapper.style.opacity = String(opacity);
    state.wrapper.style.left = left + '%';
    if (segment.action === 'drag') {
      state.wrapper.style.top = 'calc(' + top + '% + ' + CONTROLLER_DRAG_TOP_OFFSET_PX + 'px)';
    } else {
      state.wrapper.style.top = top + '%';
    }
  }

  function updateMouseOverlay() {
    const state = mouseOverlayState;
    const activeVideo = getOverlayVideoEl();
    if (!state) {
      return;
    }

    if (isTutorialMediaLoading()) {
      hideMouseOverlay(state);
      if (state.clickCursorWrapper) {
        updateClickCursorVisuals(state, state.cursorFromLeft, state.cursorFromTop, 1, 0);
      }
      refreshNativeOverlayMeshIfNeeded();
      return;
    }

    const duration = activeVideo.duration;
    if (!Number.isFinite(duration) || duration <= 0) {
      if (state.type === 'mouse-drag-segments' && typeof state.fromLeft === 'number') {
        if (isVrTutorialPage()) {
          hideMouseOverlay(state);
        } else {
          showDragOverlayAtHome(state);
        }
      } else {
        hideMouseOverlay(state);
      }
      refreshNativeOverlayMeshIfNeeded();
      return;
    }

    const videoTime = activeVideo.currentTime;
    const videoProgress = videoTime / duration;

    if (state.type === 'mouse-lmb-cycle') {
      updateLmbCycleOverlay(state, videoProgress, videoTime, duration);
      refreshNativeOverlayMeshIfNeeded();
      return;
    }

    if (state.type === 'mouse-drag-segments') {
      const startDelay = typeof state.videoStartDelaySec === 'number'
        ? state.videoStartDelaySec
        : 1;
      const endPadding = typeof state.videoEndPaddingSec === 'number'
        ? state.videoEndPaddingSec
        : startDelay;
      const demoProgress = mapVideoToProgress(videoTime, duration, startDelay, endPadding);

      if (isDragIntroPhase(state, videoTime, duration)) {
        const introT = getDragIntroT(state, videoTime, duration);
        showDragOverlayTravel(state, introT, 'intro');
        updateRotateMoveSwitcher(demoProgress, state.segments);
        refreshNativeOverlayMeshIfNeeded();
        return;
      }

      if (isDragOutroPhase(state, videoTime, duration, demoProgress)) {
        const outroT = getDragOutroT(state, videoTime, duration, demoProgress);
        const lastSegment = state.segments[state.segments.length - 1];
        let scrollBlend = 0;
        if (lastSegment && lastSegment.button === 'scroll' && demoProgress >= lastSegment.start) {
          scrollBlend = 1 - outroT;
        }
        showDragOverlayTravel(state, outroT, 'outro', { scrollBlend: scrollBlend });
        updateRotateMoveSwitcher(demoProgress, state.segments);
        refreshNativeOverlayMeshIfNeeded();
        return;
      }

      updateDragSegmentsOverlay(state, demoProgress);
      updateRotateMoveSwitcher(demoProgress, state.segments);
      refreshNativeOverlayMeshIfNeeded();
      return;
    }

    if (state.type === 'mouse-click-segments') {
      updateClickSegmentsOverlay(state, videoProgress);
      refreshNativeOverlayMeshIfNeeded();
      return;
    }

    if (state.type === 'controller-segments') {
      updateControllerSegmentsOverlay(state, videoProgress);
      refreshNativeOverlayMeshIfNeeded();
    }
  }

  function startMouseOverlayLoop() {
    if (!mouseOverlayState || mouseOverlayRaf) {
      return;
    }
    const activeVideo = getOverlayVideoEl();
    const loop = () => {
      updateMouseOverlay();
      if (mouseOverlayState && !activeVideo.paused) {
        mouseOverlayRaf = scheduleFrame(loop);
      } else {
        mouseOverlayRaf = null;
      }
    };
    mouseOverlayRaf = scheduleFrame(loop);
  }

  function stopMouseOverlayLoop() {
    if (mouseOverlayRaf) {
      cancelFrame(mouseOverlayRaf);
      mouseOverlayRaf = null;
    }
    updateMouseOverlay();
  }

  function bindMouseOverlaySync(state) {
    mouseOverlayState = state;
    applyLmbOverlayLayout(state);
    applyClickSegmentsOverlayLayout(state);
    const activeVideo = getOverlayVideoEl();
    activeVideo.addEventListener('timeupdate', updateMouseOverlay);
    activeVideo.addEventListener('seeked', updateMouseOverlay);
    activeVideo.addEventListener('loadedmetadata', updateMouseOverlay);
    activeVideo.addEventListener('play', startMouseOverlayLoop);
    activeVideo.addEventListener('pause', stopMouseOverlayLoop);
    updateMouseOverlay();
    refreshNativeOverlayMeshIfNeeded();
    if (!activeVideo.paused) {
      startMouseOverlayLoop();
    }
  }

  function renderOverlay(step) {
    stopMouseOverlaySync();
    removeChartSwitcher();

    const svgRoot = getOverlaySvgEl();
    if (!svgRoot) {
      return;
    }

    svgRoot.innerHTML = '';

    const overlayConfig = step.overlay;
    if (!overlayConfig) {
      return;
    }

    const width = overlayConfig.type === 'controller-segments'
      ? (overlayConfig.width || CONTROLLER_DEFAULT_WIDTH)
      : (overlayConfig.width || '64px');

    if (overlayConfig.type === 'controller-segments' && Array.isArray(overlayConfig.segments)) {
      const controllerElements = createControllerOverlayElement(width);
      svgRoot.appendChild(controllerElements.wrapper);
      bindMouseOverlaySync({
        type: 'controller-segments',
        dragRange: typeof overlayConfig.dragRange === 'number' ? overlayConfig.dragRange : 6,
        segments: overlayConfig.segments,
        wrapper: controllerElements.wrapper,
        inner: controllerElements.inner,
        icon: controllerElements.icon,
        pressedIcon: controllerElements.pressedIcon,
        leftIcon: controllerElements.leftIcon,
        leftPressedIcon: controllerElements.leftPressedIcon,
        pressLine: controllerElements.pressLine,
        glow: controllerElements.glow
      });
      return;
    }

    const useVrDeviceController = usesVrTutorialControllerOverlay(step, overlayConfig);
    const showLmbClickCursor = overlayConfig.type === 'mouse-lmb-cycle' && shouldShowLmbClickCursor();
    const useVrPagePointerTravel = isVrTutorialPage()
      && !isVrTutorialDevice()
      && !showLmbClickCursor;
    const elements = createMouseOverlayElement(width, {
      useControllerIcons: useVrDeviceController,
      useMouseIcons: overlayConfig.type === 'mouse-lmb-cycle'
        && !isVrTutorialDevice()
        && !isTouchTutorialDevice()
    });

    if (overlayConfig.pressLineHeight && elements.pressLine) {
      elements.pressLine.style.height = overlayConfig.pressLineHeight;
    }

    if (overlayConfig.type === 'mouse-drag-segments') {
      elements.wrapper.classList.add('tutorial-mouse-overlay--anchor-end');
    }

    let clickCursorElements = null;
    if (showLmbClickCursor) {
      const cursorWidth = overlayConfig.cursorWidth || '40px';
      clickCursorElements = createClickCursorElement(cursorWidth);
      svgRoot.appendChild(clickCursorElements.wrapper);
    }

    svgRoot.appendChild(elements.wrapper);

    if (overlayConfig.type === 'mouse-lmb-cycle') {
      const cursorStopLeft = parseLmbCursorStopLeft(overlayConfig);
      const cursorStopTop = parseLmbCursorStopTop(overlayConfig);
      const alignPointerToCursorStop = isVrTutorialDevice();
      const pointerHomeLeft = parsePercentValue(overlayConfig.fromLeft, 84);
      const pointerHomeTop = parsePercentValue(overlayConfig.fromTop, 68);
      const useTouchHandLmbSync = elements.useHandIcons && isTouchTutorialDevice();
      const useVrControllerLmbTravel = isVrTutorialDevice();
      const usePointerTravelToCursor = useVrControllerLmbTravel || useTouchHandLmbSync || useVrPagePointerTravel;
      const useTouchHandDesktopTiming = useTouchHandLmbSync && !isVrTutorialPage();
      const pointerStaysAtHome = alignPointerToCursorStop
        && !elements.useHandIcons
        && !useVrControllerLmbTravel;
      const handTargetLeft = useTouchHandLmbSync
        ? resolveTouchHandLmbTargetLeft(cursorStopLeft)
        : useVrControllerLmbTravel
          ? cursorStopLeft
          : parsePercentValue(overlayConfig.left, 23);
      const handTargetTop = useTouchHandLmbSync
        ? resolveTouchHandLmbTargetTop(cursorStopTop)
        : useVrControllerLmbTravel
          ? cursorStopTop
          : parsePercentValue(overlayConfig.top, 40);
      const handCursorTargetLeft = useTouchHandLmbSync || useVrControllerLmbTravel
        ? handTargetLeft
        : cursorStopLeft;
      const handCursorTargetTop = useTouchHandLmbSync || useVrControllerLmbTravel
        ? handTargetTop
        : cursorStopTop;
      bindMouseOverlaySync({
        type: 'mouse-lmb-cycle',
        videoStart: typeof overlayConfig.videoStart === 'number' ? overlayConfig.videoStart : 0.3,
        videoEnd: typeof overlayConfig.videoEnd === 'number' ? overlayConfig.videoEnd : 0.6,
        fromLeft: pointerHomeLeft,
        fromTop: pointerHomeTop,
        controllerLeft: alignPointerToCursorStop
          ? pointerHomeLeft
          : overlayConfig.controllerLeft != null
            ? parsePercentValue(overlayConfig.controllerLeft, pointerHomeLeft)
            : null,
        controllerTop: alignPointerToCursorStop
          ? pointerHomeTop
          : overlayConfig.controllerTop != null
            ? parsePercentValue(overlayConfig.controllerTop, pointerHomeTop)
            : null,
        toLeft: pointerStaysAtHome
          ? pointerHomeLeft
          : handTargetLeft,
        toTop: pointerStaysAtHome
          ? pointerHomeTop
          : handTargetTop,
        cursorFromLeft: parsePercentValue(overlayConfig.cursorFromLeft, 84),
        cursorFromTop: parsePercentValue(overlayConfig.cursorFromTop, 72),
        cursorToLeft: handCursorTargetLeft,
        cursorToTop: handCursorTargetTop,
        cursorLeadSec: typeof overlayConfig.cursorLeadSec === 'number' ? overlayConfig.cursorLeadSec : 2.5,
        cursorHoldSec: typeof overlayConfig.cursorHoldSec === 'number' ? overlayConfig.cursorHoldSec : 2,
        clickLeadSec: useTouchHandDesktopTiming
          ? MOBILE_SITE_LAYOUT_HAND_TIMING.clickLeadSec
          : typeof overlayConfig.clickLeadSec === 'number' ? overlayConfig.clickLeadSec : 2,
        clickEarly: useTouchHandDesktopTiming
          ? MOBILE_SITE_LAYOUT_HAND_TIMING.clickEarly
          : overlayConfig.clickEarly,
        pointerArriveEarly: useTouchHandDesktopTiming
          ? MOBILE_SITE_LAYOUT_HAND_TIMING.pointerArriveEarly
          : overlayConfig.pointerArriveEarly != null
            ? overlayConfig.pointerArriveEarly
            : overlayConfig.handArriveEarly,
        pointerTravelSec: useTouchHandDesktopTiming
          ? MOBILE_SITE_LAYOUT_HAND_TIMING.pointerTravelSec
          : overlayConfig.pointerTravelSec,
        pointerApproachLeadSec: useTouchHandDesktopTiming
          ? MOBILE_SITE_LAYOUT_HAND_TIMING.pointerApproachLeadSec
          : overlayConfig.pointerApproachLeadSec,
        pointerPresenceFadeSec: useTouchHandDesktopTiming
          ? MOBILE_SITE_LAYOUT_HAND_TIMING.pointerPresenceFadeSec
          : overlayConfig.pointerPresenceFadeSec,
        mouseTravelRange: usePointerTravelToCursor ? 0 : overlayConfig.mouseTravelRange,
        travelControllerToCursor: useVrControllerLmbTravel,
        travelVrPagePointer: useVrPagePointerTravel,
        alignPointerToTarget: isVrTutorialDevice(),
        mouseToLeft: overlayConfig.mouseToLeft != null
          ? parsePercentValue(overlayConfig.mouseToLeft, parsePercentValue(overlayConfig.left, 23))
          : null,
        mouseToTop: overlayConfig.mouseToTop != null
          ? parsePercentValue(overlayConfig.mouseToTop, parsePercentValue(overlayConfig.top, 40))
          : null,
        cursorTravelEarly: overlayConfig.cursorTravelEarly,
        controllerOffsetLeft: overlayConfig.controllerOffsetLeft,
        controllerOffsetTop: overlayConfig.controllerOffsetTop,
        handArriveEarly: overlayConfig.handArriveEarly,
        wrapper: elements.wrapper,
        inner: elements.inner,
        moveIcon: elements.moveIcon,
        lmbIcon: elements.lmbIcon,
        rmbIcon: elements.rmbIcon,
        scrollIcon: elements.scrollIcon,
        glow: elements.glow,
        pressLine: elements.pressLine,
        clickCursorWrapper: clickCursorElements ? clickCursorElements.wrapper : null,
        controllerSizing: elements.controllerSizing,
        useHandIcons: elements.useHandIcons
      });
      return;
    }

    if (overlayConfig.type === 'mouse-drag-segments' && Array.isArray(overlayConfig.segments)) {
      const videoSync = resolveOverlayVideoSync(overlayConfig);
      if (isTouchTutorialDevice() && !shouldUseFixedTutorialMediaSize()) {
        chartSwitcherEl = createTutorialRotateMoveSwitcher();
        placeChartSwitcherOnMedia();
      }
      bindMouseOverlaySync({
        type: 'mouse-drag-segments',
        fromLeft: parsePercentValue(overlayConfig.fromLeft, 88),
        fromTop: parsePercentValue(overlayConfig.fromTop, 68),
        centerLeft: parsePercentValue(overlayConfig.left, 88),
        centerTop: parsePercentValue(overlayConfig.top, 48),
        pathCenterLeft: overlayConfig.pathCenterLeft != null
          ? parsePercentValue(overlayConfig.pathCenterLeft, 50)
          : 50,
        pathCenterTop: overlayConfig.pathCenterTop != null
          ? parsePercentValue(overlayConfig.pathCenterTop, parsePercentValue(overlayConfig.top, 48))
          : parsePercentValue(overlayConfig.top, 48),
        pathDragRange: typeof overlayConfig.pathDragRange === 'number'
          ? overlayConfig.pathDragRange
          : (typeof overlayConfig.dragRange === 'number' ? overlayConfig.dragRange * 2 : 16),
        dragRange: typeof overlayConfig.dragRange === 'number' ? overlayConfig.dragRange : 8,
        segments: overlayConfig.segments,
        videoStartDelaySec: videoSync.startDelaySec,
        videoEndPaddingSec: videoSync.endPaddingSec,
        introSec: overlayConfig.introSec,
        outroSec: overlayConfig.outroSec,
        wrapper: elements.wrapper,
        inner: elements.inner,
        moveIcon: elements.moveIcon,
        lmbIcon: elements.lmbIcon,
        rmbIcon: elements.rmbIcon,
        scrollIcon: elements.scrollIcon,
        glow: elements.glow,
        pressLine: elements.pressLine,
        controllerSizing: elements.controllerSizing
      });
      return;
    }

    if (overlayConfig.type === 'mouse-click-segments' && Array.isArray(overlayConfig.segments)) {
      let clickCursorElements = null;
      if (shouldShowClickCursor()) {
        const cursorWidth = overlayConfig.cursorWidth || '40px';
        clickCursorElements = createClickCursorElement(cursorWidth);
        svgRoot.appendChild(clickCursorElements.wrapper);
      }
      bindMouseOverlaySync({
        type: 'mouse-click-segments',
        segments: overlayConfig.segments,
        fromLeft: parsePercentValue(overlayConfig.fromLeft, 88),
        fromTop: parsePercentValue(overlayConfig.fromTop, 68),
        mouseLeft: overlayConfig.mouseLeft,
        mouseTop: overlayConfig.mouseTop,
        cursorFromLeft: parsePercentValue(overlayConfig.cursorFromLeft, 80),
        cursorFromTop: parsePercentValue(overlayConfig.cursorFromTop, 53),
        controllerFromLeft: overlayConfig.controllerFromLeft,
        controllerFromTop: overlayConfig.controllerFromTop,
        alignPointerToTarget: isVrTutorialDevice(),
        keepMouseVisible: overlayConfig.keepMouseVisible === true,
        controllerOffsetLeft: overlayConfig.controllerOffsetLeft,
        controllerOffsetTop: overlayConfig.controllerOffsetTop,
        controllerTravelRotateDeg: overlayConfig.controllerTravelRotateDeg,
        controllerTravelTargetOffsetLeft: overlayConfig.controllerTravelTargetOffsetLeft,
        controllerTravelTargetOffsetTop: overlayConfig.controllerTravelTargetOffsetTop,
        continuousCursor: overlayConfig.continuousCursor === true,
        clickCursorWrapper: clickCursorElements ? clickCursorElements.wrapper : null,
        wrapper: elements.wrapper,
        inner: elements.inner,
        moveIcon: elements.moveIcon,
        lmbIcon: elements.lmbIcon,
        rmbIcon: elements.rmbIcon,
        scrollIcon: elements.scrollIcon,
        glow: elements.glow,
        pressLine: elements.pressLine,
        controllerSizing: elements.controllerSizing,
        useHandIcons: elements.useHandIcons
      });
    }
    refreshNativeOverlayMeshIfNeeded();
  }

  function renderStep(stepIndex, options) {
    const step = manifest.steps[stepIndex];
    if (!step) {
      return false;
    }

    const force2d = options && options.force2d;

    if (!force2d && shouldUseVrTutorialMesh()) {
      if (window.VrTutorialNative && VrTutorialNative.isVisible()) {
        return activateVrTutorialMeshStep(stepIndex);
      }
      currentStep = stepIndex;
      showVrTutorialMesh().then((shown) => {
        if (!isTutorialOpen()) {
          return;
        }
        if (shown) {
          if (!(window.VrTutorialNative
            && VrTutorialNative.isVisible()
            && typeof VrTutorialNative.getActiveStepIndex === 'function'
            && VrTutorialNative.getActiveStepIndex() === stepIndex)) {
            activateVrTutorialMeshStep(stepIndex);
          }
          return;
        }
        applyNativePresentationMode(false);
        renderStep(stepIndex, { force2d: true });
      });
      return true;
    }

    currentStep = stepIndex;
    titleEl.textContent = step.title;
    subtitleEl.textContent = step.subtitle || '';
    renderStepBullets(step);
    noteEl.textContent = step.note || '';
    copyEl.classList.toggle('has-note', Boolean(step.note));
    syncMobileTutorialCopyClasses(step);

    let videoSrcChanged = false;

    if (step.video) {
      mediaEl.classList.add('has-video');
      videoEl.loop = true;
      const nextVideoKey = isVrTutorialPage() ? tutorialVideoKey(step.video) : step.video;
      const currentVideoKey = isVrTutorialPage()
        ? tutorialVideoKey(videoEl.currentSrc || videoEl.getAttribute('src') || '')
        : videoEl.getAttribute('src');
      videoSrcChanged = nextVideoKey !== currentVideoKey;
      if (videoSrcChanged) {
        resetTutorialVideoLoadState();
        setMediaLoading(true);
        videoEl.src = step.video;
        videoEl.load();
      }
      syncMediaLoadingState();
    } else {
      mediaEl.classList.remove('has-video');
      setMediaLoading(false);
      videoEl.removeAttribute('src');
      videoEl.load();
    }

    renderOverlay(step);
    syncMediaAspectRatio();
    if (shouldUseFixedTutorialMediaSize()) {
      scheduleFrame(() => {
        syncMobileTutorialLayout();
      });
    }

    const bulletHighlights = resolveBulletHighlights(step);
    const noteHighlight = resolveNoteHighlight(step);
    if (bulletHighlights || noteHighlight) {
      bindBulletHighlightSync(bulletHighlights, noteHighlight);
    } else {
      stopBulletHighlightSync();
    }

    if (step.video && (videoSrcChanged || videoEl.paused)) {
      playTutorialVideo();
    }

    syncMobileTutorialActionLabels(stepIndex);
    if (prevBtn) {
      syncPrevButtonVisibility(stepIndex);
    }
    renderDots(stepIndex);
    saveState({
      step: stepIndex,
      status: (() => {
        const status = loadState()?.status;
        if (status === 'completed' || status === 'skipped') {
          return status;
        }
        return status || 'pending';
      })()
    });
    return true;
  }

  async function open(stepIndex) {
    if (isVrTutorialPage()) {
      suppressVrCookieConsentForTutorial();
    }
    syncTutorialTheme();
    const idx = Math.max(0, Math.min(manifest.steps.length - 1, typeof stepIndex === 'number' ? stepIndex : 0));
    currentStep = idx;
    overlay.classList.add('show');
    overlay.setAttribute('aria-hidden', 'false');
    syncTutorialPageInteractionLock();

    if (isVrTutorialPage() && isImmersiveVrActive()) {
      ensureVrScenePlaying();
      startVrScenePlayWatchdogIfNeeded();
    }

    if (isVrTutorialPage() && shouldUseVrTutorialMesh()) {
      ensureVrScenePlayingForMeshTutorial();
      const nativeShown = await showVrTutorialMesh();
      if (nativeShown) {
        if (!(window.VrTutorialNative
          && VrTutorialNative.isVisible()
          && typeof VrTutorialNative.getActiveStepIndex === 'function'
          && VrTutorialNative.getActiveStepIndex() === idx)) {
          activateVrTutorialMeshStep(idx);
        }
        syncVrOverlayPointerGuard();
        syncTutorialPageInteractionLock();
        return { ok: true, native: true, step: idx };
      }
    }

    applyNativePresentationMode(false);
    renderStep(idx, { force2d: true });
    await syncTutorialPresentation();
    syncVrOverlayPointerGuard();
    if (shouldUseFixedTutorialMediaSize()) {
      scheduleFrame(() => {
        syncMobileTutorialLayout();
      });
    }
    return { ok: true, native: false, step: idx };
  }

  async function openVrMeshTutorial(stepIndex) {
    const idx = typeof stepIndex === 'number' ? stepIndex : (loadState()?.step || 0);
    if (!isVrTutorialDevice() && !vrTutorialForceMeshFromUrl) {
      return open(idx);
    }
    vrTutorialForceMesh = true;
    return open(idx);
  }

  function close() {
    overlay.classList.remove('show', 'tutorial-vr-mesh-mode', 'tutorial-vr-immersive-guard', 'tutorial-vr-native-panel');
    overlay.setAttribute('aria-hidden', 'true');
    hideVrTutorialMesh();
    stopMouseOverlaySync();
    stopBulletHighlightSync();
    stopVrScenePlayWatchdog();
    syncVrOverlayPointerGuard();
    if (videoEl) {
      videoEl.pause();
    }
    vrTutorialForceMesh = false;
    resumeVrSceneAfterTutorial();
    if (isImmersiveVrActive()) {
      ensureVrScenePlaying();
    }
    applyNativePresentationMode(false);
    if (isImmersiveVrActive()) {
      document.getElementById('cameraRig')?.emit('reset-camera', { message: 'Tutorial closed' });
    }
    showVrCookieConsentIfNeeded();
  }

  function skipTutorial() {
    saveState({ status: 'skipped', step: currentStep });
    close();
  }

  function dismissForSession() {
    close();
  }

  function completeTutorial() {
    saveState({ status: 'completed', step: currentStep });
    close();
  }

  function goNext() {
    if (currentStep >= manifest.steps.length - 1) {
      completeTutorial();
      return;
    }
    renderStep(currentStep + 1);
  }

  function goPrev() {
    if (currentStep <= 0) {
      return;
    }
    renderStep(currentStep - 1);
  }

  async function loadManifest() {
    try {
      const response = await fetch(MANIFEST_URL, { cache: 'no-cache' });
      if (!response.ok) {
        return;
      }
      const data = await response.json();
      if (data && Array.isArray(data.steps) && data.steps.length > 0) {
        manifest = normalizeManifest(data);
      }
    } catch (e) { }
  }

  function waitForPreloaderHidden() {
    return new Promise((resolve) => {
      const preloader = document.getElementById('preloader');
      if (!preloader) {
        resolve();
        return;
      }
      const isHidden = () => {
        if (preloader.classList.contains('hidden') || preloader.hasAttribute('hidden')) {
          return true;
        }
        if (preloader.style.display === 'none' || preloader.style.visibility === 'hidden') {
          return true;
        }
        if (preloader.getAttribute('aria-hidden') === 'true') {
          return true;
        }
        return false;
      };
      if (isHidden()) {
        resolve();
        return;
      }
      const observer = new MutationObserver(() => {
        if (isHidden()) {
          observer.disconnect();
          resolve();
        }
      });
      observer.observe(preloader, { attributes: true, attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'] });
      setTimeout(() => {
        observer.disconnect();
        resolve();
      }, 8000);
    });
  }

  async function initAutoOpen() {
    await Promise.all([loadManifest(), initVrTutorialDeviceDetection()]);
    renderDots(0);

    if (!shouldAutoOpen()) {
      return;
    }

    await waitForPreloaderHidden();
    setTimeout(async () => {
      if (!shouldAutoOpen()) {
        return;
      }
      const stepIndex = loadState()?.step || 0;
      if (isVrTutorialPage()) {
        if (!shouldAutoOpen()) {
          return;
        }
        if (isVrTutorialDevice()) {
          await openVrMeshTutorial(stepIndex);
          return;
        }
      }
      open(stepIndex);
    }, AUTO_OPEN_DELAY_MS);
  }

  function onAuthSessionChanged(event) {
    const session = event && event.detail ? event.detail.session : null;
    const isSignedIn = !!(session && session.user && session.user.is_anonymous !== true);
    if (isSignedIn && overlay.classList.contains('show')) {
      close();
    }
  }

  document.addEventListener('auth:session-changed', onAuthSessionChanged);

  overlay.addEventListener('pointerdown', () => {
    playTutorialVideo();
  }, { passive: true });
  overlay.addEventListener('touchstart', onTutorialOverlayTouchStart, { capture: true, passive: true });
  overlay.addEventListener('touchmove', onTutorialOverlayTouchMove, { passive: false });

  nextBtn.addEventListener('click', goNext);
  if (prevBtn) {
    prevBtn.addEventListener('click', goPrev);
  }
  remindBtn.addEventListener('click', skipTutorial);
  closeBtn.addEventListener('click', dismissForSession);
  backdropEl.addEventListener('click', () => {
    if (shouldUseVrTutorialMesh()) {
      return;
    }
    dismissForSession();
  });

  window.StockStormTutorial = {
    open: (options) => {
      const force = options && options.force;
      const state = loadState();
      if (!force && (state?.status === 'completed' || state?.status === 'skipped')) {
        return Promise.resolve({ ok: false, reason: 'already-completed' });
      }
      if (options && typeof options.forceOculus === 'boolean') {
        vrTutorialForceOverride = options.forceOculus;
      }
      if (options && typeof options.forceMesh === 'boolean') {
        vrTutorialForceMesh = options.forceMesh;
      }
      return open(options?.step ?? state?.step ?? 0);
    },
    openVrMeshTutorial: (stepIndex) => openVrMeshTutorial(typeof stepIndex === 'number' ? stepIndex : (loadState()?.step || 0)),
    close,
    complete: completeTutorial,
    skip: skipTutorial,
    isOpen: isTutorialOpen
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAutoOpen);
  } else {
    initAutoOpen();
  }
}
